/**
 * ViewModel - a arma em primeira pessoa (ARCHITECTURE.md secao 4.14).
 *
 * Tudo aqui e construido em codigo: geometria composta e chanfrada, texturas de
 * relevo/rugosidade geradas em canvas 2D, iluminacao propria na viewScene.
 *
 * Decisoes de arquitetura (o porque):
 *
 * 1) A arma vive numa pilha de grupos aninhados, um por camada de animacao:
 *      holder > sway > bob > swap > pose > recoil > rig
 *    Cada camada escreve APENAS na sua transformacao local. Isso deixa cada efeito
 *    independente (posso zerar o sway sem tocar no bob) e, principalmente, torna o
 *    alinhamento da mira na ADS exato: com sway/bob/swap/recoil em identidade,
 *    basta o grupo `pose` levar a mira ate o eixo optico da camera.
 *
 * 2) `holder` copia a transformacao mundial da camera do jogador em lateUpdate,
 *    depois do CameraRig ter fechado o frame. Assim o espaco local do holder E o
 *    espaco da camera, e a posicao mundial de qualquer peca (boca do cano, porta
 *    de ejecao) sai correta para FX e balistica.
 *
 * 3) As pecas estaticas de cada arma sao FUNDIDAS por material (mergeGeometries),
 *    entao cada arma custa ~5 draw calls mesmo tendo ~90 pecas. So o que anima
 *    (ferrolho, carregador, gatilho, alavanca, maos) fica como objeto separado.
 *
 * 4) Sombreamento sem mapa de textura pintado a mao, tudo na COR DE VERTICE:
 *    a) desgaste de quina - as faces de chanfro sao as unicas com normal fora dos
 *       eixos do objeto; medindo esse desvio clareio a quina, mas modulado por um
 *       ruido de POSICAO, senao sai uma linha continua que le como adesivo;
 *    b) oclusao de contato assada com three-mesh-bvh na construcao (bakeVertexAO).
 *       E a unica fonte de escuro da arma: ela nao projeta nem recebe sombra e o
 *       SSAO do PostFX a exclui de proposito.
 *
 * 5) A UV e TRIPLANAR, gerada em prep() a partir da posicao. As UV nativas do
 *    ExtrudeGeometry (metros) e do CylinderGeometry (0..1 na volta) nao tem escala
 *    comum, entao um mesmo `repeat` dava ladrilho de 10 cm numa peca e esticava
 *    3:1 na outra.
 *
 * 6) A luz e propria (key/fill/rim presas ao holder) mas nao e FIXA: direcao, cor
 *    e intensidade saem de ctx.lighting/ctx.sky, e um raio a cada 0.1 s diz se a
 *    arma esta na sombra. Sem isso ela ficava com o mesmo brilho no sol, no galpao
 *    e no contraluz.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MeshBVH } from 'three-mesh-bvh';

// ---------------------------------------------------------------------------
// Temporarios de modulo. ZERO alocacao dentro de update/lateUpdate.
// ---------------------------------------------------------------------------
const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _v3 = new THREE.Vector3();
const _v4 = new THREE.Vector3();
const _v5 = new THREE.Vector3();
const _v6 = new THREE.Vector3();
const _v7 = new THREE.Vector3();
const _q1 = new THREE.Quaternion();
const _shellPayload = { position: new THREE.Vector3(), velocity: new THREE.Vector3() };

// temporarios exclusivos da luz dinamica (nao podem colidir com os de cima,
// que sao usados no mesmo frame pela pose e pelo coice)
const _ls1 = new THREE.Vector3();
const _lq1 = new THREE.Quaternion();
const _lc1 = new THREE.Color();
const _identity = new THREE.Matrix4();

const DEG = Math.PI / 180;

/** Alturas da linha de mira por arma: todas as miras ficam em x=0, y=SIGHT_Y. */
const ADS_EYE_Z = -0.235;   // distancia do olho ate a mira quando na ADS

/**
 * FOV da camera EXCLUSIVA da arma (ctx.viewCamera). O Engine cria essa camera com
 * 55 graus e o resize dele so reescreve `aspect`, nunca o fov - entao quem manda
 * na lente da arma e este modulo, o unico dono da viewScene.
 *
 * 62 graus com a peca mais proxima a 21 cm do olho dava razao de distancia de
 * 4.8:1 entre a boca do cano e a placa de coice: as duas pontas da arma saiam com
 * 3.4x de diferenca de tamanho e a silhueta virava CUNHA, nao fuzil (topo e base
 * nem perto de paralelos). Lente longa e arma mais afastada e o que endireita
 * isso - 52 graus e o meio do intervalo de 45 a 55 usado em FPS comercial, e a
 * pose empurrou a arma para a frente junto (ver hipPos), senao ela so ficaria
 * maior. Medido pela geometria: razao maxima de 3.2:1 e peca mais proxima a
 * pelo menos 31 cm nas SETE armas - as quatro acrescentadas na rodada 2 foram
 * dimensionadas contra este mesmo limite, e cada uma registra a conta no
 * comentario do proprio `hipPos`.
 */
const VIEW_FOV = 52;

/** Ladrilhos de textura por METRO de peca (1/45 m = 2.2 cm por ladrilho). */
const UV_TILE = 45;

// ---------------------------------------------------------------------------
// Ruido e texturas procedurais (geradas uma vez, compartilhadas pelas 7 armas)
// ---------------------------------------------------------------------------

function hash2(x, y, seed) {
  let h = x * 374761393 + y * 668265263 + seed * 1442695040;
  h = (h ^ (h >> 13)) * 1274126177;
  return ((h ^ (h >> 16)) >>> 0) / 4294967295;
}

function smooth(t) { return t * t * (3 - 2 * t); }

/** Ruido de valor bilinear, periodico em `period` para a textura casar na borda. */
function valueNoise(x, y, period, seed) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const x0 = ((xi % period) + period) % period;
  const y0 = ((yi % period) + period) % period;
  const x1 = (x0 + 1) % period;
  const y1 = (y0 + 1) % period;
  const a = hash2(x0, y0, seed), b = hash2(x1, y0, seed);
  const c = hash2(x0, y1, seed), d = hash2(x1, y1, seed);
  const u = smooth(xf), v = smooth(yf);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

function fbm(x, y, octaves, period, seed) {
  let sum = 0, amp = 0.5, norm = 0, p = period;
  for (let i = 0; i < octaves; i++) {
    sum += valueNoise(x * (p / period), y * (p / period), p, seed + i * 17) * amp;
    norm += amp;
    amp *= 0.5;
    p *= 2;
  }
  return sum / norm;
}

/**
 * Gera o mapa de altura de um material, em Float32Array normalizada 0..1.
 * kind: 'metal' (grao fino + linhas de usinagem + riscos) ou
 *       'polymer' (granulado grosso tipo polimero moldado, com pontilhado).
 */
function heightField(size, kind) {
  const out = new Float32Array(size * size);
  const inv = 1 / size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x * inv, v = y * inv;
      let h;
      // NOTA DE ESCALA: com a UV triplanar de prep() um ladrilho cobre 2.2 cm de
      // peca. Entao periodo 44 = 0.5 mm (grao, some no mip) e periodo 9 = 2.5 mm
      // (mancha, ~4 px na tela). Antes o ladrilho cobria 10 cm e o periodo 5 dava
      // manchas de 2 cm - as "nuvens" que apareciam na coronha.
      if (kind === 'metal') {
        // grao fino isotropico
        h = fbm(u * 44, v * 44, 3, 44, 11) * 0.42;
        // linhas de usinagem: anisotropia de FRESA (1 mm x 0.2 mm), nao faixa
        // pintada. O 6 x 150 antigo dava 25:1 e saia como listra esticada.
        h += fbm(u * 20, v * 120, 2, 120, 29) * 0.26;
        // manchas curtas de oxidacao para quebrar a uniformidade
        h += fbm(u * 9, v * 9, 3, 9, 71) * 0.26;
      } else {
        // polimero: pontilhado denso (textura antiderrapante) + ondulacao curta
        const dots = hash2(Math.floor(u * 72), Math.floor(v * 72), 5);
        h = (dots > 0.62 ? 1 : 0.18) * 0.45;
        h += fbm(u * 24, v * 24, 3, 24, 3) * 0.34;
        h += fbm(u * 7, v * 7, 2, 7, 91) * 0.21;
      }
      out[y * size + x] = Math.min(1, Math.max(0, h));
    }
  }
  // riscos longos so no metal, tracados como segmentos finos e claros
  if (kind === 'metal') {
    for (let s = 0; s < 26; s++) {
      const r = hash2(s, 7, 13);
      const r2 = hash2(s, 19, 41);
      const len = 12 + r2 * 70;
      const ang = (r - 0.5) * 0.55 + (r2 > 0.5 ? 0 : Math.PI * 0.5);
      let px = r * size, py = r2 * size;
      const dx = Math.cos(ang), dy = Math.sin(ang);
      const depth = 0.18 + r * 0.22;
      for (let i = 0; i < len; i++) {
        const ix = ((Math.floor(px) % size) + size) % size;
        const iy = ((Math.floor(py) % size) + size) % size;
        out[iy * size + ix] = Math.min(1, out[iy * size + ix] + depth);
        px += dx; py += dy;
      }
    }
  }
  return out;
}

/** Converte um campo de altura em normal map (sobel), devolvendo THREE.DataTexture. */
function normalTexture(height, size, strength) {
  const data = new Uint8Array(size * size * 4);
  const at = (x, y) => height[(((y % size) + size) % size) * size + (((x % size) + size) % size)];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      // normal = normalize(-dx, -dy, 1)
      let nx = -dx, ny = -dy, nz = 1;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      nx /= len; ny /= len; nz /= len;
      const i = (y * size + x) * 4;
      data[i] = (nx * 0.5 + 0.5) * 255;
      data[i + 1] = (ny * 0.5 + 0.5) * 255;
      data[i + 2] = (nz * 0.5 + 0.5) * 255;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/** Campo de altura -> mapa de rugosidade remapeado para [lo, hi]. */
function roughnessTexture(height, size, lo, hi, invert) {
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const h = invert ? 1 - height[i] : height[i];
    const r = (lo + h * (hi - lo)) * 255;
    data[i * 4] = r; data[i * 4 + 1] = r; data[i * 4 + 2] = r; data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/** Albedo procedural sutil: riscos no metal e granulado no polimero. */
function albedoTexture(height, size, kind) {
  const data = new Uint8Array(size * size * 4);
  const metal = kind === 'metal';
  for (let i = 0; i < size * size; i++) {
    const h = height[i] - 0.5;
    const shade = Math.max(0.68, Math.min(1, (metal ? 0.88 : 0.91) + h * (metal ? 0.24 : 0.16)));
    const r = shade * (metal ? 0.98 : 0.96);
    const g = shade * (metal ? 0.99 : 0.98);
    const b = shade * (metal ? 1.00 : 0.94);
    data[i * 4] = r * 255;
    data[i * 4 + 1] = g * 255;
    data[i * 4 + 2] = b * 255;
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

// ---------------------------------------------------------------------------
// Biblioteca de materiais do viewmodel
// ---------------------------------------------------------------------------

/**
 * Materiais proprios (nao uso ctx.materials: aquilo e escala de cenario, com
 * repeat em metros; aqui preciso de grao fino a 20 cm do olho).
 */
function buildMaterials(anisotropy) {
  const S = 512;
  const hMetal = heightField(S, 'metal');
  const hPoly = heightField(S, 'polymer');

  const aMetal = albedoTexture(hMetal, S, 'metal');
  const nMetal = normalTexture(hMetal, S, 1.6);
  const rMetal = roughnessTexture(hMetal, S, 0.30, 0.72, false);
  const aPoly = albedoTexture(hPoly, S, 'polymer');
  const nPoly = normalTexture(hPoly, S, 2.4);
  const rPoly = roughnessTexture(hPoly, S, 0.62, 0.94, false);

  for (const t of [aMetal, nMetal, rMetal, aPoly, nPoly, rPoly]) t.anisotropy = anisotropy || 4;

  const mk = (opts) => new THREE.MeshStandardMaterial(opts);

  /**
   * PBR do metal. Duas medidas, dois erros.
   *
   * 1) `metalness` proximo de 1 tirava a cor base do difuso e deixava a resposta
   *    quase toda especular: o cano pavonado (0x15171b, a peca MAIS ESCURA do
   *    modelo) refletia o PMREM do ceu e saia como cromo espelhado, mais claro
   *    que os caixotes iluminados atras dele. Aco pavonado real reflete pouco e
   *    espalha: quem da a leitura de metal e o roughness variando pelo mapa.
   *
   * 2) A COR BASE era escura demais para sobreviver a conversao para LINEAR - e e
   *    isso, nao a luz, que fazia a arma ler como silhueta preta contra um cenario
   *    bem exposto. 0x22252a e sRGB 0.133, ou seja 0.0135 em linear; com metalness
   *    0.80 o difuso caia para 0.0135 x 0.20 = 0.0027, e o F0 do condutor (que
   *    para metal E a propria cor base) para 0.011 - um metal que reflete 1% e nao
   *    tem como acender destaque nenhum. Com o mapa de albedo multiplicando por
   *    ~0.75 e o AO assado por cima, a face batida de sol dava ~0.004 de
   *    radiancia contra ~0.05 do chao no mesmo frame. Subir a luz nao resolve
   *    isso: com difuso 0.0027 nem o sol do mundo tira a peca do preto.
   *
   * Os valores abaixo foram calculados a partir da iluminancia efetiva do sol,
   * que e 3.2 (sunIntensity 5.05 x luminancia 0.633 da cor do sol de fim de
   * tarde): difuso = albedo_linear x (1 - metalness) x 3.2 / PI x NdotL, ainda
   * multiplicado por ~0.75 do mapa de albedo. Alvo: face iluminada do receptor em
   * ~0.025 de radiancia (luminancia ~45 de 255), coronha tan em ~0.055 (~66),
   * contra ~0.05 (~61) do asfalto ao sol - a arma na mesma familia do cenario, com
   * a face de baixo caindo para 15-20 por AO e falta de key.
   *
   * O ESPECULAR NAO SUBIU junto: metalness cai quase na mesma proporcao em que a
   * cor sobe, entao F0 sai de 0.011 para 0.019 no corpo e 0.013 no cano. O cromo
   * do dossie nao volta - o que volta e o destaque, que so existe se F0 > 0.
   */
  const mats = {
    // corpo principal: aluminio anodizado preto fosco
    steel: mk({
      color: 0x43474e, metalness: 0.46, roughness: 0.52, vertexColors: true,
      map: aMetal, normalMap: nMetal, roughnessMap: rMetal, envMapIntensity: 0.80
    }),
    // aco de cano/ferrolho: mais escuro e um pouco mais liso que o corpo
    blued: mk({
      color: 0x30333a, metalness: 0.44, roughness: 0.46, vertexColors: true,
      map: aMetal, normalMap: nMetal, roughnessMap: rMetal, envMapIntensity: 0.72
    }),
    // parafusos, pinos e detalhes claros
    bright: mk({
      color: 0x9aa0a9, metalness: 0.58, roughness: 0.38, vertexColors: true,
      map: aMetal, normalMap: nMetal, envMapIntensity: 0.90
    }),
    // polimero da coronha, punho e guarda-mao. TAN sujo (FDE): e o unico
    // contraste de material da arma - todo o resto e cinza de metal.
    polymer: mk({
      color: 0x5b5340, metalness: 0.04, roughness: 0.86, vertexColors: true,
      map: aPoly, normalMap: nPoly, roughnessMap: rPoly, envMapIntensity: 0.70
    }),
    // borracha de coice e pegas emborrachadas: a peca mais escura que ainda tem
    // de mostrar forma (0x141514 dava 0.0058 linear - preto de recorte)
    rubber: mk({
      color: 0x212320, metalness: 0.0, roughness: 0.96, vertexColors: true,
      map: aPoly, normalMap: nPoly, envMapIntensity: 0.30
    }),
    // latao do estojo: o ponto quente da paleta
    brass: mk({
      color: 0x8a6a2e, metalness: 0.78, roughness: 0.45, vertexColors: true,
      map: aMetal, normalMap: nMetal, envMapIntensity: 1.0
    }),
    // marcas pintadas do seletor: branco-osso, com emissivo fraco para nao
    // sumirem quando a arma entra na sombra
    paint: mk({
      color: 0xd8d2c0, metalness: 0.0, roughness: 0.70, vertexColors: true,
      emissive: 0x3a382f, emissiveIntensity: 1.0, envMapIntensity: 0.15
    }),
    // interior de canos, frestas e vazios: preto que nao reflete
    dark: mk({ color: 0x050506, metalness: 0.2, roughness: 0.95, vertexColors: true, envMapIntensity: 0.06 }),
    // luva escura fosca. O valor anterior (0x2f3237) ficava em apenas
    // ~0.03 linear e virava silhueta depois do tint dos dedos e do AO.
    // Este charcoal quente fica em ~0.08-0.10 linear: ainda escuro, mas com
    // meio-tom suficiente para as falanges responderem a key/fill/rim.
    glove: mk({
      color: 0x595750, metalness: 0.02, roughness: 0.92, vertexColors: true,
      map: aPoly, normalMap: nPoly, roughnessMap: rPoly, envMapIntensity: 0.54
    }),
    // reforco de couro/kevlar nos nos dos dedos
    gloveHard: mk({
      color: 0x4a4945, metalness: 0.08, roughness: 0.78, vertexColors: true,
      map: aPoly, normalMap: nPoly, envMapIntensity: 0.58
    }),
    // manga do uniforme: tecido, nunca metal. O antebraco usava 'polymer' (o
    // mesmo da coronha) e por isso lia como tubo de PVC.
    sleeve: mk({
      color: 0x41463b, metalness: 0.0, roughness: 0.98, vertexColors: true,
      map: aPoly, normalMap: nPoly, roughnessMap: rPoly, envMapIntensity: 0.32
    })
  };

  // Vidro da luneta. Precisa ser TRANSPARENTE de verdade: na mira ele fica
  // exatamente sobre o centro da tela e um material opaco cegaria o jogador.
  // depthWrite falso para nao cortar o que vem depois na mesma cena.
  mats.lens = new THREE.MeshPhysicalMaterial({
    color: 0x9fc6e8, metalness: 0.0, roughness: 0.05,
    clearcoat: 1.0, clearcoatRoughness: 0.04, envMapIntensity: 1.8,
    transparent: true, opacity: 0.12, depthWrite: false,
    side: THREE.FrontSide
  });

  /**
   * Quanto de AO de contato cada material aceita (ver bakeVertexAO).
   * A mao e um feixe de dedos encostados uns nos outros: com AO cheio ela fica
   * ocluida por inteiro e vira um borrao preto, que e justamente o defeito que se
   * quer corrigir. Latao e tinta sao os dois acentos de cor da arma - se o AO os
   * apaga, nao ha por que te-los.
   */
  for (const k of Object.keys(mats)) {
    const m = mats[k];
    if (m && m.isMaterial) m.userData.aoScale = AO_SCALE[k] === undefined ? 1 : AO_SCALE[k];
  }

  mats._textures = [aMetal, nMetal, rMetal, aPoly, nPoly, rPoly];
  return mats;
}

// Preserva o escuro entre os dedos sem derrubar as faces abertas da luva.
const AO_SCALE = { glove: 0.40, gloveHard: 0.45, sleeve: 0.75, brass: 0.30, paint: 0.30 };

// ---------------------------------------------------------------------------
// Helpers de geometria
// ---------------------------------------------------------------------------

/** Retangulo com cantos arredondados, para extrusao. */
function roundedRect(w, h, r) {
  return roundedRectInto(new THREE.Shape(), w, h, r);
}

/** Mesma silhueta, mas como THREE.Path - serve de FURO dentro de uma Shape. */
function roundedHole(w, h, r) {
  return roundedRectInto(new THREE.Path(), w, h, r);
}

function roundedRectInto(s, w, h, r) {
  const x = -w / 2, y = -h / 2;
  r = Math.min(r, w / 2 - 1e-4, h / 2 - 1e-4);
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

/** Poligono livre a partir de pares [x,y]. Usado para silhuetas (coronha, punho). */
function polyShape(points) {
  const s = new THREE.Shape();
  s.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) s.lineTo(points[i][0], points[i][1]);
  s.closePath();
  return s;
}

/**
 * Caixa CHANFRADA (a peca-chave do acabamento). Extrusao de um retangulo
 * arredondado com bisel nas duas tampas: nenhuma quina viva sobra, e as faces do
 * bisel viram automaticamente a mascara de desgaste.
 * O eixo de extrusao e Z e a peca sai centrada na origem.
 */
function chamferBox(w, h, d, bevel, corner) {
  const b = Math.min(bevel, w * 0.32, h * 0.32, d * 0.45);
  const shape = roundedRect(Math.max(1e-3, w - b * 2), Math.max(1e-3, h - b * 2), corner === undefined ? b * 1.1 : corner);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(1e-3, d - b * 2), bevelEnabled: true, bevelSegments: 1,
    bevelSize: b, bevelThickness: b, curveSegments: 3, steps: 1
  });
  g.translate(0, 0, -(d - b * 2) / 2 - b);
  g.computeVertexNormals();
  return g;
}

/** Extrusao chanfrada de uma silhueta qualquer (perfil no plano XY). */
function chamferShape(shape, d, bevel, curveSegments) {
  const b = Math.min(bevel, d * 0.45);
  const g = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(1e-3, d - b * 2), bevelEnabled: true, bevelSegments: 1,
    bevelSize: b, bevelThickness: b, curveSegments: curveSegments || 4, steps: 1
  });
  g.translate(0, 0, -(d - b * 2) / 2 - b);
  g.computeVertexNormals();
  return g;
}

/** Cilindro deitado no eixo Z (cano, tubo de luneta, supressor). */
function tubeZ(rTop, rBottom, len, seg, open) {
  const g = new THREE.CylinderGeometry(rTop, rBottom, len, seg || 16, 1, !!open);
  g.rotateX(Math.PI / 2);
  return g;
}

/** Perfil de revolucao no eixo Z, a partir de pares [raio, z]. */
function latheZ(profile, seg) {
  const pts = [];
  for (let i = 0; i < profile.length; i++) pts.push(new THREE.Vector2(profile[i][0], profile[i][1]));
  const g = new THREE.LatheGeometry(pts, seg || 20);
  // LatheGeometry gira em torno de Y com o perfil em XY; levo para o eixo Z
  g.rotateX(Math.PI / 2);
  g.computeVertexNormals();
  return g;
}

/** Anel de reforco/knurl: toro achatado no eixo Z. */
function ringZ(radius, thickness, seg) {
  const g = new THREE.TorusGeometry(radius, thickness, 6, seg || 16);
  return g;
}
// ---------------------------------------------------------------------------
// Normalizacao de geometria + cor de vertice (desgaste de quina)
// ---------------------------------------------------------------------------

/**
 * Deixa toda geometria no MESMO formato (nao indexada, position/normal/uv/color)
 * para poder fundir tudo num buffer so por material.
 *
 * `wear` (0..1) controla quanto as faces de chanfro clareiam. A medida e o desvio
 * da normal em relacao ao eixo dominante: face plana da ~0, chanfro de 45 graus
 * da ~0.42, curva suave da algo intermediario. Cor de vertice acima de 1.0 e
 * legitima em three e produz exatamente o brilho de metal gasto pelo uso.
 */
function prep(geom, wear, tint, noise) {
  let g = geom.index ? geom.toNonIndexed() : geom;
  if (g !== geom) geom.dispose();

  if (!g.getAttribute('normal')) g.computeVertexNormals();
  // atributos extras atrapalham o merge
  for (const name of Object.keys(g.attributes)) {
    if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
  }

  const pos = g.getAttribute('position');
  const nrm = g.getAttribute('normal');
  const count = pos.count;
  const col = new Float32Array(count * 3);
  const uv = new Float32Array(count * 2);
  const w = wear === undefined ? 0.55 : wear;
  const nz = noise === undefined ? 0.06 : noise;
  const tr = tint ? tint[0] : 1, tg = tint ? tint[1] : 1, tb = tint ? tint[2] : 1;

  for (let i = 0; i < count; i++) {
    const px = pos.getX(i), py = pos.getY(i), pz = pos.getZ(i);
    const rx = nrm.getX(i), ry = nrm.getY(i), rz = nrm.getZ(i);
    const nx = Math.abs(rx), ny = Math.abs(ry), nzz = Math.abs(rz);
    const dom = Math.max(nx, ny, nzz);

    /**
     * UV TRIPLANAR (eixo dominante da normal). Resolve de uma vez extrusao,
     * cilindro e lathe: o ExtrudeGeometry emitia UV em METROS do espaco local e o
     * CylinderGeometry emitia 0..1 na volta, entao um mesmo `repeat` dava ladrilho
     * de 10 cm numa peca e anisotropia de 3:1 na outra. Aqui todo material passa a
     * ver a MESMA escala fisica: UV_TILE ladrilhos por metro.
     */
    if (dom === nx) { uv[i * 2] = pz * UV_TILE; uv[i * 2 + 1] = py * UV_TILE; }
    else if (dom === ny) { uv[i * 2] = px * UV_TILE; uv[i * 2 + 1] = pz * UV_TILE; }
    else { uv[i * 2] = px * UV_TILE; uv[i * 2 + 1] = py * UV_TILE; }

    // 0 em face plana, ~0.42 em chanfro de 45 graus
    const edge = Math.max(0, 1 - dom);
    const e = Math.min(1, edge * 2.35);
    // ruido espacial baixa frequencia: nenhuma peca fica com cor chapada
    const v = valueNoise(px * 34 + 5, py * 34 + pz * 17, 64, 3);
    const dirt = 1 - nz * 0.5 + (v - 0.5) * nz * 2;
    /**
     * DESGASTE DE QUINA. So a normal nao basta: como o desvio e igual ao longo de
     * toda a aresta, saia uma linha clara continua e perfeita - lia como adesivo,
     * nao como metal descascado. Um segundo ruido, agora de POSICAO, corta a quina
     * em trechos: clareia onde a mascara abre e deixa o resto no tom da peca.
     */
    const mk = valueNoise(px * 90, py * 90 + pz * 47, 64, 9);
    const mask = smooth(clamp01((mk - 0.42) / 0.30));
    const gain = (1 + e * e * w * 0.90 * mask) * dirt;
    col[i * 3] = gain * tr;
    col[i * 3 + 1] = gain * tg;
    col[i * 3 + 2] = gain * tb;
  }
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/**
 * Quanto do desgaste de quina cada material aceita. Metal gasta a quina de
 * verdade; polimero, borracha e tecido NAO ficam com aresta brilhante - deixar o
 * mesmo ganho neles punha quina de cromo na coronha e na luva.
 */
const SOFT_WEAR = { polymer: 0.12, rubber: 0.12, glove: 0.16, sleeve: 0.10, gloveHard: 0.22, dark: 0.50 };

function wearFor(mat, wear) {
  const w = wear === undefined ? 0.55 : wear;
  const k = SOFT_WEAR[mat];
  return k === undefined ? w : w * k;
}

/**
 * Acumulador de pecas. Junta tudo que e estatico por material e devolve poucas
 * malhas - a arma inteira sai em ~5 draw calls.
 */
class PartBuilder {
  constructor() {
    this.buckets = new Map();
  }

  /**
   * @param {THREE.BufferGeometry} geom geometria ja criada (sera consumida)
   * @param {string} mat chave do material
   * @param {object} t   { x,y,z, rx,ry,rz, sx,sy,sz, wear, tint, noise }
   */
  add(geom, mat, t) {
    const o = t || {};
    if (o.sx !== undefined || o.sy !== undefined || o.sz !== undefined) {
      geom.scale(o.sx === undefined ? 1 : o.sx, o.sy === undefined ? 1 : o.sy, o.sz === undefined ? 1 : o.sz);
    }
    if (o.rx) geom.rotateX(o.rx);
    if (o.ry) geom.rotateY(o.ry);
    if (o.rz) geom.rotateZ(o.rz);
    if (o.x || o.y || o.z) geom.translate(o.x || 0, o.y || 0, o.z || 0);
    const g = prep(geom, wearFor(mat, o.wear), o.tint, o.noise);
    let list = this.buckets.get(mat);
    if (!list) { list = []; this.buckets.set(mat, list); }
    list.push(g);
    return this;
  }

  /** Funde e pendura as malhas em `parent`. */
  flush(parent, mats, castShadow) {
    for (const [key, list] of this.buckets) {
      if (!list.length) continue;
      let geom = null;
      if (list.length === 1) {
        geom = list[0];
      } else {
        geom = mergeGeometries(list, false);
        for (const g of list) g.dispose();
      }
      if (!geom) continue;
      const mesh = new THREE.Mesh(geom, mats[key] || mats.steel);
      mesh.castShadow = !!castShadow;
      mesh.receiveShadow = false;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.frustumCulled = false;   // sempre na tela; testar frustum e desperdicio
      parent.add(mesh);
    }
    this.buckets.clear();
    return parent;
  }
}

/** Malha isolada (peca que anima). Mesmo tratamento de cor/chanfro. */
function part(geom, mat, mats, opts) {
  const o = opts || {};
  if (o.rx) geom.rotateX(o.rx);
  if (o.ry) geom.rotateY(o.ry);
  if (o.rz) geom.rotateZ(o.rz);
  if (o.x || o.y || o.z) geom.translate(o.x || 0, o.y || 0, o.z || 0);
  const mesh = new THREE.Mesh(prep(geom, wearFor(mat, o.wear), o.tint, o.noise), mats[mat] || mats.steel);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.frustumCulled = false;
  return mesh;
}

// ---------------------------------------------------------------------------
// Oclusao de contato assada na cor de vertice
// ---------------------------------------------------------------------------

const AO_RAYS = 6;
const AO_RADIUS = 0.030;    // alcance suficiente para juntas, dedos e contato da pega
const AO_BIAS = 0.42;       // preserva faces abertas e concentra o sinal em frestas
const AO_STRENGTH = 0.64;   // separacao legivel sem transformar o rig em borrao preto
const AO_GRID = 200;        // cache por posicao, celula de 5 mm

/**
 * A arma nao tinha UMA fresta escura, e isso era triplo por construcao: nenhuma
 * malha do rig projeta ou recebe sombra, a key light do viewmodel tem castShadow
 * falso, e o SSAO e propositalmente excluido da arma no PostFX (para a textura de
 * profundidade estavel continuar sendo a do mundo). Sobrava so a cor de vertice de
 * prep(), que CLAREIA e nunca escurece - por isso a face de baixo do receptor
 * tinha o mesmo valor da de cima.
 *
 * Aqui isso e resolvido de uma vez e de graca em tempo de frame: um MeshBVH sobre
 * a uniao do rig (arma + maos, ja nas poses de repouso) e, por vertice, um punhado
 * de raios num hemisferio cosseno de 3 cm. O resultado multiplica a cor de
 * vertice que prep() ja calculou. Custo unico na construcao, ZERO por frame.
 *
 * O cache por celula de 5 mm e o que torna isso viavel: a geometria e nao indexada
 * (cada triangulo repete seus vertices), entao os ~76 mil vertices de um rig caem
 * para ~4.9 mil amostras. AO e sinal de baixa frequencia; medido, o conjunto das
 * TRES armas originais custa ~330 ms uma unica vez, na tela de carregamento. Com
 * as quatro da rodada 2 sao sete rigs assados no mesmo ponto - o custo cresce com
 * o numero de rigs e NAO foi remedido; se a tela de carregamento incomodar, e aqui
 * que se mede primeiro.
 */
function bakeVertexAO(root) {
  root.updateMatrixWorld(true);

  // 1) malhas alvo, uma por GEOMETRIA (o pente que cai compartilha o buffer do
  //    pente normal: assar duas vezes escureceria o dobro)
  const targets = [];
  const seen = new Set();
  let triIdx = 0;
  root.traverse((o) => {
    if (!o.isMesh || !o.geometry) return;
    if (o.material && o.material.transparent) return;   // vidro nao ocluir nem recebe
    const p = o.geometry.getAttribute('position');
    if (!p || seen.has(o.geometry)) return;
    seen.add(o.geometry);
    targets.push(o);
    triIdx += o.geometry.index ? o.geometry.index.count : p.count;
  });
  if (!targets.length) return;

  // 2) oclusor: todos os triangulos do rig num buffer so, em espaco do rig
  const tri = new Float32Array(triIdx * 3);
  const tmp = new THREE.Vector3();
  let w = 0;
  for (const o of targets) {
    const g = o.geometry, p = g.getAttribute('position'), ix = g.index;
    const n = ix ? ix.count : p.count;
    for (let i = 0; i < n; i++) {
      tmp.fromBufferAttribute(p, ix ? ix.getX(i) : i).applyMatrix4(o.matrixWorld);
      tri[w++] = tmp.x; tri[w++] = tmp.y; tri[w++] = tmp.z;
    }
  }
  const occGeom = new THREE.BufferGeometry();
  occGeom.setAttribute('position', new THREE.BufferAttribute(tri, 3));

  let bvh = null;
  try {
    bvh = new MeshBVH(occGeom);
  } catch (err) {
    occGeom.dispose();
    console.warn('[ViewModel] BVH da arma falhou, sem oclusao de contato:', err);
    return;
  }

  // 3) amostragem
  const ray = new THREE.Ray();
  const cache = new Map();
  const nm = new THREE.Matrix3();
  const t1 = new THREE.Vector3(), t2 = new THREE.Vector3();
  const nn = new THREE.Vector3();

  for (const o of targets) {
    const g = o.geometry;
    const pA = g.getAttribute('position');
    const nA = g.getAttribute('normal');
    const cA = g.getAttribute('color');
    if (!pA || !nA || !cA) continue;
    if (pA.isInterleavedBufferAttribute || nA.isInterleavedBufferAttribute || cA.isInterleavedBufferAttribute) continue;

    // acesso direto ao buffer: getX/getY/getZ por vertice, em 230 mil vertices,
    // custava mais que os proprios raios
    const P = pA.array, N = nA.array, C = cA.array;
    const scale = AO_STRENGTH * (o.material && o.material.userData && o.material.userData.aoScale !== undefined
      ? o.material.userData.aoScale : 1);
    const m = o.matrixWorld.elements;
    const ident = o.matrixWorld.equals(_identity);
    if (!ident) nm.getNormalMatrix(o.matrixWorld);
    const e = nm.elements;

    for (let i = 0, j = 0; i < pA.count; i++, j += 3) {
      let px = P[j], py = P[j + 1], pz = P[j + 2];
      if (!ident) {
        const x = px, y = py, z = pz;
        const w = 1 / (m[3] * x + m[7] * y + m[11] * z + m[15] || 1);
        px = (m[0] * x + m[4] * y + m[8] * z + m[12]) * w;
        py = (m[1] * x + m[5] * y + m[9] * z + m[13]) * w;
        pz = (m[2] * x + m[6] * y + m[10] * z + m[14]) * w;
      }

      // chave: celula de posicao. Sem o octante da normal de proposito - separar
      // por normal triplicava o numero de amostras (e o tempo de construcao) para
      // distinguir faces de chanfro que tem, na pratica, a mesma oclusao.
      const key = (Math.round(px * AO_GRID) * 4001 + Math.round(py * AO_GRID)) * 4001
        + Math.round(pz * AO_GRID);

      let a = cache.get(key);
      if (a === undefined) {
        let nx = N[j], ny = N[j + 1], nz = N[j + 2];
        if (!ident) {
          const x = nx, y = ny, z = nz;
          nx = e[0] * x + e[3] * y + e[6] * z;
          ny = e[1] * x + e[4] * y + e[7] * z;
          nz = e[2] * x + e[5] * y + e[8] * z;
        }
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (len < 1e-6) continue;
        nx /= len; ny /= len; nz /= len;
        nn.set(nx, ny, nz);

        if (Math.abs(nz) < 0.9) t1.set(-ny, nx, 0).normalize();
        else t1.set(0, -nz, ny).normalize();
        t2.copy(nn).cross(t1);
        ray.origin.set(px + nx * 0.0012, py + ny * 0.0012, pz + nz * 0.0012);

        let hits = 0;
        for (let r = 0; r < AO_RAYS; r++) {
          const u = (r + 0.5) / AO_RAYS;
          const ph = r * 2.39996;                 // angulo aureo: espalha sem repetir
          const st = Math.sqrt(u), ct = Math.sqrt(1 - u);
          const cs = Math.cos(ph) * st, sn = Math.sin(ph) * st;
          ray.direction.set(
            t1.x * cs + t2.x * sn + nx * ct,
            t1.y * cs + t2.y * sn + ny * ct,
            t1.z * cs + t2.z * sn + nz * ct
          );
          if (bvh.raycastFirst(ray, THREE.DoubleSide, 0, AO_RADIUS)) hits++;
        }
        // remapeia por AO_BIAS: uma face exposta que so esbarra no proprio corpo
        // da arma tem de continuar valendo 1, senao o modelo inteiro escurece
        a = clamp01((hits / AO_RAYS - AO_BIAS) / (1 - AO_BIAS));
        cache.set(key, a);
      }

      const k = 1 - scale * a;
      C[j] *= k; C[j + 1] *= k; C[j + 2] *= k;
    }
    cA.needsUpdate = true;
  }

  occGeom.dispose();
}

// ---------------------------------------------------------------------------
// Sub-conjuntos reaproveitados pelas armas
// ---------------------------------------------------------------------------

/**
 * Trilho picatinny: base chanfrada + dentes. Os dentes sao o detalhe que mais
 * denuncia arma de brinquedo quando faltam - custam pouco depois do merge.
 */
function railTop(pb, x, y, z, len, width, teeth) {
  const w = width || 0.021;
  pb.add(chamferBox(w, 0.006, len, 0.0016), 'steel', { x, y, z, wear: 0.9 });
  const n = teeth || Math.max(4, Math.floor(len / 0.0102));
  const step = (len - 0.008) / n;
  for (let i = 0; i < n; i++) {
    pb.add(chamferBox(w * 0.98, 0.0052, 0.0042, 0.0012), 'steel',
      { x, y: y + 0.0053, z: z - len / 2 + 0.005 + i * step, wear: 1.0 });
  }
  // rasgo lateral inclinado das duas bordas (perfil trapezoidal do trilho)
  pb.add(chamferBox(w * 1.06, 0.0026, len, 0.001), 'blued', { x, y: y - 0.0026, z, wear: 0.8 });
}

/**
 * Guarda-mato: anel fechado feito de silhueta com furo, extrudado e chanfrado.
 * Dentro dele vai o gatilho, que anima.
 */
function triggerGuard(pb, x, y, z, mats) {
  const outer = new THREE.Shape();
  outer.moveTo(-0.030, 0.008);
  outer.lineTo(0.030, 0.008);
  outer.lineTo(0.030, -0.006);
  outer.quadraticCurveTo(0.030, -0.036, 0.000, -0.036);
  outer.quadraticCurveTo(-0.030, -0.036, -0.030, -0.006);
  outer.closePath();
  const hole = new THREE.Path();
  hole.moveTo(-0.022, 0.004);
  hole.lineTo(0.022, 0.004);
  hole.lineTo(0.022, -0.006);
  hole.quadraticCurveTo(0.022, -0.029, 0.000, -0.029);
  hole.quadraticCurveTo(-0.022, -0.029, -0.022, -0.006);
  hole.closePath();
  outer.holes.push(hole);
  // o perfil esta no plano XY; giro para o comprimento cair no eixo Z da arma
  const g = chamferShape(outer, 0.019, 0.0022, 6);
  g.rotateY(Math.PI / 2);
  pb.add(g, 'steel', { x, y, z, wear: 0.85 });
}

/** Gatilho curvo, malha propria (recua ao disparar). */
function triggerMesh(mats) {
  const s = polyShape([
    [-0.0035, 0.006], [0.0035, 0.006], [0.0042, -0.004],
    [0.0026, -0.014], [-0.0016, -0.019], [-0.0052, -0.016], [-0.0048, -0.004]
  ]);
  const g = chamferShape(s, 0.0075, 0.0012, 5);
  g.rotateY(Math.PI / 2);
  return part(g, 'bright', mats, { wear: 1.0, noise: 0.10 });
}

/**
 * Mira de ferro. `sightY` e a altura da LINHA DE MIRA - a mesma para a massa de
 * mira e para a alca. Manter as duas em x=0 e y=sightY e o que garante que, na
 * ADS, o poste apareca dentro da alca exatamente no centro da tela.
 */
function ironSights(pb, sightY, zFront, zRear) {
  // Massa de mira: poste fino dentro de um tunel aberto.
  // O TOPO DO POSTE fica exatamente na linha de mira (y = sightY), que e onde a
  // bala vai: assim, na ADS, a ponta do poste encosta no centro da tela. E a
  // capota tem de ficar bem acima dele, senao o poste "fecha" a janela de mira.
  // base alargada, encostando no bloco de gas
  pb.add(chamferBox(0.026, 0.020, 0.026, 0.0025), 'blued', { x: 0, y: sightY - 0.023, z: zFront, wear: 0.9 });
  /**
   * CAPOTA FECHADA, nao dois postes e uma travessa. A capota antiga era feita de
   * pecas de 3.0 a 3.5 mm: a 74 cm do olho isso da 4 px de espessura, com VAO
   * ABERTO entre elas - uma trave de arame por onde o cenario aparecia, e que
   * serrilhava em movimento (SMAA nao resolve geometria sub-pixel). Aqui e um
   * tunel solido de parede de 6 mm com o poste dentro: opaco, com espessura, e
   * nada da arma fica abaixo de ~5 px na tela.
   */
  // A janela (0.020 x 0.024, ja descontado o bisel de 1.6 mm da extrusao) e um
  // pouco MAIOR que o vao dos postes antigos, para a mira de ferro nao apertar.
  const hood = roundedRect(0.030, 0.034, 0.005);
  hood.holes.push(roundedHole(0.020, 0.024, 0.004));
  pb.add(chamferShape(hood, 0.016, 0.0016, 6), 'blued', { x: 0, y: sightY - 0.0025, z: zFront, wear: 1.0 });
  // poste de mira: engrossado de 2.2 para 3.6 mm (2.6 px -> ~6 px na tela)
  pb.add(chamferBox(0.0036, 0.0140, 0.0080, 0.0008), 'dark', { x: 0, y: sightY - 0.0070, z: zFront, wear: 0.6 });

  // alca de mira: chapa com abertura circular (peep)
  const plate = roundedRect(0.023, 0.019, 0.004);
  const hole = new THREE.Path();
  hole.absarc(0, 0.0005, 0.0042, 0, Math.PI * 2, true);
  plate.holes.push(hole);
  const pg = chamferShape(plate, 0.0055, 0.0011, 10);
  pb.add(pg, 'blued', { x: 0, y: sightY, z: zRear, wear: 1.0 });
  // orelhas de protecao da alca
  pb.add(chamferBox(0.0032, 0.017, 0.012, 0.0008), 'blued', { x: -0.0128, y: sightY + 0.002, z: zRear + 0.004, wear: 1.0 });
  pb.add(chamferBox(0.0032, 0.017, 0.012, 0.0008), 'blued', { x: 0.0128, y: sightY + 0.002, z: zRear + 0.004, wear: 1.0 });
}

/**
 * Boca de um tubo ABERTO (guarda-mao): anel de parede virado para a frente mais o
 * vao interno escuro ate o raio do que passa por dentro.
 *
 * `tubeZ` sem `open` fecha a ponta com um disco chapado virado para a camera - o
 * guarda-mao terminava num tampo liso em vez de mostrar a espessura da parede. Mas
 * so abrir o tubo deixaria um furo por onde se ve o MUNDO, porque a parede interna
 * fica de costas para a camera e e descartada. A dupla anel + vao escuro resolve os
 * dois: silhueta com espessura e interior opaco.
 *
 * Direcao da normal: no latheZ, raio CRESCENTE aponta para -Z e raio DECRESCENTE
 * para +Z (medido, nao suposto).
 */
function frontRing(pb, y, z, rIn, rOut, rBore, mat) {
  pb.add(latheZ([[rIn, 0.0016], [rIn + 0.0003, 0.0004], [rOut - 0.0003, 0], [rOut, 0.0014]], 24),
    mat, { x: 0, y, z, wear: 0.9 });
  // `rBore` tem de ser MENOR que o raio do cano que passa por dentro, senao
  // sobra um anel de alguns milimetros por onde se ve o mundo
  pb.add(latheZ([[rBore, 0], [rIn, 0.0018]], 24), 'dark', { x: 0, y, z: z + 0.005, wear: 0.15, noise: 0.04 });
}

/**
 * Fecha a traseira de um tubo aberto. Disco INTEIRO, nao so o anel de parede: a
 * traseira fica encostada no receptor mas sobra folga entre a face plana dele e a
 * parede curva do tubo, e por essa folga se veria o mundo.
 */
function backRing(pb, y, z, rOut, mat) {
  pb.add(latheZ([[rOut, 0], [rOut * 0.94, 0.0016]], 24), mat, { x: 0, y, z, wear: 0.7 });
  pb.add(latheZ([[rOut * 0.95, 0], [0, 0.0022]], 24), 'dark', { x: 0, y, z: z + 0.002, wear: 0.15, noise: 0.04 });
}

/** Boca do cano: coroa + furo escuro. Cano "macico" e erro classico. */
function muzzleBore(pb, x, y, z, rOuter, rBore) {
  // coroa: cone curto da borda do furo para fora (perfil em z CRESCENTE, senao a
  // LatheGeometry sai com as normais para dentro)
  pb.add(latheZ([[rBore, 0.0], [rOuter * 0.97, 0.0035], [rOuter, 0.006]], 20),
    'bright', { x, y, z, wear: 1.0 });
  pb.add(tubeZ(rBore * 0.98, rBore * 0.8, 0.034, 14, true), 'dark', { x, y, z: z + 0.017, wear: 0.15, noise: 0 });
}
/**
 * ARO FANTASMA + ESFERA DE LATAO - a mira das armas de cano largo (espingarda).
 *
 * Nao e a `ironSights`: aquela e poste dentro de tunel, leitura de fuzil. Aqui a
 * traseira e um ANEL grosso e aberto (o olho ve por dentro dele) e a dianteira e
 * uma ESFERA sobre a costela ventilada. As duas ficam em x=0 com o TOPO em
 * y=sightY, que e o que alinha a mira no centro exato da tela na ADS.
 *
 * O anel usa toro com raio de secao de 2.6 mm: a 60 cm do olho isso da ~5 px de
 * espessura, o piso abaixo do qual a geometria serrilha e o SMAA nao salva.
 */
function ghostRing(pb, sightY, z) {
  // O CENTRO do aro fica na linha de mira (nao o topo): o olho ve POR DENTRO dele,
  // e o alinhamento da ADS depende de o vazio, e nao o metal, cair em y=sightY.
  pb.add(ringZ(0.0092, 0.0026, 20), 'blued', { x: 0, y: sightY, z, wear: 1.0 });
  // pe do aro, descendo ate a nervura do receptor
  pb.add(chamferBox(0.010, 0.020, 0.011, 0.0022), 'blued', { x: 0, y: sightY - 0.020, z, wear: 0.95 });
  // orelhas laterais: protegem o aro e engrossam a silhueta
  for (const sx of [-1, 1]) {
    pb.add(chamferBox(0.0034, 0.020, 0.013, 0.0008), 'blued',
      { x: sx * 0.0140, y: sightY + 0.002, z: z + 0.004, wear: 1.0 });
  }
}

/** Esfera de latao no topo da costela. Latao para ela acender contra o alvo. */
function beadFront(pb, sightY, z) {
  const g = new THREE.SphereGeometry(0.0036, 12, 8);
  pb.add(g, 'brass', { x: 0, y: sightY - 0.0036, z, wear: 0.9, noise: 0.08 });
  pb.add(chamferBox(0.0050, 0.0060, 0.0060, 0.0010), 'blued', { x: 0, y: sightY - 0.0100, z, wear: 0.95 });
}

/**
 * Costela ventilada: barra chata no topo do cano com janelas vazadas. E o detalhe
 * que le "espingarda" antes de qualquer outro, e o que sustenta a esfera de mira.
 */
function ventRib(pb, y, zCenter, len, windows) {
  pb.add(chamferBox(0.014, 0.006, len, 0.0015), 'blued', { x: 0, y, z: zCenter, wear: 0.9 });
  const n = windows || 6;
  const step = (len - 0.026) / (n - 1);
  for (let i = 0; i < n; i++) {
    pb.add(chamferBox(0.016, 0.0070, 0.0090, 0.0012), 'dark',
      { x: 0, y, z: zCenter - len / 2 + 0.013 + i * step, wear: 0.3, noise: 0 });
  }
}

/**
 * Cartucho de calibre 12 visivel na boca do pente. Nao usa a ogiva das outras
 * armas de proposito: cartucho de espingarda e um TUBO de ponta chata com base de
 * latao, e essa silhueta gorda e curta e parte da identidade da arma.
 */
function shotShell(pb, x, y, z, mat) {
  pb.add(tubeZ(0.0098, 0.0098, 0.058, 14), mat || 'brass', { x, y, z, wear: 0.8, noise: 0.10 });
  // friso da prensagem, na ponta
  pb.add(latheZ([[0.0100, -0.0030], [0.0100, 0.0030]], 14), 'dark', { x, y, z: z - 0.023, wear: 0.2, noise: 0 });
  // base de latao, atras
  pb.add(latheZ([[0.0102, -0.010], [0.0102, 0.010]], 14), 'brass', { x, y, z: z + 0.019, wear: 0.9 });
}

/**
 * Cartucho de fuzil (tubo de latao + ogiva) na boca de um pente. As tres armas
 * originais repetem estas quatro linhas inline; as novas chamam daqui porque a
 * metralhadora mostra TRES deles na boca do tambor.
 *
 * A ogiva sai num latheZ com raio CRESCENTE em z para a normal apontar para -Z
 * (frente), e vai posicionada na ponta do tubo, em zCenter - len/2.
 */
function riflePrimer(pb, x, y, zCenter, len, radius) {
  pb.add(tubeZ(radius, radius, len, 12), 'brass', { x, y, z: zCenter, wear: 0.8 });
  pb.add(latheZ([
    [radius * 0.33, -len * 0.36], [radius * 0.70, -len * 0.19], [radius, 0.0]
  ], 12), 'brass', { x, y, z: zCenter - len / 2, wear: 0.9 });
}



/** Volume organico barato, usado na palma, juntas e pontas dos dedos. */
function handEllipsoid(rx, ry, rz, seg) {
  const g = new THREE.SphereGeometry(1, seg || 12, 8);
  g.scale(rx, ry, rz);
  return g;
}

/**
 * Falange arredondada e afunilada entre dois pontos. O perfil engrossa perto da
 * junta e perde raio na ponta; as juntas sobrepostas escondem as emendas.
 */
function addPhalanx(pb, x0, y0, z0, x1, y1, z1, baseR, tipR, tint) {
  const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-5) return;
  const h = len * 0.5;
  const round = Math.min(baseR, tipR) * 0.72;
  const geom = latheZ([
    [baseR * 0.72, -h],
    [baseR, -h + round],
    [baseR * 1.02, -h + len * 0.24],
    [(baseR + tipR) * 0.51, 0],
    [tipR, h - len * 0.22],
    [tipR * 0.78, h - round * 0.45],
    [0, h]
  ], 10);
  const axis = new THREE.Vector3(0, 0, 1);
  const dir = new THREE.Vector3(dx / len, dy / len, dz / len);
  geom.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(axis, dir));
  pb.add(geom, 'glove', {
    x: (x0 + x1) * 0.5, y: (y0 + y1) * 0.5, z: (z0 + z1) * 0.5,
    wear: 0.24, noise: 0.14, tint
  });
}

/** Junta levemente mais larga que as falanges vizinhas. */
function addFingerJoint(pb, x, y, z, r, tint) {
  pb.add(handEllipsoid(r * 1.08, r * 0.96, r * 1.12, 10), 'glove',
    { x, y, z, wear: 0.20, noise: 0.13, tint });
}

/**
 * Dedo articulado em tres falanges. O eixo da empunhadura e Y; cada dobra muda
 * a direcao no plano XZ e um pequeno deslocamento em Y evita dedos paralelos.
 * Angulo zero aponta para -Z; positivo curva para -X.
 */
function fingerChain(pb, x, y, z, ang, segs, radius, curl, tint) {
  let px = x, py = y, pz = z, a = ang;
  const c = curl === undefined ? 1 : curl;
  addFingerJoint(pb, px, py, pz, radius * 1.12, tint);

  for (let i = 0; i < segs.length; i++) {
    const len = segs[i][0];
    a += segs[i][1] * c;
    const ex = px - Math.sin(a) * len;
    const ey = py + (segs[i][2] || 0) * c;
    const ez = pz - Math.cos(a) * len;
    const baseR = radius * (1 - i * 0.13);
    const tipR = radius * (0.86 - i * 0.13);
    addPhalanx(pb, px, py, pz, ex, ey, ez, baseR, tipR, tint);
    px = ex; py = ey; pz = ez;
    if (i < segs.length - 1) addFingerJoint(pb, px, py, pz, tipR * 1.10, tint);
  }

  const tip = radius * 0.59;
  pb.add(handEllipsoid(tip * 0.94, tip * 0.88, tip * 1.18, 10), 'glove',
    { x: px, y: py, z: pz, wear: 0.16, noise: 0.12, tint });
}

/**
 * Mao direita canonica. A pose trigger leva o indicador ao gatilho e fecha os
 * outros tres dedos no punho. A pose support mantem quatro dedos curvos em volta
 * do guarda-mao; a mao esquerda nasce do espelhamento desta malha.
 */
function buildHand(mats, pose, curl, wideGrip) {
  const g = new THREE.Group();
  const pb = new PartBuilder();
  const c = curl === undefined ? 1 : curl;
  const support = pose === 'support';
  const spread = support && wideGrip ? 0.42 : 0;
  const seamTint = [0.68, 0.70, 0.74];
  const padTint = [0.58, 0.60, 0.64];

  // Palma e dorso abaulados. Tres volumes sobrepostos removem a leitura de bloco.
  pb.add(handEllipsoid(0.036, 0.046, 0.028, 14), 'glove',
    { x: 0.026, y: -0.006, z: 0.013, ry: -0.25, wear: 0.18, noise: 0.16 });
  pb.add(handEllipsoid(0.028, 0.040, 0.023, 12), 'glove',
    { x: 0.012, y: -0.005, z: 0.006, ry: -0.16, wear: 0.16, noise: 0.16 });
  // Eminencia tenar: o polegar nasce deste volume, nao da lateral de uma caixa.
  pb.add(handEllipsoid(0.024, 0.029, 0.023, 12), 'glove',
    { x: 0.018, y: 0.018, z: 0.029, ry: -0.42, wear: 0.17, noise: 0.15 });

  // Dois tendoes discretos no dorso. Usam o mesmo material para nao criar draw call.
  pb.add(tubeZ(0.0013, 0.0020, 0.050, 8), 'glove',
    { x: 0.049, y: -0.005, z: 0.004, rx: Math.PI * 0.5, rz: 0.10, wear: 0.12, noise: 0.10, tint: seamTint });
  pb.add(tubeZ(0.0011, 0.0018, 0.046, 8), 'glove',
    { x: 0.048, y: -0.008, z: 0.017, rx: Math.PI * 0.5, rz: -0.08, wear: 0.12, noise: 0.10, tint: seamTint });

  if (support) {
    // Medio mais longo; indicador e anelar intermediarios; mindinho bem menor.
    // As tres dobras continuam fortes mesmo na pega larga.
    fingerChain(pb, 0.022, 0.030, -0.008, 0.12 + spread,
      [[0.027, 0.34, -0.0005], [0.019, 0.58, -0.0011], [0.014, 0.56, -0.0015]], 0.0083, c, padTint);
    fingerChain(pb, 0.022, 0.008, -0.010, 0.08 + spread * 1.06,
      [[0.031, 0.36, -0.0007], [0.022, 0.62, -0.0013], [0.015, 0.58, -0.0018]], 0.0087, c, padTint);
    fingerChain(pb, 0.022, -0.014, -0.009, 0.10 + spread * 1.12,
      [[0.029, 0.38, -0.0009], [0.020, 0.64, -0.0015], [0.014, 0.60, -0.0020]], 0.0082, c, padTint);
    fingerChain(pb, 0.021, -0.034, -0.006, 0.13 + spread * 1.18,
      [[0.023, 0.42, -0.0011], [0.016, 0.66, -0.0017], [0.011, 0.62, -0.0022]], 0.0072, c, padTint);

    // Polegar sai pelo lado oposto aos quatro dedos e trava a pega por cima.
    fingerChain(pb, 0.015, 0.027, 0.032, wideGrip ? -0.82 : -0.66,
      [[0.026, -0.36, 0.0018], [0.019, -0.48, 0.0012]], 0.0092, c, seamTint);
  } else {
    // Indicador destacado e menos fechado: a ponta cai sobre o gatilho.
    fingerChain(pb, 0.021, 0.030, -0.006, -0.18,
      [[0.024, -0.62, -0.0006], [0.018, 0.10, -0.0010], [0.012, 0.15, -0.0011]], 0.0081, 1, padTint);
    // Medio, anelar e mindinho fecham progressivamente em volta do punho.
    fingerChain(pb, 0.022, 0.008, -0.007, -0.05,
      [[0.029, -0.42, -0.0008], [0.020, -0.66, -0.0014], [0.014, -0.62, -0.0018]], 0.0087, 1, padTint);
    fingerChain(pb, 0.022, -0.014, -0.006, -0.04,
      [[0.027, -0.44, -0.0010], [0.019, -0.69, -0.0016], [0.013, -0.64, -0.0020]], 0.0082, 1, padTint);
    fingerChain(pb, 0.021, -0.034, -0.004, -0.02,
      [[0.022, -0.48, -0.0012], [0.015, -0.72, -0.0018], [0.010, -0.66, -0.0022]], 0.0072, 1, padTint);

    // Polegar cruza por cima no sentido contrario ao fechamento dos dedos.
    fingerChain(pb, 0.014, 0.025, 0.032, 0.58,
      [[0.026, 0.48, 0.0020], [0.018, 0.50, 0.0014]], 0.0092, 1, seamTint);
  }

  // Punho afunilado e manguito inclinado ligam a palma ao antebraco.
  pb.add(handEllipsoid(0.030, 0.021, 0.030, 12), 'glove',
    { x: 0.031, y: -0.050, z: 0.024, ry: -0.28, rx: 0.20, wear: 0.16, noise: 0.15 });
  pb.add(latheZ([[0.026, -0.014], [0.030, -0.006], [0.031, 0.008], [0.028, 0.016]], 14), 'glove',
    { x: 0.031, y: -0.057, z: 0.027, rx: Math.PI * 0.5 + 0.20, ry: -0.28, wear: 0.18, noise: 0.14, tint: seamTint });

  // Uma unica malha de luva por mao.
  pb.flush(g, mats, false);
  return g;
}

/**
 * Antebraco em grupo separado. A manga segue para tras e para fora ate cruzar a
 * borda da tela; o perfil cresce suavemente do pulso em direcao ao cotovelo.
 */
function buildArm(mats) {
  const g = new THREE.Group();
  const pb = new PartBuilder();
  pb.add(latheZ([
    [0.0255, -0.012], [0.0288, 0.010], [0.0305, 0.026],
    [0.0320, 0.080], [0.0332, 0.150], [0.0348, 0.240],
    [0.0360, 0.330], [0.0370, 0.420]
  ], 20), 'sleeve', { wear: 0.18, noise: 0.24 });
  // Punho escuro fundido na manga: uma malha de antebraco por lado.
  pb.add(latheZ([[0.0304, 0.008], [0.0326, 0.015], [0.0326, 0.024], [0.0305, 0.030]], 16),
    'sleeve', { wear: 0.22, noise: 0.15, tint: [0.58, 0.60, 0.56] });
  pb.flush(g, mats, false);
  g.position.set(0.030, -0.046, 0.020);
  return g;
}

/**
 * Pendura o antebraco na mao compensando a rotacao dela. O grupo continua filho
 * da mao, portanto acompanha sway, bob, recuo, ADS, corrida e recarga.
 */
function attachArm(handGroup, mats, pitch, yaw, side) {
  const arm = buildArm(mats);
  if (side < 0) arm.position.x = -arm.position.x;
  const want = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, yaw, 0, 'YXZ'));
  arm.quaternion.copy(handGroup.quaternion).invert().multiply(want);
  handGroup.add(arm);
}

/** Ambos os antebracos seguem para baixo e para fora ate cruzarem a borda. */
function attachArms(hands, mats) {
  attachArm(hands.right, mats, 0.72, 0.62, 1);
  attachArm(hands.left, mats, 0.78, -0.68, -1);
}

/**
 * Par de maos com poses diferentes. Cada lado tem uma malha de luva e uma de
 * manga: quatro draw calls para as duas maos completas com antebracos.
 */
function buildHandPair(mats, leftCurl) {
  const right = new THREE.Group();
  right.add(buildHand(mats, 'trigger', 1, false));

  const left = new THREE.Group();
  const mirror = new THREE.Group();
  const supportCurl = leftCurl === undefined ? 1 : leftCurl;
  mirror.scale.x = -1;
  mirror.add(buildHand(mats, 'support', supportCurl, supportCurl < 0.99));
  left.add(mirror);

  return { right, left };
}
// ---------------------------------------------------------------------------
// Construcao das armas
// ---------------------------------------------------------------------------

/**
 * Silhueta lateral: os pontos sao lidos como (z, y) DA ARMA e a espessura sai no
 * eixo X. E assim que coronha, punho e carregador ganham perfil de verdade em vez
 * de virarem caixas empilhadas.
 */
function sideProfile(points, width, bevel) {
  const g = chamferShape(polyShape(points), width, bevel, 5);
  g.rotateY(-Math.PI / 2);
  return g;
}

/** Objeto vazio marcador (boca do cano, porta de ejecao, eixo optico). */
function marker(x, y, z) {
  const o = new THREE.Object3D();
  o.position.set(x, y, z);
  o.matrixAutoUpdate = false;
  o.updateMatrix();
  return o;
}

/**
 * FUZIL DE ASSALTO - carabina 5.56 tipo AR, cano curto, guarda-mao com rasgos,
 * apagador de chamas de tres dentes. Identidade: perfil anguloso e trilho longo.
 * Eixo do cano em y=0; linha de mira em y=0.056; frente para -Z.
 */
function buildRifle(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.056;      // linha de mira
  const RY = 0.034;      // altura do trilho

  // --- coronha ------------------------------------------------------------
  pb.add(sideProfile([
    [0.010, 0.030], [0.070, 0.033], [0.140, 0.030], [0.196, 0.024],
    [0.208, 0.006], [0.208, -0.034], [0.150, -0.030], [0.086, -0.006],
    [0.030, -0.004], [0.010, -0.012]
  ], 0.040, 0.007), 'polymer', { wear: 0.35, noise: 0.14 });
  // tubo do amortecedor por dentro da coronha
  pb.add(tubeZ(0.019, 0.019, 0.16, 18), 'steel', { x: 0, y: 0.014, z: 0.085, wear: 0.7 });
  // travessa de ajuste embaixo
  pb.add(chamferBox(0.020, 0.012, 0.150, 0.003), 'steel', { x: 0, y: -0.026, z: 0.115, wear: 0.8 });
  // coice de borracha
  pb.add(chamferBox(0.046, 0.076, 0.016, 0.006), 'rubber', { x: 0, y: -0.004, z: 0.212, wear: 0.4, noise: 0.2 });
  // encaixe de bandoleira
  pb.add(ringZ(0.010, 0.0028, 12), 'steel', { x: -0.022, y: -0.020, z: 0.190, ry: Math.PI / 2, wear: 1.0 });
  // painel lateral, frestas de molde e alavanca quebram a grande laje da coronha
  pb.add(chamferBox(0.0022, 0.0030, 0.112, 0.0006), 'dark', { x: -0.0208, y: 0.019, z: 0.108, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.0022, 0.0030, 0.090, 0.0006), 'dark', { x: -0.0208, y: -0.018, z: 0.130, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.0022, 0.034, 0.0030, 0.0006), 'dark', { x: -0.0208, y: 0.001, z: 0.066, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.026, 0.011, 0.032, 0.003), 'bright', { x: 0, y: -0.035, z: 0.142, rx: -0.18, wear: 0.8 });
  /**
   * RECORTE DE SILHUETA NA CORONHA, na escala que o olho le como acabamento.
   * Linha de molde e grao de textura nao resolvem uma laje de ~200 px de altura:
   * a 37 cm do olho, feature de 1 a 12 cm da 20 a 150 px, e so GEOMETRIA entra
   * nessa faixa. Sao tres pecas, cada uma com sua propria fresta:
   *  - apoio de face sobreposto ao topo (13 mm de degrau, 1 mm mais estreito que
   *    a coronha dos dois lados, entao a aresta de topo deixa de ser uma reta de
   *    35 cm - que era tambem a origem do risco continuo tipo fita adesiva);
   *  - rasgo de bandoleira ABERTO atravessando a coronha (le como vazio, nao como
   *    linha pintada);
   *  - a placa de coice separada do corpo por uma fresta de 2.6 mm em volta.
   */
  pb.add(chamferBox(0.038, 0.013, 0.116, 0.004), 'polymer', { x: 0, y: 0.036, z: 0.092, wear: 0.4, noise: 0.16 });
  pb.add(chamferBox(0.0405, 0.0026, 0.112, 0.0006), 'dark', { x: 0, y: 0.0293, z: 0.092, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.046, 0.011, 0.028, 0.0022), 'dark', { x: 0, y: -0.012, z: 0.168, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.050, 0.080, 0.0026, 0.0006), 'dark', { x: 0, y: -0.004, z: 0.2042, wear: 0.1, noise: 0 });

  // --- receptor superior --------------------------------------------------
  pb.add(chamferBox(0.047, 0.047, 0.243, 0.006), 'steel', { x: 0, y: 0.010, z: -0.120, wear: 0.85 });
  // aba do receptor (aumenta a silhueta perto do olho)
  pb.add(chamferBox(0.050, 0.020, 0.055, 0.005), 'steel', { x: 0, y: 0.006, z: 0.010, wear: 0.9 });
  /**
   * FRESTA DA JUNTA receptor superior / receptor inferior, na face -X (a que a
   * camera ve). O AO assado nao consegue produzir isso sozinho: as duas pecas se
   * encostam por faces PLANAS e paralelas, e o hemisferio de 3 cm de bakeVertexAO
   * enxerga ceu aberto em cima da junta - por isso punho, guarda-mato, poco do
   * pente e coronha saiam todos no mesmo valor, sem uma linha separando um do
   * outro. Uma caixa 'dark' de 2 mm na altura exata do encaixe resolve de graca.
   */
  pb.add(chamferBox(0.0020, 0.0028, 0.132, 0.0006), 'dark', { x: -0.0240, y: 0.0045, z: -0.084, wear: 0.1, noise: 0 });
  // trilho superior continuo: a junta e uma fresta curta, nao 4.5 cm sem dentes
  railTop(pb, 0, RY, -0.2225, 0.430);
  pb.add(chamferBox(0.022, 0.0020, 0.0020, 0.0005), 'dark', { x: 0, y: RY + 0.004, z: -0.230, wear: 0.1, noise: 0 });

  // --- receptor inferior e poco do carregador ------------------------------
  pb.add(sideProfile([
    [-0.012, -0.004], [-0.012, -0.030], [-0.036, -0.036], [-0.052, -0.034],
    [-0.070, -0.014], [-0.120, -0.010], [-0.120, 0.004], [-0.012, 0.004]
  ], 0.041, 0.005), 'steel', { wear: 0.8 });
  pb.add(chamferBox(0.040, 0.066, 0.076, 0.007), 'steel', { x: 0, y: -0.040, z: -0.086, rx: 0.10, wear: 0.9 });
  pb.add(chamferBox(0.044, 0.014, 0.082, 0.005), 'steel', { x: 0, y: -0.012, z: -0.086, rx: 0.10, wear: 0.95 });
  // botao de retencao do carregador e retem do ferrolho
  pb.add(tubeZ(0.006, 0.006, 0.012, 10), 'bright', { x: 0.022, y: -0.016, z: -0.056, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.006, 0.020, 0.026, 0.002), 'bright', { x: -0.023, y: -0.016, z: -0.052, wear: 1.0 });
  // seletor de tiro
  pb.add(tubeZ(0.008, 0.008, 0.046, 16), 'blued', { x: 0, y: -0.008, z: -0.030, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.008, 0.010, 0.030, 0.002), 'blued', { x: -0.026, y: -0.012, z: -0.024, rx: 0.5, wear: 1.0 });
  // Marcas pintadas do seletor. Ficam no lado ESQUERDO porque e o lado que a
  // camera ve: com a arma em x=+0.108 e roll +0.180, a normal da face direita da
  // produto NEGATIVO com a direcao para o olho - o lado direito e traseira.
  pb.add(chamferBox(0.0018, 0.0026, 0.0105, 0.0005), 'paint', { x: -0.0208, y: 0.0035, z: -0.0405 });
  pb.add(chamferBox(0.0018, 0.0105, 0.0026, 0.0005), 'paint', { x: -0.0208, y: 0.0000, z: -0.0165 });
  // assistente de fechamento (lado direito)
  pb.add(tubeZ(0.008, 0.009, 0.014, 10), 'steel', { x: 0.026, y: 0.020, z: -0.062, ry: Math.PI / 2, wear: 0.95 });

  // --- porta de ejecao ----------------------------------------------------
  // Conjunto no lado -X, que e o lado voltado para a camera na pose de quadril.
  pb.add(chamferBox(0.005, 0.030, 0.058, 0.001), 'dark', { x: -0.0225, y: 0.010, z: -0.150, wear: 0.3, noise: 0 });
  pb.add(chamferBox(0.005, 0.030, 0.056, 0.0018), 'blued', { x: -0.0345, y: -0.002, z: -0.150, rz: 0.55, wear: 1.0 });
  pb.add(chamferBox(0.014, 0.024, 0.026, 0.006), 'steel', { x: -0.026, y: 0.026, z: -0.112, wear: 0.9 });

  // --- punho ---------------------------------------------------------------
  pb.add(sideProfile([
    [-0.046, -0.020], [-0.010, -0.026], [0.006, -0.070], [0.004, -0.104],
    [-0.020, -0.110], [-0.040, -0.086], [-0.052, -0.040]
  ], 0.033, 0.009), 'polymer', { wear: 0.3, noise: 0.16 });
  // sulcos dos dedos na frente do punho
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.031, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.048 - i * 0.020, z: -0.043 + i * 0.006, rx: 0.35, wear: 0.5, noise: 0.25 });
  }
  pb.add(chamferBox(0.030, 0.010, 0.026, 0.004), 'rubber', { x: 0, y: -0.108, z: -0.008, wear: 0.4, noise: 0.2 });
  // fresta do encaixe do punho no receptor: mesma razao da junta do receptor,
  // duas faces planas que o AO de 3 cm nao consegue ocluir
  pb.add(chamferBox(0.035, 0.0024, 0.042, 0.0006), 'dark', { x: 0, y: -0.0225, z: -0.032, wear: 0.1, noise: 0 });

  triggerGuard(pb, 0, -0.017, -0.052, mats);

  // --- guarda-mao ----------------------------------------------------------
  // 8 lados a 60 cm do olho davam facetas de 9 px com salto de normal de 45
  // graus: dava para CONTAR os lados no contorno. 20 lados custa ~24 triangulos.
  pb.add(tubeZ(0.0295, 0.0305, 0.195, 20, true), 'polymer', { x: 0, y: 0.004, z: -0.345, rz: Math.PI / 20, wear: 0.35, noise: 0.15 });
  // Boca do guarda-mao com ESPESSURA DE PAREDE. O tubo era fechado por um disco
  // chapado virado para a camera; agora e aberto, com um anel de parede na frente
  // e o vao interno escuro ate o cano - que e o que se ve num guarda-mao real.
  frontRing(pb, 0.004, -0.4425, 0.0295, 0.0305, 0.0095, 'polymer');
  backRing(pb, 0.004, -0.2475, 0.0305, 'polymer');
  // anel de reforco traseiro e dianteiro
  pb.add(latheZ([[0.032, -0.008], [0.034, -0.004], [0.034, 0.004], [0.032, 0.008]], 24), 'steel', { x: 0, y: 0.004, z: -0.250, wear: 0.9 });
  pb.add(latheZ([[0.030, -0.007], [0.032, -0.003], [0.032, 0.003], [0.030, 0.007]], 24), 'steel', { x: 0, y: 0.004, z: -0.437, wear: 0.9 });
  // rasgos M-LOK dos dois lados e embaixo. Os raios subiram junto com a contagem
  // de lados: num tubo de 20 lados a face fica em 0.0301 (nao 0.0282 como no de
  // 8), e nesse raio antigo os rasgos ficavam DENTRO da parede.
  for (let i = 0; i < 4; i++) {
    const z = -0.278 - i * 0.038;
    pb.add(chamferBox(0.006, 0.011, 0.030, 0.0018), 'dark', { x: 0.0295, y: 0.004, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.006, 0.011, 0.030, 0.0018), 'dark', { x: -0.0295, y: 0.004, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.011, 0.006, 0.030, 0.0018), 'dark', { x: 0, y: -0.0256, z, wear: 0.4, noise: 0 });
  }
  // apoio de mao inferior
  pb.add(chamferBox(0.026, 0.020, 0.030, 0.006), 'polymer', { x: 0, y: -0.032, z: -0.300, rx: -0.30, wear: 0.4, noise: 0.15 });

  // --- cano e boca ---------------------------------------------------------
  pb.add(tubeZ(0.0100, 0.0115, 0.320, 20), 'blued', { x: 0, y: 0, z: -0.400, wear: 0.85 });
  // bloco de gas e tubo de gas
  pb.add(chamferBox(0.024, 0.030, 0.028, 0.004), 'blued', { x: 0, y: 0.008, z: -0.462, wear: 1.0 });
  pb.add(tubeZ(0.0035, 0.0035, 0.115, 8), 'bright', { x: 0, y: 0.021, z: -0.406, wear: 1.0 });
  // porca do cano
  pb.add(latheZ([[0.0125, -0.010], [0.0145, -0.006], [0.0145, 0.006], [0.0125, 0.010]], 20), 'blued', { x: 0, y: 0, z: -0.520, wear: 1.0 });
  // apagador de chamas: perfil e dentes espessos o bastante para sobreviver ao AA
  pb.add(latheZ([
    [0.0105, -0.588], [0.0175, -0.584], [0.0175, -0.572], [0.0148, -0.570],
    [0.0148, -0.562], [0.0190, -0.558], [0.0190, -0.548], [0.0145, -0.545]
  ], 20), 'blued', { wear: 1.0 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.008, 0.020, 0.022, 0.0018), 'dark',
      { x: 0, y: 0.014, z: -0.578, rz: i * (Math.PI * 2 / 3) + 0.5, wear: 0.4, noise: 0 });
  }
  muzzleBore(pb, 0, 0, -0.588, 0.0110, 0.0038);

  // --- miras ---------------------------------------------------------------
  ironSights(pb, SY, -0.430, -0.055);

  pb.flush(root, mats, false);

  // --- pecas animadas ------------------------------------------------------
  // ferrolho visivel pela porta de ejecao na face -X
  const bolt = part(chamferBox(0.005, 0.017, 0.050, 0.0012), 'bright', mats,
    { x: -0.0245, y: 0.018, z: -0.150, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // alavanca de armar em T, no alto e atras
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(chamferBox(0.062, 0.011, 0.014, 0.003), 'steel', { x: 0, y: 0.030, z: 0.004, wear: 1.0 });
  cpb.add(chamferBox(0.016, 0.009, 0.048, 0.002), 'steel', { x: 0, y: 0.030, z: -0.022, wear: 1.0 });
  cpb.add(chamferBox(0.014, 0.008, 0.012, 0.002), 'bright', { x: -0.030, y: 0.026, z: 0.000, wear: 1.0 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.020, -0.046);
  root.add(trigger);

  // carregador curvo de 30 tiros
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  // silhueta em banana: a boca fica atras e a base avanca para a frente
  mpb.add(sideProfile([
    [-0.122, -0.014], [-0.064, -0.016], [-0.072, -0.060], [-0.084, -0.112],
    [-0.094, -0.160], [-0.152, -0.168], [-0.144, -0.112], [-0.134, -0.058]
  ], 0.027, 0.005), 'polymer', { wear: 0.45, noise: 0.18 });
  mpb.add(chamferBox(0.031, 0.011, 0.062, 0.004), 'polymer', { x: 0, y: -0.168, z: -0.123, rx: -0.30, wear: 0.6, noise: 0.2 });
  for (let i = 0; i < 3; i++) {
    mpb.add(chamferBox(0.029, 0.006, 0.008, 0.0015), 'dark',
      { x: 0, y: -0.062 - i * 0.032, z: -0.104 - i * 0.011, wear: 0.3, noise: 0 });
  }
  // labio superior metalico e o primeiro cartucho aparecendo
  mpb.add(chamferBox(0.028, 0.012, 0.058, 0.003), 'blued', { x: 0, y: -0.020, z: -0.093, wear: 1.0 });
  // Cartucho na boca do pente, agora em LATAO de verdade (era 'bright' cinza com
  // um tint). Com o pente de volta ao quadro, e o unico ponto quente da paleta.
  mpb.add(tubeZ(0.0050, 0.0050, 0.038, 12), 'brass', { x: 0.003, y: -0.0125, z: -0.094, wear: 0.8 });
  // ogiva: raio CRESCENTE com z para a normal sair para -Z (frente)
  mpb.add(latheZ([[0.0016, -0.0130], [0.0034, -0.0070], [0.0050, 0.0000]], 12), 'brass',
    { x: 0.003, y: -0.0125, z: -0.113, wear: 0.9 });
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  // --- maos ----------------------------------------------------------------
  /**
   * MAOS. A esquerda estava com o centro da palma na mesma altura do eixo do cano
   * e o manguito a 2.8 cm do eixo, ou seja DENTRO do tubo do guarda-mao: ela
   * agarrava o tubo pelo lado, os dedos ficavam do lado oposto ao da camera e so
   * escapavam o dorso e um no - os tres blocos cinza soltos do print. Agora a
   * palma apoia POR BAIXO (centro ~4.5 cm abaixo do eixo) e os dedos sobem por
   * cima do tubo. A direita subiu para os nos ficarem visiveis sobre o guarda-mato.
   */
  const hands = buildHandPair(mats, 0.88);
  // A palma direita fica inteira na face -X, diante do punho e do poco do pente.
  hands.right.position.set(-0.045, -0.048, -0.038);
  hands.right.rotation.set(-0.30, -0.12, 0.10);
  // A base abre sobre o tubo e as falanges, ainda curvadas, passam por cima e
  // voltam em direcao a camera. O polegar cruza a lateral visivel.
  hands.left.position.set(-0.024, -0.064, -0.335);
  hands.left.rotation.set(1.82, 0.28, 3.142);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'rifle',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.046, chargingTravel: 0.070, triggerTravel: 0.0045,
    boltAxis: 1,                         // ferrolho anda em +Z
    muzzle: marker(0, 0, -0.592),
    ejectPort: marker(-0.030, 0.016, -0.150),
    sight: marker(0, SY, -0.055),
    sightY: SY,
    leftHandBase: new THREE.Vector3(-0.024, -0.064, -0.335),
    magAxis: new THREE.Vector3(0.06, -0.955, -0.29).normalize(),
    /**
     * A placa de coice fica a 36.8 cm do olho e a razao boca/coice cai para
     * 3.19:1. O yaw quase nulo converge o eixo do cano na mira; x/y recompostos
     * mantem a boca proxima de NDC (0.10,-0.24) com a lente de 52 graus.
     */
    hipPos: new THREE.Vector3(0.108, -0.137, -0.580),
    hipRot: new THREE.Euler(0.010, 0.008, 0.180, 'YXZ'),
    sprintPos: new THREE.Vector3(0.148, -0.197, -0.527),
    sprintRot: new THREE.Euler(-0.330, 0.545, 0.640, 'YXZ'),
    recoilScale: 1.0
  };
}
/**
 * SUBMETRALHADORA 9mm - receptor tubular estampado, coronha retratil de haste,
 * punho vertical dianteiro. Identidade: SUPRESSOR gordo na frente, que muda a
 * silhueta inteira e a leitura da arma na tela.
 */
function buildSmg(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.050;
  const RY = 0.029;

  // --- coronha retratil ----------------------------------------------------
  pb.add(chamferBox(0.052, 0.040, 0.030, 0.008), 'steel', { x: 0, y: 0.004, z: 0.012, wear: 0.9 });
  for (const sx of [-1, 1]) {
    pb.add(tubeZ(0.0055, 0.0055, 0.150, 8), 'blued', { x: sx * 0.020, y: 0.004, z: 0.098, wear: 1.0 });
  }
  pb.add(chamferBox(0.056, 0.016, 0.014, 0.004), 'steel', { x: 0, y: 0.004, z: 0.168, wear: 1.0 });
  pb.add(sideProfile([
    [0.168, 0.030], [0.182, 0.028], [0.182, -0.032], [0.168, -0.030], [0.168, -0.006], [0.168, 0.008]
  ], 0.044, 0.005), 'rubber', { wear: 0.4, noise: 0.2 });
  // travessa da coronha
  pb.add(chamferBox(0.048, 0.010, 0.012, 0.003), 'steel', { x: 0, y: -0.014, z: 0.070, wear: 0.9 });

  // --- receptor tubular ----------------------------------------------------
  pb.add(tubeZ(0.0255, 0.0255, 0.226, 18), 'steel', { x: 0, y: 0.002, z: -0.112, wear: 0.85 });
  // topo achatado com o trilho
  pb.add(chamferBox(0.034, 0.014, 0.220, 0.004), 'steel', { x: 0, y: 0.022, z: -0.112, wear: 0.9 });
  railTop(pb, 0, RY, -0.112, 0.210);
  // aneis estruturais estampados
  for (const z of [-0.030, -0.196]) {
    pb.add(latheZ([[0.0265, -0.006], [0.0285, -0.002], [0.0285, 0.002], [0.0265, 0.006]], 18), 'steel', { x: 0, y: 0.002, z, wear: 1.0 });
  }
  // tampa traseira com o pino de desmontagem
  pb.add(latheZ([[0.0, -0.006], [0.026, -0.004], [0.027, 0.004], [0.0, 0.006]], 18), 'steel', { x: 0, y: 0.002, z: 0.000, wear: 0.9 });
  pb.add(tubeZ(0.005, 0.005, 0.010, 8), 'bright', { x: 0.014, y: -0.010, z: 0.004, wear: 1.0 });

  // --- porta de ejecao e alavanca de armar (lado visivel) ------------------
  pb.add(chamferBox(0.005, 0.026, 0.046, 0.001), 'dark', { x: -0.0235, y: 0.006, z: -0.150, wear: 0.3, noise: 0 });
  // tubo guia da alavanca, por cima e a esquerda
  pb.add(tubeZ(0.010, 0.010, 0.115, 10), 'steel', { x: -0.026, y: 0.019, z: -0.180, wear: 0.95 });

  // --- carcaca do gatilho e punho ------------------------------------------
  pb.add(sideProfile([
    [-0.020, -0.014], [-0.126, -0.014], [-0.126, -0.044], [-0.096, -0.048],
    [-0.076, -0.044], [-0.030, -0.046], [-0.020, -0.040]
  ], 0.040, 0.006), 'polymer', { wear: 0.35, noise: 0.16 });
  pb.add(sideProfile([
    [-0.038, -0.036], [-0.010, -0.040], [0.004, -0.082], [0.000, -0.116],
    [-0.026, -0.120], [-0.040, -0.092], [-0.048, -0.052]
  ], 0.032, 0.009), 'polymer', { wear: 0.3, noise: 0.16 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.030, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.058 - i * 0.020, z: -0.038 + i * 0.005, rx: 0.32, wear: 0.5, noise: 0.25 });
  }
  triggerGuard(pb, 0, -0.026, -0.062, mats);
  // seletor ambidestro
  pb.add(tubeZ(0.007, 0.007, 0.044, 16), 'blued', { x: 0, y: -0.020, z: -0.042, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.008, 0.009, 0.026, 0.002), 'blued', { x: -0.025, y: -0.024, z: -0.036, rx: 0.6, wear: 1.0 });
  // marcas do seletor, no lado que a camera ve
  pb.add(chamferBox(0.0018, 0.0026, 0.0100, 0.0005), 'paint', { x: -0.0202, y: -0.0100, z: -0.0520 });
  pb.add(chamferBox(0.0018, 0.0100, 0.0026, 0.0005), 'paint', { x: -0.0202, y: -0.0135, z: -0.0290 });

  // --- guarda-mao e punho vertical -----------------------------------------
  pb.add(chamferBox(0.048, 0.046, 0.090, 0.010), 'polymer', { x: 0, y: -0.002, z: -0.256, wear: 0.35, noise: 0.15 });
  for (let i = 0; i < 3; i++) {
    const z = -0.226 - i * 0.028;
    pb.add(chamferBox(0.052, 0.009, 0.014, 0.002), 'dark', { x: 0, y: 0.004, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.052, 0.009, 0.014, 0.002), 'dark', { x: 0, y: -0.014, z, wear: 0.4, noise: 0 });
  }
  pb.add(sideProfile([
    [-0.284, -0.024], [-0.246, -0.024], [-0.242, -0.070], [-0.248, -0.100],
    [-0.276, -0.104], [-0.288, -0.072]
  ], 0.030, 0.008), 'polymer', { wear: 0.3, noise: 0.18 });
  pb.add(chamferBox(0.032, 0.010, 0.044, 0.004), 'rubber', { x: 0, y: -0.104, z: -0.264, wear: 0.4, noise: 0.2 });

  // --- cano e supressor ----------------------------------------------------
  pb.add(tubeZ(0.0095, 0.0105, 0.110, 18), 'blued', { x: 0, y: 0, z: -0.300, wear: 0.9 });
  // corpo do supressor: tubo grosso com aneis de refrigeracao
  pb.add(tubeZ(0.0225, 0.0225, 0.165, 20), 'blued', { x: 0, y: 0, z: -0.388, wear: 0.75, noise: 0.12 });
  for (let i = 0; i < 6; i++) {
    pb.add(latheZ([[0.0228, -0.0025], [0.0246, -0.0012], [0.0246, 0.0012], [0.0228, 0.0025]], 20),
      'blued', { x: 0, y: 0, z: -0.330 - i * 0.024, wear: 1.0 });
  }
  // tampa traseira e dianteira do supressor
  pb.add(latheZ([[0.0130, -0.474], [0.0232, -0.470], [0.0232, -0.462], [0.0180, -0.458]], 20), 'blued', { wear: 1.0 });
  pb.add(latheZ([[0.0180, -0.314], [0.0232, -0.310], [0.0232, -0.302], [0.0140, -0.300]], 20), 'blued', { wear: 1.0 });
  muzzleBore(pb, 0, 0, -0.476, 0.0125, 0.0048);

  // --- miras ---------------------------------------------------------------
  // massa de mira em torre, tipica de submetralhadora, sobre o guarda-mao
  pb.add(latheZ([[0.006, -0.298], [0.014, -0.292], [0.014, -0.282], [0.007, -0.278]], 12),
    'blued', { x: 0, y: SY - 0.014, z: 0, wear: 1.0 });
  ironSights(pb, SY, -0.290, -0.046);

  pb.flush(root, mats, false);

  // --- pecas animadas ------------------------------------------------------
  const bolt = part(chamferBox(0.005, 0.015, 0.040, 0.0012), 'bright', mats,
    { x: -0.0255, y: 0.013, z: -0.150, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // alavanca de armar: anda junto com o ferrolho (aqui ela e reciprocante)
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(chamferBox(0.030, 0.012, 0.016, 0.004), 'steel', { x: -0.034, y: 0.019, z: -0.232, wear: 1.0 });
  cpb.add(chamferBox(0.012, 0.010, 0.040, 0.003), 'steel', { x: -0.028, y: 0.019, z: -0.216, wear: 1.0 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.030, -0.056);
  root.add(trigger);

  // carregador reto de 30, levemente inclinado para a frente
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  mpb.add(sideProfile([
    [-0.140, -0.020], [-0.092, -0.020], [-0.100, -0.090], [-0.108, -0.176],
    [-0.156, -0.180], [-0.150, -0.092]
  ], 0.026, 0.005), 'steel', { wear: 0.55, noise: 0.16 });
  mpb.add(chamferBox(0.030, 0.011, 0.052, 0.004), 'polymer', { x: 0, y: -0.180, z: -0.132, rx: -0.12, wear: 0.6, noise: 0.2 });
  for (let i = 0; i < 4; i++) {
    mpb.add(chamferBox(0.028, 0.005, 0.028, 0.0012), 'dark',
      { x: 0, y: -0.052 - i * 0.032, z: -0.122 - i * 0.004, wear: 0.3, noise: 0 });
  }
  mpb.add(chamferBox(0.027, 0.010, 0.050, 0.003), 'blued', { x: 0, y: -0.024, z: -0.116, wear: 1.0 });
  mpb.add(tubeZ(0.0046, 0.0046, 0.032, 12), 'brass', { x: 0.003, y: -0.0165, z: -0.117, wear: 0.8 });
  mpb.add(latheZ([[0.0015, -0.0110], [0.0031, -0.0060], [0.0046, 0.0000]], 12), 'brass',
    { x: 0.003, y: -0.0165, z: -0.133, wear: 0.9 });
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  const hands = buildHandPair(mats);
  hands.right.position.set(-0.044, -0.056, -0.046);
  hands.right.rotation.set(-0.28, -0.12, 0.10);
  // Punho vertical dianteiro: aqui a mao FECHA de verdade (o punho tem 30 x 46 mm
  // e cabe no vao de 22 mm do punho cerrado), entao a mao esquerda continua com o
  // curl cheio. Posicao: o eixo de pega da mao (medido em 0.0084 do centro dela)
  // cai sobre o eixo do punho vertical, em (0, -0.065, -0.265).
  hands.left.position.set(0.008, -0.062, -0.266);
  hands.left.rotation.set(-0.06, 0.10, 0.06);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'smg',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.040, chargingTravel: 0.062, triggerTravel: 0.004,
    chargingWithBolt: true,
    muzzle: marker(0, 0, -0.480),
    ejectPort: marker(-0.030, 0.010, -0.150),
    sight: marker(0, SY, -0.046),
    sightY: SY,
    leftHandBase: new THREE.Vector3(0.008, -0.062, -0.266),
    magAxis: new THREE.Vector3(0.05, -0.985, -0.16).normalize(),
    hipPos: new THREE.Vector3(0.112, -0.118, -0.500),
    hipRot: new THREE.Euler(0.012, 0.008, 0.160, 'YXZ'),
    sprintPos: new THREE.Vector3(0.150, -0.180, -0.450),
    sprintRot: new THREE.Euler(-0.310, 0.585, 0.660, 'YXZ'),
    recoilScale: 0.68
  };
}
/**
 * FUZIL SEMIAUTOMATICO DE PRECISAO - cano pesado com estrias, coronha de furo
 * para o polegar, freio de boca retangular. Identidade: LUNETA e BIPE.
 *
 * A luneta e um tubo ABERTO com material FrontSide: da mira, o olho esta sobre o
 * eixo do tubo e todas as paredes ficam de costas (backface), entao a visao passa
 * limpa. Quem frame a imagem e o anel traseiro do ocular, que e uma face virada
 * para o olho - e exatamente o que um oculo real mostra.
 */
function buildDmr(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.078;      // eixo optico da luneta

  // --- coronha com furo para o polegar -------------------------------------
  const stock = polyShape([
    [0.008, 0.034], [0.075, 0.040], [0.150, 0.038], [0.232, 0.030],
    [0.240, 0.004], [0.240, -0.044], [0.176, -0.040], [0.150, -0.086],
    [0.108, -0.092], [0.086, -0.050], [0.030, -0.030], [0.008, -0.024]
  ]);
  const thumb = new THREE.Path();
  thumb.absarc(0.152, -0.014, 0.024, 0, Math.PI * 2, true);
  stock.holes.push(thumb);
  const sg = chamferShape(stock, 0.042, 0.008, 6);
  sg.rotateY(-Math.PI / 2);
  pb.add(sg, 'polymer', { wear: 0.35, noise: 0.16 });
  // apoio de face regulavel
  pb.add(chamferBox(0.040, 0.024, 0.120, 0.008), 'polymer', { x: 0, y: 0.048, z: 0.135, wear: 0.4, noise: 0.14 });
  for (const sx of [-1, 1]) {
    pb.add(tubeZ(0.0045, 0.0045, 0.030, 8), 'bright', { x: sx * 0.014, y: 0.036, z: 0.135, rx: Math.PI / 2, wear: 1.0 });
  }
  pb.add(chamferBox(0.046, 0.086, 0.018, 0.006), 'rubber', { x: 0, y: -0.006, z: 0.246, wear: 0.4, noise: 0.2 });
  // regulagem do comprimento
  pb.add(chamferBox(0.030, 0.020, 0.050, 0.005), 'steel', { x: 0, y: -0.030, z: 0.215, wear: 0.9 });

  // --- receptor -------------------------------------------------------------
  pb.add(chamferBox(0.052, 0.058, 0.262, 0.007), 'steel', { x: 0, y: 0.008, z: -0.128, wear: 0.85 });
  pb.add(chamferBox(0.056, 0.016, 0.090, 0.005), 'steel', { x: 0, y: -0.014, z: -0.150, wear: 0.9 });
  // trilho longo, base da luneta
  railTop(pb, 0, 0.040, -0.130, 0.250);
  // porta de ejecao ampla e defletor na face -X visivel
  pb.add(chamferBox(0.005, 0.034, 0.072, 0.001), 'dark', { x: -0.0245, y: 0.012, z: -0.150, wear: 0.3, noise: 0 });
  pb.add(chamferBox(0.016, 0.026, 0.030, 0.006), 'steel', { x: -0.028, y: 0.030, z: -0.106, wear: 0.9 });

  // --- punho e gatilho ------------------------------------------------------
  pb.add(sideProfile([
    [-0.040, -0.026], [-0.004, -0.030], [0.010, -0.078], [0.006, -0.112],
    [-0.020, -0.118], [-0.038, -0.092], [-0.048, -0.048]
  ], 0.034, 0.010), 'polymer', { wear: 0.3, noise: 0.16 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.032, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.056 - i * 0.021, z: -0.038 + i * 0.006, rx: 0.33, wear: 0.5, noise: 0.25 });
  }
  triggerGuard(pb, 0, -0.024, -0.058, mats);
  pb.add(tubeZ(0.008, 0.008, 0.048, 16), 'blued', { x: 0, y: -0.016, z: -0.034, ry: Math.PI / 2, wear: 1.0 });

  // --- guarda-mao tubular com rasgos e trilho inferior ---------------------
  pb.add(tubeZ(0.0325, 0.0335, 0.240, 20, true), 'steel', { x: 0, y: 0.004, z: -0.380, rz: Math.PI / 20, wear: 0.8, noise: 0.12 });
  frontRing(pb, 0.004, -0.500, 0.0325, 0.0335, 0.0125, 'steel');
  backRing(pb, 0.004, -0.260, 0.0335, 'steel');
  pb.add(latheZ([[0.034, -0.268], [0.037, -0.264], [0.037, -0.256], [0.034, -0.252]], 24), 'steel', { x: 0, y: 0.004, z: 0, wear: 0.95 });
  for (let i = 0; i < 5; i++) {
    const z = -0.290 - i * 0.040;
    pb.add(chamferBox(0.010, 0.012, 0.032, 0.002), 'dark', { x: 0.031, y: 0.006, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.010, 0.012, 0.032, 0.002), 'dark', { x: -0.031, y: 0.006, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.013, 0.010, 0.032, 0.002), 'dark', { x: 0, y: -0.027, z, wear: 0.4, noise: 0 });
  }

  // --- cano pesado com estrias e freio de boca -----------------------------
  pb.add(tubeZ(0.0135, 0.0155, 0.440, 16), 'blued', { x: 0, y: 0, z: -0.470, wear: 0.85 });
  for (let i = 0; i < 6; i++) {
    // Estrias: sulcos LONGITUDINAIS, rasos. Altura pequena e centro sobre a
    // superficie do cano, senao viram aletas para fora em vez de sulcos.
    pb.add(chamferBox(0.007, 0.008, 0.230, 0.0012), 'dark',
      { x: 0, y: 0.0126, z: -0.570, rz: i * (Math.PI / 3) + 0.26, wear: 0.5, noise: 0.1 });
  }
  pb.add(latheZ([[0.0155, -0.694], [0.0180, -0.690], [0.0180, -0.680], [0.0155, -0.676]], 20), 'blued', { wear: 1.0 });
  // freio de boca retangular com tres janelas de cada lado
  pb.add(chamferBox(0.034, 0.030, 0.062, 0.004), 'blued', { x: 0, y: 0, z: -0.726, wear: 1.0 });
  for (let i = 0; i < 3; i++) {
    const z = -0.706 - i * 0.019;
    pb.add(chamferBox(0.040, 0.016, 0.011, 0.002), 'dark', { x: 0, y: 0.006, z, wear: 0.4, noise: 0 });
  }
  pb.add(chamferBox(0.020, 0.014, 0.056, 0.002), 'dark', { x: 0, y: 0.017, z: -0.726, wear: 0.4, noise: 0 });
  muzzleBore(pb, 0, 0, -0.758, 0.0150, 0.0055);

  // --- bipe recolhido para a frente ----------------------------------------
  pb.add(chamferBox(0.030, 0.026, 0.044, 0.006), 'steel', { x: 0, y: -0.034, z: -0.452, wear: 0.95 });
  for (const sx of [-1, 1]) {
    pb.add(tubeZ(0.0055, 0.0060, 0.115, 8), 'steel',
      { x: sx * 0.013, y: -0.048, z: -0.512, rx: -0.20, ry: sx * 0.10, wear: 1.0 });
    pb.add(tubeZ(0.0075, 0.0075, 0.028, 8), 'rubber',
      { x: sx * 0.021, y: -0.070, z: -0.566, rx: -0.20, ry: sx * 0.10, wear: 0.4, noise: 0.2 });
    pb.add(tubeZ(0.0045, 0.0045, 0.026, 8), 'bright',
      { x: sx * 0.014, y: -0.034, z: -0.452, ry: Math.PI / 2, wear: 1.0 });
  }

  // --- luneta ---------------------------------------------------------------
  // aneis de montagem
  for (const z of [-0.072, -0.176]) {
    pb.add(chamferBox(0.030, 0.030, 0.026, 0.005), 'steel', { x: 0, y: SY - 0.022, z, wear: 0.95 });
    pb.add(latheZ([[0.0205, -0.013], [0.0225, -0.009], [0.0225, 0.009], [0.0205, 0.013]], 18), 'steel', { x: 0, y: SY, z, wear: 0.95 });
    for (const sx of [-1, 1]) {
      pb.add(tubeZ(0.0032, 0.0032, 0.008, 8), 'bright', { x: sx * 0.012, y: SY + 0.014, z, rx: Math.PI / 2, wear: 1.0 });
    }
  }
  // corpo do tubo (aberto nas duas pontas)
  pb.add(tubeZ(0.0170, 0.0170, 0.130, 24, true), 'blued', { x: 0, y: SY, z: -0.120, wear: 0.7, noise: 0.1 });
  // sino da objetiva e ocular, tambem abertos
  pb.add(latheZ([[0.0300, -0.240], [0.0300, -0.216], [0.0180, -0.196], [0.0170, -0.186]], 22), 'blued', { x: 0, y: SY, z: 0, wear: 0.8 });
  pb.add(latheZ([[0.0170, -0.055], [0.0195, -0.048], [0.0265, -0.034], [0.0265, -0.017]], 22), 'blued', { x: 0, y: SY, z: 0, wear: 0.8 });
  // torres de ajuste (elevacao e deriva) com knurl
  pb.add(latheZ([[0.0150, 0.000], [0.0150, 0.020], [0.0130, 0.024]], 20), 'steel', { x: 0, y: SY + 0.014, z: -0.128, rx: -Math.PI / 2, wear: 0.95 });
  // torre de deriva: ry NEGATIVO para o corpo dela crescer para FORA (-X). Com
  // +PI/2 ela avancava por dentro do tubo e tapava o eixo optico da luneta.
  pb.add(latheZ([[0.0130, 0.000], [0.0130, 0.017], [0.0112, 0.020]], 20), 'steel', { x: -0.016, y: SY, z: -0.128, ry: -Math.PI / 2, wear: 0.95 });
  for (let i = 0; i < 10; i++) {
    pb.add(chamferBox(0.0030, 0.016, 0.0030, 0.0008), 'steel',
      { x: Math.sin(i * 0.628) * 0.0148, y: SY + 0.024, z: -0.128 + Math.cos(i * 0.628) * 0.0148, wear: 1.0 });
  }
  // anel de ajuste de aumento, atras
  pb.add(latheZ([[0.0272, -0.050], [0.0272, -0.038]], 22), 'rubber', { x: 0, y: SY, z: 0, wear: 0.5, noise: 0.2 });
  // ANEL DO OCULAR virado para o olho: e ele que da o quadro da imagem na mira.
  // Raio DECRESCENTE de proposito - e o que faz a normal do anel apontar para +Z.
  pb.add(latheZ([[0.0330, -0.017], [0.0192, -0.017]], 26), 'dark', { x: 0, y: SY, z: 0, wear: 0.2, noise: 0.05 });
  // reticulo: cruz fina com ponto central, no eixo optico
  pb.add(chamferBox(0.0011, 0.0300, 0.0011, 0.0003), 'dark', { x: 0, y: SY, z: -0.150, wear: 0.2, noise: 0 });
  pb.add(chamferBox(0.0300, 0.0011, 0.0011, 0.0003), 'dark', { x: 0, y: SY, z: -0.150, wear: 0.2, noise: 0 });
  for (let i = 1; i <= 3; i++) {
    pb.add(chamferBox(0.0022, 0.0022, 0.0012, 0.0004), 'dark', { x: 0, y: SY - i * 0.0060, z: -0.150, wear: 0.2, noise: 0 });
  }

  pb.flush(root, mats, false);

  // lentes: quase transparentes, so para dar reflexo de vidro e um leve tom frio
  const lensBack = new THREE.Mesh(new THREE.CircleGeometry(0.0165, 24), mats.lens);
  lensBack.position.set(0, SY, -0.056);
  lensBack.renderOrder = 6;
  // a objetiva olha para a FRENTE: de fora ve-se o reflexo do vidro; de dentro
  // ela e backface e nao acrescenta um segundo tom sobre a imagem da mira
  const lensFront = new THREE.Mesh(new THREE.CircleGeometry(0.0290, 24), mats.lens);
  lensFront.position.set(0, SY, -0.234);
  lensFront.rotation.y = Math.PI;
  lensFront.renderOrder = 5;
  for (const l of [lensBack, lensFront]) { l.frustumCulled = false; root.add(l); }

  // --- pecas animadas ------------------------------------------------------
  const bolt = part(chamferBox(0.005, 0.019, 0.062, 0.0012), 'bright', mats,
    { x: -0.0265, y: 0.021, z: -0.150, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // alavanca de ferrolho na mesma face visivel da janela
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(tubeZ(0.0050, 0.0050, 0.040, 8), 'steel', { x: -0.044, y: 0.020, z: -0.120, ry: -Math.PI / 2, wear: 1.0 });
  const knob = new THREE.SphereGeometry(0.0105, 12, 8);
  cpb.add(knob, 'blued', { x: -0.064, y: 0.020, z: -0.120, wear: 0.9 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.028, -0.052);
  root.add(trigger);

  // carregador de 20 tiros, reto e longo
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  mpb.add(sideProfile([
    [-0.192, -0.022], [-0.126, -0.024], [-0.132, -0.100], [-0.140, -0.186],
    [-0.204, -0.190], [-0.200, -0.104]
  ], 0.030, 0.005), 'steel', { wear: 0.5, noise: 0.16 });
  mpb.add(chamferBox(0.034, 0.012, 0.070, 0.004), 'polymer', { x: 0, y: -0.190, z: -0.172, rx: -0.10, wear: 0.6, noise: 0.2 });
  for (let i = 0; i < 3; i++) {
    mpb.add(chamferBox(0.032, 0.006, 0.038, 0.0015), 'dark',
      { x: 0, y: -0.070 - i * 0.038, z: -0.166 - i * 0.004, wear: 0.3, noise: 0 });
  }
  mpb.add(chamferBox(0.031, 0.012, 0.066, 0.003), 'blued', { x: 0, y: -0.026, z: -0.160, wear: 1.0 });
  mpb.add(tubeZ(0.0060, 0.0060, 0.044, 12), 'brass', { x: 0.004, y: -0.0175, z: -0.161, wear: 0.8 });
  mpb.add(latheZ([[0.0018, -0.0150], [0.0040, -0.0080], [0.0060, 0.0000]], 12), 'brass',
    { x: 0.004, y: -0.0175, z: -0.183, wear: 0.9 });
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  const hands = buildHandPair(mats, 0.88);
  hands.right.position.set(-0.045, -0.052, -0.044);
  hands.right.rotation.set(-0.28, -0.12, 0.10);
  hands.left.position.set(-0.026, -0.067, -0.395);
  hands.left.rotation.set(1.82, 0.28, 3.142);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'dmr',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.060, chargingTravel: 0.078, triggerTravel: 0.004,
    chargingWithBolt: true,
    muzzle: marker(0, 0, -0.762),
    ejectPort: marker(-0.032, 0.018, -0.150),
    sight: marker(0, SY, -0.016),
    sightY: SY,
    adsEyeZ: -0.150,
    leftHandBase: new THREE.Vector3(-0.026, -0.067, -0.395),
    magAxis: new THREE.Vector3(0.05, -0.980, -0.19).normalize(),
    hipPos: new THREE.Vector3(0.125, -0.170, -0.710),
    hipRot: new THREE.Euler(0.008, 0.008, 0.165, 'YXZ'),
    sprintPos: new THREE.Vector3(0.165, -0.228, -0.651),
    sprintRot: new THREE.Euler(-0.350, 0.525, 0.620, 'YXZ'),
    recoilScale: 1.9
  };
}
/**
 * ESPINGARDA DE COMBATE - cano de 18.4 mm com CAPA DE VENTILACAO, costela com
 * esfera de latao, coronha esqueletada de dois tubos e pente-caixote GORDO.
 *
 * A silhueta e construida para nao poder ser confundida com fuzil, e o que decide
 * isso e a ESCALA das pecas, nao o detalhe: o furo do cano tem 18.4 mm contra
 * 7.6 mm do KR-7, a capa de ventilacao tem 55 mm de diametro externo (o
 * guarda-mao do fuzil tem 61 mm, mas e liso e fino) e o pente tem 44 mm de largura
 * contra 27 mm do fuzil. De relance, o contorno e curto, grosso e cheio de vazios.
 *
 * Eixo do cano em y=0; linha de mira em y=0.042 (baixa - a costela e rasante ao
 * cano, ao contrario do trilho alto do fuzil); frente para -Z.
 */
function buildShotgun(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.042;      // linha de mira: topo da esfera e centro do aro fantasma

  // --- coronha esqueletada -------------------------------------------------
  // Bloco de encaixe no receptor, e dele saem os tubos. Coronha VAZADA: entre os
  // tubos ve-se o cenario, e e esse vazio que separa a leitura dela da laje de
  // polimero do KR-7 e da coronha de furo do SR-12.
  pb.add(chamferBox(0.048, 0.046, 0.038, 0.008), 'steel', { x: 0, y: 0.000, z: 0.016, wear: 0.9 });
  for (const sx of [-1, 1]) {
    pb.add(tubeZ(0.0062, 0.0062, 0.174, 10), 'blued', { x: sx * 0.018, y: 0.018, z: 0.104, wear: 1.0 });
  }
  pb.add(tubeZ(0.0058, 0.0058, 0.156, 10), 'blued', { x: 0, y: -0.030, z: 0.096, wear: 1.0 });
  // travessa diagonal entre o tubo de cima e o de baixo
  pb.add(chamferBox(0.036, 0.010, 0.058, 0.003), 'blued', { x: 0, y: -0.006, z: 0.128, rx: 0.72, wear: 0.95 });
  // apoio de face sobre os tubos superiores
  pb.add(chamferBox(0.042, 0.014, 0.088, 0.004), 'polymer', { x: 0, y: 0.032, z: 0.140, wear: 0.4, noise: 0.16 });
  pb.add(chamferBox(0.0445, 0.0026, 0.084, 0.0006), 'dark', { x: 0, y: 0.0245, z: 0.140, wear: 0.1, noise: 0 });
  // placa de coice grossa, com fresta em volta (mesma razao da placa do KR-7:
  // duas faces planas encostadas que o AO de 3 cm nao consegue escurecer)
  pb.add(chamferBox(0.052, 0.088, 0.018, 0.006), 'rubber', { x: 0, y: -0.004, z: 0.196, wear: 0.4, noise: 0.22 });
  pb.add(chamferBox(0.056, 0.092, 0.0026, 0.0006), 'dark', { x: 0, y: -0.004, z: 0.1866, wear: 0.1, noise: 0 });
  // encaixe de bandoleira
  pb.add(ringZ(0.010, 0.0028, 12), 'steel', { x: -0.020, y: -0.030, z: 0.170, ry: Math.PI / 2, wear: 1.0 });

  // --- receptor ------------------------------------------------------------
  pb.add(chamferBox(0.052, 0.058, 0.198, 0.006), 'steel', { x: 0, y: 0.006, z: -0.100, wear: 0.85 });
  // tampa superior com nervura central
  pb.add(chamferBox(0.038, 0.012, 0.180, 0.004), 'steel', { x: 0, y: 0.032, z: -0.100, wear: 0.9 });
  pb.add(chamferBox(0.0025, 0.0028, 0.174, 0.0006), 'dark', { x: -0.0180, y: 0.0325, z: -0.100, wear: 0.1, noise: 0 });
  // fresta da junta receptor / carcaca do gatilho, na face -X que a camera ve
  pb.add(chamferBox(0.0020, 0.0028, 0.110, 0.0006), 'dark', { x: -0.0250, y: -0.0165, z: -0.088, wear: 0.1, noise: 0 });
  // porta de ejecao ampla (cartucho de calibre 12 exige janela grande) e defletor
  pb.add(chamferBox(0.005, 0.036, 0.070, 0.001), 'dark', { x: -0.0250, y: 0.008, z: -0.104, wear: 0.3, noise: 0 });
  pb.add(chamferBox(0.016, 0.026, 0.030, 0.006), 'steel', { x: -0.030, y: 0.030, z: -0.058, wear: 0.9 });
  // botao de retencao do pente e trava do ferrolho
  pb.add(tubeZ(0.0065, 0.0065, 0.013, 10), 'bright', { x: 0.024, y: -0.018, z: -0.062, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.007, 0.022, 0.028, 0.002), 'bright', { x: -0.025, y: -0.018, z: -0.058, wear: 1.0 });
  // seletor com marca pintada, no lado visivel
  pb.add(tubeZ(0.008, 0.008, 0.046, 16), 'blued', { x: 0, y: -0.012, z: -0.034, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.0018, 0.0026, 0.0105, 0.0005), 'paint', { x: -0.0212, y: -0.0005, z: -0.0445 });
  pb.add(chamferBox(0.0018, 0.0105, 0.0026, 0.0005), 'paint', { x: -0.0212, y: -0.0040, z: -0.0205 });

  // --- punho e gatilho -----------------------------------------------------
  pb.add(sideProfile([
    [-0.038, -0.022], [-0.002, -0.028], [0.012, -0.072], [0.008, -0.108],
    [-0.018, -0.114], [-0.036, -0.088], [-0.046, -0.044]
  ], 0.034, 0.009), 'polymer', { wear: 0.3, noise: 0.16 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.032, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.052 - i * 0.020, z: -0.036 + i * 0.006, rx: 0.34, wear: 0.5, noise: 0.25 });
  }
  pb.add(chamferBox(0.031, 0.010, 0.026, 0.004), 'rubber', { x: 0, y: -0.112, z: -0.006, wear: 0.4, noise: 0.2 });
  pb.add(chamferBox(0.036, 0.0024, 0.044, 0.0006), 'dark', { x: 0, y: -0.0235, z: -0.026, wear: 0.1, noise: 0 });
  triggerGuard(pb, 0, -0.020, -0.052, mats);

  // --- poco do pente -------------------------------------------------------
  pb.add(chamferBox(0.048, 0.054, 0.072, 0.006), 'steel', { x: 0, y: -0.040, z: -0.142, rx: 0.08, wear: 0.9 });
  pb.add(chamferBox(0.052, 0.014, 0.078, 0.005), 'steel', { x: 0, y: -0.014, z: -0.142, rx: 0.08, wear: 0.95 });

  // --- cano e capa de ventilacao -------------------------------------------
  // Cano de 40 mm externos. Comeca DENTRO do receptor (z -0.190) para nao sobrar
  // vao entre a face do receptor e o inicio do tubo.
  pb.add(tubeZ(0.0185, 0.0200, 0.400, 20), 'blued', { x: 0, y: 0, z: -0.390, wear: 0.85 });
  pb.add(latheZ([[0.0205, -0.222], [0.0240, -0.218], [0.0240, -0.206], [0.0205, -0.202]], 22), 'steel', { wear: 0.95 });
  // capa de ventilacao: tubo ABERTO em volta do cano, com janelas grandes
  pb.add(tubeZ(0.0265, 0.0275, 0.230, 20, true), 'steel',
    { x: 0, y: 0, z: -0.350, rz: Math.PI / 20, wear: 0.8, noise: 0.12 });
  frontRing(pb, 0, -0.465, 0.0265, 0.0275, 0.0170, 'steel');
  backRing(pb, 0, -0.235, 0.0275, 'steel');
  // janelas: 4 de cada lado e 4 embaixo. Sao RETANGULOS GRANDES (20 x 32 mm),
  // nao os rasgos de 11 mm do M-LOK do fuzil - o vazio faz parte da silhueta.
  for (let i = 0; i < 4; i++) {
    const z = -0.264 - i * 0.052;
    pb.add(chamferBox(0.009, 0.020, 0.032, 0.002), 'dark', { x: 0.0262, y: 0.004, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.009, 0.020, 0.032, 0.002), 'dark', { x: -0.0262, y: 0.004, z, wear: 0.4, noise: 0 });
    pb.add(chamferBox(0.020, 0.009, 0.032, 0.002), 'dark', { x: 0, y: -0.0262, z, wear: 0.4, noise: 0 });
  }
  // aneis de reforco da capa
  for (const z of [-0.256, -0.444]) {
    pb.add(latheZ([[0.0278, -0.006], [0.0296, -0.002], [0.0296, 0.002], [0.0278, 0.006]], 24),
      'steel', { x: 0, y: 0, z, wear: 0.95 });
  }
  // luva de borracha onde a mao esquerda apoia
  pb.add(latheZ([[0.0290, -0.392], [0.0290, -0.316]], 24), 'rubber', { wear: 0.5, noise: 0.24 });

  // --- estrangulador e boca ------------------------------------------------
  pb.add(latheZ([[0.0200, -0.590], [0.0228, -0.585], [0.0228, -0.570], [0.0200, -0.564]], 22), 'blued', { wear: 1.0 });
  muzzleBore(pb, 0, 0, -0.590, 0.0200, 0.0092);

  // --- miras ---------------------------------------------------------------
  // A costela fica em y=0.0295: a base dela (0.0265) entra 1 mm na parede da capa
  // (raio 0.0275). Em 0.0315 sobrava uma fresta de 1 mm entre as duas e a costela
  // aparecia FLUTUANDO sobre o cano.
  ventRib(pb, 0.0295, -0.360, 0.216, 6);
  beadFront(pb, SY, -0.452);
  ghostRing(pb, SY, -0.030);

  pb.flush(root, mats, false);

  // --- pecas animadas ------------------------------------------------------
  const bolt = part(chamferBox(0.005, 0.022, 0.058, 0.0012), 'bright', mats,
    { x: -0.0245, y: 0.010, z: -0.104, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // Alavanca de armar grande, presa ao ferrolho: e por ela que se recarrega a
  // camara depois do pente vazio.
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(chamferBox(0.030, 0.015, 0.022, 0.005), 'steel', { x: -0.038, y: 0.006, z: -0.070, wear: 1.0 });
  cpb.add(chamferBox(0.016, 0.012, 0.050, 0.003), 'steel', { x: -0.030, y: 0.006, z: -0.052, wear: 1.0 });
  cpb.add(ringZ(0.008, 0.0022, 10), 'bright', { x: -0.044, y: 0.006, z: -0.070, ry: Math.PI / 2, wear: 1.0 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.024, -0.046);
  root.add(trigger);

  // --- pente-caixote de 6 cartuchos ---------------------------------------
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  // 44 mm de largura contra 27 do pente do fuzil: cartucho de calibre 12 tem
  // 20 mm de diametro, entao o caixote e mais LARGO que fundo. Curto e gordo.
  mpb.add(sideProfile([
    [-0.176, -0.030], [-0.106, -0.030], [-0.112, -0.088], [-0.118, -0.146],
    [-0.184, -0.150], [-0.180, -0.090]
  ], 0.044, 0.006), 'polymer', { wear: 0.5, noise: 0.18 });
  mpb.add(chamferBox(0.047, 0.012, 0.076, 0.004), 'polymer', { x: 0, y: -0.150, z: -0.150, rx: -0.22, wear: 0.6, noise: 0.2 });
  for (let i = 0; i < 3; i++) {
    mpb.add(chamferBox(0.046, 0.006, 0.010, 0.0015), 'dark',
      { x: 0, y: -0.058 - i * 0.032, z: -0.108 - i * 0.005, wear: 0.3, noise: 0 });
  }
  // labio metalico e o cartucho de cima aparecendo: o unico latao da arma junto
  // com a esfera de mira
  mpb.add(chamferBox(0.045, 0.012, 0.066, 0.003), 'blued', { x: 0, y: -0.034, z: -0.144, wear: 1.0 });
  shotShell(mpb, 0.002, -0.024, -0.146, 'brass');
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  // --- maos ----------------------------------------------------------------
  const hands = buildHandPair(mats, 0.88);
  hands.right.position.set(-0.045, -0.048, -0.030);
  hands.right.rotation.set(-0.30, -0.12, 0.10);
  // A esquerda apoia POR BAIXO da capa de ventilacao (raio 27.5 mm) e os dedos
  // sobem por cima dela, igual ao guarda-mao do fuzil.
  hands.left.position.set(-0.024, -0.064, -0.352);
  hands.left.rotation.set(1.82, 0.28, 3.142);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'shotgun',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.052, chargingTravel: 0.058, triggerTravel: 0.0045,
    chargingWithBolt: true,
    muzzle: marker(0, 0, -0.596),
    ejectPort: marker(-0.030, 0.012, -0.104),
    sight: marker(0, SY, -0.030),
    sightY: SY,
    leftHandBase: new THREE.Vector3(-0.024, -0.064, -0.352),
    magAxis: new THREE.Vector3(0.05, -0.972, -0.23).normalize(),
    // Placa de coice a 36.0 cm do olho e boca a 1.161 m: razao 3.22:1, dentro do
    // limite de 3.25 que mantem a silhueta paralela em vez de virar cunha.
    hipPos: new THREE.Vector3(0.112, -0.145, -0.565),
    hipRot: new THREE.Euler(0.010, 0.008, 0.180, 'YXZ'),
    sprintPos: new THREE.Vector3(0.152, -0.203, -0.512),
    sprintRot: new THREE.Euler(-0.330, 0.545, 0.640, 'YXZ'),
    recoilScale: 2.1
  };
}

/**
 * PISTOLA - armacao de polimero, CORREDEIRA inteira animada, mira de tres pontos.
 *
 * A diferenca de construcao em relacao as outras seis: aqui a peca `bolt` NAO e um
 * bloquinho visto pela janela de ejecao, e a CORREDEIRA COMPLETA - metade da
 * silhueta da arma. Ela recua 30 mm a cada tiro, e e isso que faz a pistola
 * parecer pistola em movimento. Por isso `bolt` e um Group (o campo aceita
 * qualquer Object3D: o animador so escreve `bolt.position.z`).
 *
 * `charging` e null DE PROPOSITO: pistola nao tem alavanca de armar - a corredeira
 * E a alavanca. No engatilhamento da recarga com pente vazio o animador faz
 * `boltP = max(boltP, chargeP)`, entao a corredeira e puxada pela mao esquerda sem
 * precisar de peca separada (ver HAND_TARGETS.pistol.charge).
 *
 * Eixo do cano em y=0; linha de mira em y=0.030 (a mais baixa do arsenal: a mira
 * fica no topo da corredeira, a 3 cm do cano).
 */
function buildPistol(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.030;

  // --- armacao -------------------------------------------------------------
  // capa de po e trilho da corredeira
  pb.add(chamferBox(0.026, 0.019, 0.150, 0.004), 'polymer', { x: 0, y: -0.015, z: -0.078, wear: 0.28, noise: 0.16 });
  pb.add(chamferBox(0.0290, 0.0060, 0.146, 0.0012), 'steel', { x: 0, y: -0.0062, z: -0.078, wear: 0.9 });
  // trilho de acessorio embaixo da capa de po
  pb.add(chamferBox(0.020, 0.006, 0.062, 0.0014), 'polymer', { x: 0, y: -0.0255, z: -0.104, wear: 0.3, noise: 0.14 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.021, 0.0055, 0.0038, 0.0009), 'dark',
      { x: 0, y: -0.0255, z: -0.082 - i * 0.017, wear: 0.3, noise: 0 });
  }
  // punho com inclinacao: o pente entra por baixo dele
  pb.add(sideProfile([
    [-0.014, -0.020], [0.026, -0.012], [0.050, -0.070], [0.056, -0.116],
    [0.020, -0.128], [0.002, -0.122], [-0.006, -0.070], [-0.012, -0.030]
  ], 0.032, 0.008), 'polymer', { wear: 0.28, noise: 0.17 });
  // dorso e esporao traseiro (a peca que encosta na mao)
  pb.add(chamferBox(0.030, 0.022, 0.026, 0.005), 'polymer', { x: 0, y: -0.012, z: 0.030, wear: 0.3, noise: 0.16 });
  // boca alargada do poco do pente
  pb.add(chamferBox(0.036, 0.012, 0.040, 0.005), 'polymer', { x: 0, y: -0.126, z: 0.036, rx: -0.28, wear: 0.35, noise: 0.18 });
  // pega emborrachada: tres faixas na frente e um painel lateral
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.030, 0.006, 0.007, 0.0016), 'rubber',
      { x: 0, y: -0.044 - i * 0.024, z: -0.002 + i * 0.010, rx: 0.30, wear: 0.5, noise: 0.26 });
  }
  pb.add(chamferBox(0.0330, 0.056, 0.030, 0.004), 'rubber', { x: 0, y: -0.076, z: 0.030, rx: 0.14, wear: 0.45, noise: 0.28 });
  // guarda-mato e gatilho
  triggerGuard(pb, 0, -0.030, -0.028, mats);
  // retem da corredeira, pino de desmontagem e botao do pente (o metal claro que
  // quebra o cinza do polimero)
  pb.add(chamferBox(0.006, 0.010, 0.034, 0.0018), 'bright', { x: -0.017, y: -0.014, z: -0.006, wear: 1.0 });
  pb.add(tubeZ(0.0042, 0.0042, 0.030, 10), 'bright', { x: 0, y: -0.020, z: -0.040, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.007, 0.011, 0.011, 0.002), 'bright', { x: -0.018, y: -0.030, z: 0.006, wear: 1.0 });
  // fresta entre armacao e corredeira, dos dois lados: sem ela as duas pecas
  // encostam por faces planas e o AO assado nao consegue separa-las
  pb.add(chamferBox(0.0320, 0.0024, 0.144, 0.0006), 'dark', { x: 0, y: -0.0035, z: -0.078, wear: 0.1, noise: 0 });

  pb.flush(root, mats, false);

  // --- corredeira (peca animada, recua a cada tiro) ------------------------
  const slide = new THREE.Group();
  const spb = new PartBuilder();
  spb.add(chamferBox(0.029, 0.028, 0.180, 0.005), 'steel', { x: 0, y: 0.007, z: -0.068, wear: 0.9 });
  // chanfros dianteiros e nervura de topo
  spb.add(chamferBox(0.0230, 0.0070, 0.176, 0.0016), 'blued', { x: 0, y: 0.0195, z: -0.068, wear: 0.95 });
  // janela de ejecao na face -X, que e a que a camera enxerga
  spb.add(chamferBox(0.005, 0.020, 0.046, 0.001), 'dark', { x: -0.0135, y: 0.008, z: -0.028, wear: 0.3, noise: 0 });
  spb.add(chamferBox(0.006, 0.013, 0.024, 0.0016), 'blued', { x: -0.0148, y: 0.019, z: -0.010, wear: 1.0 });
  // SERRILHADO traseiro: 9 frisos. E o detalhe que le "pistola" de imediato -
  // nenhuma outra arma do arsenal tem ranhura vertical repetida no corpo.
  for (let i = 0; i < 9; i++) {
    spb.add(chamferBox(0.0310, 0.0225, 0.0026, 0.0006), 'dark',
      { x: 0, y: 0.007, z: 0.006 - i * 0.0064, wear: 0.4, noise: 0 });
  }
  // serrilhado dianteiro, mais curto
  for (let i = 0; i < 4; i++) {
    spb.add(chamferBox(0.0310, 0.0200, 0.0026, 0.0006), 'dark',
      { x: 0, y: 0.006, z: -0.118 - i * 0.0064, wear: 0.4, noise: 0 });
  }
  // boca: bucha do cano e o furo. O cano da pistola tem 8.8 mm externos - a peca
  // mais fina do arsenal, e por isso o freio de boca dela e so a coroa.
  spb.add(latheZ([[0.0100, -0.160], [0.0128, -0.156], [0.0128, -0.148], [0.0100, -0.144]], 18), 'steel', { wear: 1.0 });
  muzzleBore(spb, 0, 0, -0.160, 0.0092, 0.0044);
  // MIRA DE TRES PONTOS, montada na corredeira (o alinhamento na ADS continua
  // exato porque na mira a corredeira esta em repouso).
  spb.add(chamferBox(0.0052, 0.0100, 0.0062, 0.0010), 'blued', { x: 0, y: SY - 0.0050, z: -0.144, wear: 0.9 });
  spb.add(chamferBox(0.0020, 0.0034, 0.0024, 0.0005), 'paint', { x: 0, y: SY - 0.0042, z: -0.1474 });
  spb.add(chamferBox(0.0190, 0.0098, 0.0080, 0.0012), 'blued', { x: 0, y: SY - 0.0049, z: 0.0100, wear: 0.95 });
  spb.add(chamferBox(0.0044, 0.0115, 0.0092, 0.0008), 'dark', { x: 0, y: SY - 0.0018, z: 0.0100, wear: 0.2, noise: 0 });
  for (const sx of [-1, 1]) {
    spb.add(chamferBox(0.0020, 0.0034, 0.0024, 0.0005), 'paint', { x: sx * 0.0062, y: SY - 0.0050, z: 0.0062 });
  }
  spb.flush(slide, mats, false);
  root.add(slide);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.033, -0.022);
  root.add(trigger);

  // --- pente de 15, dentro do punho ---------------------------------------
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  mpb.add(sideProfile([
    [-0.006, -0.024], [0.030, -0.016], [0.046, -0.120], [0.010, -0.126]
  ], 0.019, 0.004), 'blued', { wear: 0.55, noise: 0.14 });
  mpb.add(chamferBox(0.024, 0.010, 0.044, 0.003), 'polymer', { x: 0, y: -0.128, z: 0.030, rx: -0.28, wear: 0.6, noise: 0.2 });
  // janelas de contagem na lateral do pente
  for (let i = 0; i < 4; i++) {
    mpb.add(chamferBox(0.021, 0.005, 0.009, 0.0012), 'dark',
      { x: 0, y: -0.040 - i * 0.022, z: 0.004 + i * 0.008, wear: 0.3, noise: 0 });
  }
  // cartucho na boca do pente
  riflePrimer(mpb, 0.002, -0.020, -0.006, 0.030, 0.0044);
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  // --- maos: pega de duas maos em copo ------------------------------------
  // A direita empunha; a esquerda NAO segura peca nenhuma da arma, ela envolve a
  // mao direita por baixo e por fora (pega em copo). Por isso o curl fica em 0.80:
  // os dedos dela fecham sobre os dedos da outra mao, nao sobre um punho de 22 mm.
  const hands = buildHandPair(mats, 0.80);
  hands.right.position.set(-0.043, -0.046, 0.014);
  hands.right.rotation.set(-0.26, -0.12, 0.10);
  hands.left.position.set(-0.036, -0.084, 0.030);
  hands.left.rotation.set(-0.02, 0.16, 0.22);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'pistol',
    root, bolt: slide, charging: null, trigger, mag, magDrop, hands,
    boltTravel: 0.030, chargingTravel: 0, triggerTravel: 0.004,
    muzzle: marker(0, 0, -0.166),
    ejectPort: marker(-0.020, 0.008, -0.028),
    sight: marker(0, SY, 0.010),
    sightY: SY,
    leftHandBase: new THREE.Vector3(-0.036, -0.084, 0.030),
    magAxis: new THREE.Vector3(0.02, -0.982, 0.19).normalize(),
    // Arma curta: quem manda no afastamento e a peca MAIS PROXIMA, nao a razao.
    // Base do punho a 31.4 cm do olho (o piso do arsenal) e boca a 53.6 cm:
    // razao 1.71:1, a silhueta mais "reta" das sete.
    hipPos: new THREE.Vector3(0.098, -0.120, -0.370),
    hipRot: new THREE.Euler(0.010, 0.010, 0.155, 'YXZ'),
    sprintPos: new THREE.Vector3(0.132, -0.176, -0.336),
    sprintRot: new THREE.Euler(-0.300, 0.560, 0.615, 'YXZ'),
    recoilScale: 0.80
  };
}

/**
 * METRALHADORA LEVE - TAMBOR de 80 tiros, capa de cano com furos redondos, alca de
 * transporte deslocada para a esquerda e gancho de ombro na coronha.
 *
 * O tambor e a assinatura: e a unica peca DISCOIDAL das sete armas, tem 116 mm de
 * diametro e fica pendurada sob o receptor, entrando no quadro por baixo. Ele e a
 * peca que CAI na recarga, e a queda de um disco desse tamanho e o que vende a
 * recarga de 6 segundos.
 *
 * A alca de transporte fica em x=-0.026, nao em x=0: no centro ela cruzaria o eixo
 * optico e taparia a mira de ferro na ADS. Metralhadora real resolve igual - alca
 * lateral, ou mira deslocada.
 *
 * Eixo do cano em y=0; linha de mira em y=0.068 (a mais alta depois da luneta do
 * SR-12, porque a tampa do alimentador fica no caminho); frente para -Z.
 */
function buildLmg(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.068;

  // --- coronha pesada com gancho de ombro ----------------------------------
  pb.add(sideProfile([
    [0.008, 0.034], [0.078, 0.038], [0.156, 0.036], [0.226, 0.028],
    [0.238, 0.002], [0.238, -0.048], [0.170, -0.042], [0.104, -0.010],
    [0.032, -0.006], [0.008, -0.016]
  ], 0.042, 0.008), 'polymer', { wear: 0.35, noise: 0.15 });
  pb.add(chamferBox(0.040, 0.016, 0.120, 0.005), 'polymer', { x: 0, y: 0.044, z: 0.120, wear: 0.4, noise: 0.14 });
  pb.add(chamferBox(0.0425, 0.0026, 0.116, 0.0006), 'dark', { x: 0, y: 0.0353, z: 0.120, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.050, 0.094, 0.018, 0.006), 'rubber', { x: 0, y: -0.008, z: 0.230, wear: 0.4, noise: 0.22 });
  pb.add(chamferBox(0.054, 0.098, 0.0026, 0.0006), 'dark', { x: 0, y: -0.008, z: 0.2206, wear: 0.1, noise: 0 });
  // GANCHO DE OMBRO: a peca que impede a coronha de ler como fuzil grande
  pb.add(chamferBox(0.046, 0.032, 0.042, 0.006), 'polymer', { x: 0, y: -0.062, z: 0.206, rx: 0.35, wear: 0.4, noise: 0.16 });
  pb.add(chamferBox(0.026, 0.014, 0.034, 0.004), 'steel', { x: 0, y: -0.040, z: 0.170, wear: 0.9 });
  pb.add(ringZ(0.011, 0.0030, 12), 'steel', { x: -0.022, y: -0.028, z: 0.196, ry: Math.PI / 2, wear: 1.0 });

  // --- receptor ------------------------------------------------------------
  pb.add(chamferBox(0.058, 0.064, 0.268, 0.007), 'steel', { x: 0, y: 0.010, z: -0.130, wear: 0.85 });
  pb.add(chamferBox(0.062, 0.020, 0.060, 0.005), 'steel', { x: 0, y: 0.006, z: 0.006, wear: 0.9 });
  pb.add(chamferBox(0.0020, 0.0028, 0.150, 0.0006), 'dark', { x: -0.0290, y: -0.0155, z: -0.120, wear: 0.1, noise: 0 });
  // tampa do alimentador (a que abre para receber a fita) com trava traseira
  pb.add(chamferBox(0.054, 0.020, 0.150, 0.005), 'steel', { x: 0, y: 0.042, z: -0.150, wear: 0.9 });
  pb.add(chamferBox(0.0565, 0.0026, 0.146, 0.0006), 'dark', { x: 0, y: 0.0315, z: -0.150, wear: 0.1, noise: 0 });
  pb.add(chamferBox(0.030, 0.014, 0.020, 0.004), 'bright', { x: 0, y: 0.048, z: -0.070, wear: 1.0 });
  // ALCA DE TRANSPORTE, deslocada para -X para nao cruzar o eixo da mira
  pb.add(chamferBox(0.020, 0.011, 0.112, 0.004), 'steel', { x: -0.026, y: 0.086, z: -0.150, wear: 0.95 });
  pb.add(chamferBox(0.018, 0.036, 0.014, 0.004), 'steel', { x: -0.026, y: 0.068, z: -0.100, rx: -0.34, wear: 0.95 });
  pb.add(chamferBox(0.018, 0.036, 0.014, 0.004), 'steel', { x: -0.026, y: 0.068, z: -0.200, rx: 0.34, wear: 0.95 });
  pb.add(latheZ([[0.0102, -0.190], [0.0102, -0.110]], 14), 'rubber', { x: -0.026, y: 0.086, z: 0, wear: 0.5, noise: 0.24 });
  // porta de ejecao grande e defletor, na face -X
  pb.add(chamferBox(0.005, 0.036, 0.078, 0.001), 'dark', { x: -0.0275, y: 0.014, z: -0.150, wear: 0.3, noise: 0 });
  pb.add(chamferBox(0.018, 0.028, 0.032, 0.006), 'steel', { x: -0.032, y: 0.034, z: -0.100, wear: 0.9 });

  // --- punho, gatilho e seletor -------------------------------------------
  pb.add(sideProfile([
    [-0.042, -0.024], [-0.006, -0.030], [0.010, -0.076], [0.006, -0.112],
    [-0.020, -0.118], [-0.038, -0.092], [-0.050, -0.048]
  ], 0.036, 0.010), 'polymer', { wear: 0.3, noise: 0.16 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.034, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.054 - i * 0.021, z: -0.040 + i * 0.006, rx: 0.34, wear: 0.5, noise: 0.25 });
  }
  pb.add(chamferBox(0.032, 0.010, 0.028, 0.004), 'rubber', { x: 0, y: -0.116, z: -0.008, wear: 0.4, noise: 0.2 });
  pb.add(chamferBox(0.038, 0.0024, 0.046, 0.0006), 'dark', { x: 0, y: -0.0255, z: -0.032, wear: 0.1, noise: 0 });
  triggerGuard(pb, 0, -0.022, -0.054, mats);
  pb.add(tubeZ(0.008, 0.008, 0.052, 16), 'blued', { x: 0, y: -0.014, z: -0.032, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.0018, 0.0026, 0.0105, 0.0005), 'paint', { x: -0.0245, y: -0.0025, z: -0.0425 });
  pb.add(chamferBox(0.0018, 0.0105, 0.0026, 0.0005), 'paint', { x: -0.0245, y: -0.0060, z: -0.0185 });

  // --- poco do tambor -----------------------------------------------------
  pb.add(chamferBox(0.052, 0.030, 0.070, 0.006), 'steel', { x: 0, y: -0.032, z: -0.150, wear: 0.9 });

  // --- miras ---------------------------------------------------------------
  // TORRE da massa de mira. A capa do cano termina em y=0.028 e a base da
  // `ironSights` comeca em y=0.035 (sightY - 0.023): sem esta peca a massa de mira
  // ficaria flutuando 7 mm acima do cano.
  pb.add(chamferBox(0.024, 0.026, 0.028, 0.004), 'steel', { x: 0, y: 0.030, z: -0.402, wear: 0.95 });
  ironSights(pb, SY, -0.402, -0.070);

  // --- cano e capa com furos redondos -------------------------------------
  pb.add(tubeZ(0.0125, 0.0150, 0.400, 18), 'blued', { x: 0, y: 0, z: -0.452, wear: 0.85 });
  pb.add(tubeZ(0.0270, 0.0280, 0.210, 20, true), 'steel',
    { x: 0, y: 0, z: -0.372, rz: Math.PI / 20, wear: 0.8, noise: 0.12 });
  frontRing(pb, 0, -0.477, 0.0270, 0.0280, 0.0115, 'steel');
  backRing(pb, 0, -0.267, 0.0280, 'steel');
  // Furos REDONDOS, nao rasgos retangulares: e a diferenca de leitura entre a capa
  // da metralhadora, o M-LOK do fuzil e as janelas quadradas da espingarda.
  for (let i = 0; i < 5; i++) {
    const z = -0.294 - i * 0.040;
    pb.add(tubeZ(0.0088, 0.0088, 0.011, 12), 'dark', { x: 0.0272, y: 0.002, z, ry: Math.PI / 2, wear: 0.3, noise: 0 });
    pb.add(tubeZ(0.0088, 0.0088, 0.011, 12), 'dark', { x: -0.0272, y: 0.002, z, ry: Math.PI / 2, wear: 0.3, noise: 0 });
    pb.add(tubeZ(0.0080, 0.0080, 0.011, 12), 'dark', { x: 0, y: -0.0272, z, rx: Math.PI / 2, wear: 0.3, noise: 0 });
  }
  for (const z of [-0.288, -0.462]) {
    pb.add(latheZ([[0.0283, -0.006], [0.0302, -0.002], [0.0302, 0.002], [0.0283, 0.006]], 24),
      'steel', { x: 0, y: 0, z, wear: 0.95 });
  }
  // luva de borracha: e por aqui que a mao esquerda segura
  pb.add(latheZ([[0.0292, -0.400], [0.0292, -0.330]], 24), 'rubber', { wear: 0.5, noise: 0.24 });
  // bloco de gas com regulador (peca gorda, tipica de arma de fita)
  pb.add(chamferBox(0.030, 0.036, 0.034, 0.005), 'blued', { x: 0, y: 0.010, z: -0.492, wear: 1.0 });
  pb.add(tubeZ(0.0060, 0.0060, 0.024, 12), 'bright', { x: 0, y: 0.010, z: -0.512, ry: Math.PI / 2, wear: 1.0 });
  pb.add(tubeZ(0.0050, 0.0050, 0.150, 10), 'bright', { x: 0, y: 0.026, z: -0.420, wear: 1.0 });

  // --- apagador conico e boca ----------------------------------------------
  pb.add(latheZ([[0.0152, -0.658], [0.0178, -0.653], [0.0178, -0.641], [0.0152, -0.636]], 20), 'blued', { wear: 1.0 });
  pb.add(latheZ([[0.0132, -0.696], [0.0230, -0.690], [0.0230, -0.676], [0.0155, -0.662]], 22), 'blued', { wear: 1.0 });
  muzzleBore(pb, 0, 0, -0.696, 0.0230, 0.0062);

  pb.flush(root, mats, false);

  // --- pecas animadas ------------------------------------------------------
  const bolt = part(chamferBox(0.005, 0.024, 0.074, 0.0012), 'bright', mats,
    { x: -0.0285, y: 0.014, z: -0.150, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // alavanca reciprocante: anda 8 cm a cada tiro, o maior percurso do arsenal
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(chamferBox(0.028, 0.016, 0.024, 0.005), 'steel', { x: -0.042, y: 0.010, z: -0.216, wear: 1.0 });
  cpb.add(chamferBox(0.016, 0.013, 0.058, 0.003), 'steel', { x: -0.034, y: 0.010, z: -0.194, wear: 1.0 });
  cpb.add(ringZ(0.009, 0.0024, 10), 'bright', { x: -0.050, y: 0.010, z: -0.216, ry: Math.PI / 2, wear: 1.0 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.026, -0.048);
  root.add(trigger);

  // --- TAMBOR de 80 tiros --------------------------------------------------
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  // pescoco que liga o tambor ao poco
  mpb.add(chamferBox(0.046, 0.050, 0.056, 0.006), 'steel', { x: 0, y: -0.046, z: -0.150, wear: 0.85 });
  // o disco: 116 mm de diametro, 52 mm de largura, com aro arredondado
  mpb.add(latheZ([
    [0.000, -0.026], [0.040, -0.026], [0.054, -0.020], [0.058, -0.006],
    [0.058, 0.006], [0.054, 0.020], [0.040, 0.026], [0.000, 0.026]
  ], 26), 'steel', { x: 0, y: -0.100, z: -0.150, wear: 0.7, noise: 0.14 });
  // tres aros de reforco no perimetro: sao eles que a camera ve de lado e de cima
  for (const dz of [-0.014, 0.000, 0.014]) {
    mpb.add(latheZ([[0.0586, -0.0026], [0.0602, -0.0012], [0.0602, 0.0012], [0.0586, 0.0026]], 26),
      'blued', { x: 0, y: -0.100, z: -0.150 + dz, wear: 0.95 });
  }
  // manivela da mola e trava da tampa, na face -Z do disco
  mpb.add(latheZ([[0.0000, -0.0300], [0.0140, -0.0300], [0.0140, -0.0220]], 16), 'bright',
    { x: 0, y: -0.100, z: -0.150, wear: 1.0 });
  mpb.add(chamferBox(0.026, 0.008, 0.004, 0.0012), 'bright', { x: 0, y: -0.100, z: -0.1795, rz: 0.6, wear: 1.0 });
  // FITA: tres cartuchos saindo do tambor para o alimentador. Junto com o aro de
  // latao, e o unico ponto quente da paleta desta arma.
  for (let i = 0; i < 3; i++) {
    riflePrimer(mpb, 0.005, -0.021 - i * 0.0016, -0.128 - i * 0.012, 0.040, 0.0052);
  }
  mpb.add(chamferBox(0.030, 0.008, 0.048, 0.0022), 'blued', { x: 0.005, y: -0.030, z: -0.140, wear: 0.9 });
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  // --- maos ----------------------------------------------------------------
  const hands = buildHandPair(mats, 0.88);
  hands.right.position.set(-0.047, -0.050, -0.036);
  hands.right.rotation.set(-0.28, -0.12, 0.10);
  hands.left.position.set(-0.024, -0.066, -0.372);
  hands.left.rotation.set(1.82, 0.28, 3.142);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'lmg',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.072, chargingTravel: 0.080, triggerTravel: 0.004,
    chargingWithBolt: true,
    muzzle: marker(0, 0, -0.702),
    ejectPort: marker(-0.034, 0.014, -0.150),
    sight: marker(0, SY, -0.070),
    sightY: SY,
    leftHandBase: new THREE.Vector3(-0.024, -0.066, -0.372),
    magAxis: new THREE.Vector3(0.04, -0.984, -0.175).normalize(),
    // Arma longa: placa de coice a 43.3 cm e boca a 1.374 m, razao 3.17:1.
    hipPos: new THREE.Vector3(0.130, -0.168, -0.672),
    hipRot: new THREE.Euler(0.008, 0.008, 0.172, 'YXZ'),
    sprintPos: new THREE.Vector3(0.172, -0.228, -0.615),
    sprintRot: new THREE.Euler(-0.345, 0.530, 0.628, 'YXZ'),
    recoilScale: 1.40
  };
}

/**
 * FUZIL DE BATALHA SEMIAUTOMATICO - coronha em TRELICA vazada, pente reto de aco,
 * guarda-mao com rasgos longitudinais, punho dianteiro INCLINADO e apagador conico.
 *
 * O risco desta arma era sair um KR-7 repintado: as duas sao fuzil, com trilho,
 * mira de ferro e pente sob o receptor. As quatro trocas de silhueta que resolvem:
 *  1) coronha em barras chatas com dois vazios, em vez da laje macica do KR-7;
 *  2) pente RETO e de aco, em vez do pente-banana de polimero;
 *  3) rasgos LONGITUDINAIS de 44 mm no guarda-mao, em vez dos M-LOK de 11 mm;
 *  4) punho dianteiro inclinado 28 graus preso embaixo, que o KR-7 nao tem - e e
 *     ele que muda a pose da mao esquerda de "apoia por baixo" para "empunha".
 * O apagador e um CONE longo, nao os tres dentes do KR-7.
 *
 * Eixo do cano em y=0; linha de mira em y=0.058; frente para -Z.
 */
function buildBattleRifle(mats) {
  const root = new THREE.Group();
  const pb = new PartBuilder();
  const SY = 0.058;
  const RY = 0.040;      // altura do trilho

  // --- coronha em trelica --------------------------------------------------
  // barra superior (espinha)
  pb.add(sideProfile([
    [0.006, 0.034], [0.216, 0.026], [0.216, 0.008], [0.006, 0.014]
  ], 0.036, 0.006), 'polymer', { wear: 0.35, noise: 0.15 });
  // barra inferior, descendo para o coice
  pb.add(sideProfile([
    [0.006, -0.006], [0.068, -0.008], [0.216, -0.036], [0.216, -0.052],
    [0.060, -0.024], [0.006, -0.020]
  ], 0.034, 0.006), 'polymer', { wear: 0.35, noise: 0.15 });
  // travessa diagonal: fecha a trelica e deixa DOIS vazios, um de cada lado dela
  pb.add(sideProfile([
    [0.148, 0.014], [0.174, 0.012], [0.190, -0.040], [0.164, -0.038]
  ], 0.030, 0.005), 'polymer', { wear: 0.35, noise: 0.16 });
  // apoio de face sobre a espinha
  pb.add(chamferBox(0.038, 0.014, 0.100, 0.005), 'polymer', { x: 0, y: 0.040, z: 0.140, wear: 0.4, noise: 0.14 });
  pb.add(chamferBox(0.0405, 0.0026, 0.096, 0.0006), 'dark', { x: 0, y: 0.0325, z: 0.140, wear: 0.1, noise: 0 });
  // placa de coice com fresta em volta
  pb.add(chamferBox(0.048, 0.086, 0.016, 0.006), 'rubber', { x: 0, y: -0.010, z: 0.220, wear: 0.4, noise: 0.22 });
  pb.add(chamferBox(0.052, 0.090, 0.0026, 0.0006), 'dark', { x: 0, y: -0.010, z: 0.2112, wear: 0.1, noise: 0 });
  pb.add(ringZ(0.010, 0.0028, 12), 'steel', { x: -0.019, y: -0.040, z: 0.196, ry: Math.PI / 2, wear: 1.0 });

  // --- receptor ------------------------------------------------------------
  pb.add(chamferBox(0.049, 0.052, 0.252, 0.006), 'steel', { x: 0, y: 0.010, z: -0.122, wear: 0.85 });
  pb.add(chamferBox(0.052, 0.020, 0.058, 0.005), 'steel', { x: 0, y: 0.006, z: 0.000, wear: 0.9 });
  pb.add(chamferBox(0.0020, 0.0028, 0.136, 0.0006), 'dark', { x: -0.0245, y: -0.0100, z: -0.100, wear: 0.1, noise: 0 });
  railTop(pb, 0, RY, -0.126, 0.240);
  pb.add(chamferBox(0.022, 0.0020, 0.0020, 0.0005), 'dark', { x: 0, y: RY + 0.004, z: -0.140, wear: 0.1, noise: 0 });
  // porta de ejecao e defletor na face -X
  pb.add(chamferBox(0.005, 0.030, 0.064, 0.001), 'dark', { x: -0.0235, y: 0.014, z: -0.140, wear: 0.3, noise: 0 });
  pb.add(chamferBox(0.014, 0.024, 0.026, 0.006), 'steel', { x: -0.028, y: 0.032, z: -0.100, wear: 0.9 });
  // retem do pente e trava do ferrolho
  pb.add(tubeZ(0.0062, 0.0062, 0.013, 10), 'bright', { x: 0.022, y: -0.014, z: -0.058, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.006, 0.020, 0.026, 0.002), 'bright', { x: -0.023, y: -0.014, z: -0.054, wear: 1.0 });
  // seletor de dois estagios (seguro / tiro) com marcas no lado visivel
  pb.add(tubeZ(0.008, 0.008, 0.048, 16), 'blued', { x: 0, y: -0.008, z: -0.030, ry: Math.PI / 2, wear: 1.0 });
  pb.add(chamferBox(0.008, 0.010, 0.030, 0.002), 'blued', { x: -0.026, y: -0.012, z: -0.024, rx: 0.5, wear: 1.0 });
  pb.add(chamferBox(0.0018, 0.0026, 0.0105, 0.0005), 'paint', { x: -0.0212, y: 0.0035, z: -0.0405 });
  pb.add(chamferBox(0.0018, 0.0105, 0.0026, 0.0005), 'paint', { x: -0.0212, y: 0.0000, z: -0.0165 });

  // --- punho e gatilho -----------------------------------------------------
  pb.add(sideProfile([
    [-0.044, -0.020], [-0.008, -0.026], [0.008, -0.070], [0.004, -0.106],
    [-0.022, -0.112], [-0.040, -0.086], [-0.052, -0.042]
  ], 0.034, 0.009), 'polymer', { wear: 0.3, noise: 0.16 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.032, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.050 - i * 0.020, z: -0.042 + i * 0.006, rx: 0.35, wear: 0.5, noise: 0.25 });
  }
  pb.add(chamferBox(0.031, 0.010, 0.026, 0.004), 'rubber', { x: 0, y: -0.110, z: -0.008, wear: 0.4, noise: 0.2 });
  pb.add(chamferBox(0.036, 0.0024, 0.044, 0.0006), 'dark', { x: 0, y: -0.0225, z: -0.030, wear: 0.1, noise: 0 });
  triggerGuard(pb, 0, -0.017, -0.052, mats);

  // --- poco do pente -------------------------------------------------------
  pb.add(chamferBox(0.038, 0.062, 0.070, 0.007), 'steel', { x: 0, y: -0.038, z: -0.092, rx: 0.05, wear: 0.9 });
  pb.add(chamferBox(0.042, 0.014, 0.076, 0.005), 'steel', { x: 0, y: -0.010, z: -0.092, rx: 0.05, wear: 0.95 });

  // --- guarda-mao com rasgos longitudinais ---------------------------------
  pb.add(tubeZ(0.0270, 0.0280, 0.170, 20, true), 'steel',
    { x: 0, y: 0.004, z: -0.336, rz: Math.PI / 20, wear: 0.8, noise: 0.12 });
  frontRing(pb, 0.004, -0.421, 0.0270, 0.0280, 0.0100, 'steel');
  backRing(pb, 0.004, -0.251, 0.0280, 'steel');
  pb.add(latheZ([[0.0284, -0.260], [0.0304, -0.256], [0.0304, -0.248], [0.0284, -0.244]], 24),
    'steel', { x: 0, y: 0.004, z: 0, wear: 0.95 });
  // Rasgos LONGITUDINAIS de 44 mm, tres fileiras de cada lado. O KR-7 tem rasgos
  // curtos e transversais; a diferenca de direcao e o que o olho pega primeiro.
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      pb.add(chamferBox(0.008, 0.009, 0.044, 0.0018), 'dark',
        { x: sx * 0.0272, y: 0.004 + (i - 1) * 0.014, z: -0.336, wear: 0.4, noise: 0 });
    }
  }
  pb.add(chamferBox(0.013, 0.008, 0.044, 0.0018), 'dark', { x: 0, y: -0.0232, z: -0.336, wear: 0.4, noise: 0 });
  // trilho curto embaixo, onde o punho dianteiro prende
  pb.add(chamferBox(0.020, 0.006, 0.070, 0.0014), 'steel', { x: 0, y: -0.0292, z: -0.330, wear: 0.9 });

  // --- punho dianteiro INCLINADO ------------------------------------------
  // Inclinado 28 graus para a frente: e ele que muda a pose da mao esquerda em
  // relacao a todas as outras armas longas do arsenal.
  pb.add(sideProfile([
    [-0.352, -0.026], [-0.316, -0.026], [-0.290, -0.076], [-0.316, -0.092], [-0.348, -0.064]
  ], 0.030, 0.008), 'polymer', { wear: 0.3, noise: 0.18 });
  pb.add(chamferBox(0.032, 0.010, 0.030, 0.004), 'rubber', { x: 0, y: -0.086, z: -0.304, rx: -0.48, wear: 0.4, noise: 0.22 });
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.031, 0.005, 0.006, 0.0015), 'rubber',
      { x: 0, y: -0.040 - i * 0.017, z: -0.336 + i * 0.009, rx: -0.48, wear: 0.5, noise: 0.25 });
  }

  // --- cano, bloco de gas e apagador conico -------------------------------
  pb.add(tubeZ(0.0105, 0.0125, 0.365, 18), 'blued', { x: 0, y: 0, z: -0.428, wear: 0.85 });
  pb.add(chamferBox(0.026, 0.032, 0.030, 0.004), 'blued', { x: 0, y: 0.008, z: -0.436, wear: 1.0 });
  pb.add(tubeZ(0.0036, 0.0036, 0.135, 8), 'bright', { x: 0, y: 0.022, z: -0.372, wear: 1.0 });
  pb.add(latheZ([[0.0132, -0.612], [0.0154, -0.608], [0.0154, -0.598], [0.0132, -0.594]], 20), 'blued', { wear: 1.0 });
  pb.add(latheZ([[0.0118, -0.648], [0.0205, -0.641], [0.0205, -0.629], [0.0132, -0.616]], 22), 'blued', { wear: 1.0 });
  // tres fendas no cone, para ele nao ler como funil liso
  for (let i = 0; i < 3; i++) {
    pb.add(chamferBox(0.008, 0.022, 0.016, 0.0018), 'dark',
      { x: 0, y: 0.014, z: -0.636, rz: i * (Math.PI * 2 / 3) + 0.4, wear: 0.4, noise: 0 });
  }
  muzzleBore(pb, 0, 0, -0.648, 0.0205, 0.0042);

  // --- miras ---------------------------------------------------------------
  ironSights(pb, SY, -0.398, -0.048);

  pb.flush(root, mats, false);

  // --- pecas animadas ------------------------------------------------------
  const bolt = part(chamferBox(0.005, 0.019, 0.058, 0.0012), 'bright', mats,
    { x: -0.0245, y: 0.018, z: -0.140, wear: 1.0, noise: 0.05 });
  root.add(bolt);

  // alavanca lateral RECIPROCANTE (anda a cada tiro): outra diferenca visivel em
  // relacao ao KR-7, cuja alavanca em T fica parada no alto
  const charging = new THREE.Group();
  const cpb = new PartBuilder();
  cpb.add(tubeZ(0.0052, 0.0052, 0.040, 8), 'steel', { x: -0.042, y: 0.024, z: -0.092, ry: -Math.PI / 2, wear: 1.0 });
  cpb.add(chamferBox(0.014, 0.016, 0.030, 0.004), 'steel', { x: -0.060, y: 0.024, z: -0.092, wear: 1.0 });
  cpb.add(ringZ(0.008, 0.0024, 10), 'bright', { x: -0.066, y: 0.024, z: -0.092, ry: Math.PI / 2, wear: 1.0 });
  cpb.flush(charging, mats, false);
  root.add(charging);

  const trigger = triggerMesh(mats);
  trigger.position.set(0, -0.020, -0.046);
  root.add(trigger);

  // --- pente RETO de aco, 20 tiros ----------------------------------------
  const mag = new THREE.Group();
  const mpb = new PartBuilder();
  // Reto: o z quase nao muda com a altura (o pente do KR-7 avanca 3 cm da boca a
  // base). Aco, nao polimero - e um material diferente do resto da arma.
  mpb.add(sideProfile([
    [-0.118, -0.014], [-0.056, -0.016], [-0.060, -0.090], [-0.064, -0.166],
    [-0.126, -0.170], [-0.122, -0.092]
  ], 0.028, 0.005), 'steel', { wear: 0.5, noise: 0.16 });
  mpb.add(chamferBox(0.032, 0.012, 0.070, 0.004), 'polymer', { x: 0, y: -0.170, z: -0.095, rx: -0.06, wear: 0.6, noise: 0.2 });
  // nervuras de reforco horizontais (o pente de aco estampado tem, o de polimero nao)
  for (let i = 0; i < 4; i++) {
    mpb.add(chamferBox(0.0295, 0.0050, 0.058, 0.0012), 'dark',
      { x: 0, y: -0.048 - i * 0.030, z: -0.092 - i * 0.002, wear: 0.3, noise: 0 });
  }
  mpb.add(chamferBox(0.029, 0.012, 0.060, 0.003), 'blued', { x: 0, y: -0.020, z: -0.088, wear: 1.0 });
  riflePrimer(mpb, 0.003, -0.0115, -0.090, 0.042, 0.0054);
  mpb.flush(mag, mats, false);
  root.add(mag);

  const magDrop = mag.clone(true);
  magDrop.visible = false;
  root.add(magDrop);

  // --- maos ----------------------------------------------------------------
  // A esquerda EMPUNHA o punho inclinado, nao apoia por baixo de um tubo: por isso
  // o curl fica cheio e a rotacao em X (-0.484 rad = 28 graus) alinha o eixo de
  // pega da mao, que e Y, com o eixo do punho.
  const hands = buildHandPair(mats);
  hands.right.position.set(-0.045, -0.050, -0.032);
  hands.right.rotation.set(-0.30, -0.12, 0.10);
  hands.left.position.set(0.008, -0.053, -0.318);
  hands.left.rotation.set(-0.484, 0.10, 0.06);
  attachArms(hands, mats);
  root.add(hands.right, hands.left);

  return {
    key: 'battle',
    root, bolt, charging, trigger, mag, magDrop, hands,
    boltTravel: 0.056, chargingTravel: 0.062, triggerTravel: 0.0045,
    chargingWithBolt: true,
    muzzle: marker(0, 0, -0.654),
    ejectPort: marker(-0.030, 0.014, -0.140),
    sight: marker(0, SY, -0.048),
    sightY: SY,
    leftHandBase: new THREE.Vector3(0.008, -0.053, -0.318),
    magAxis: new THREE.Vector3(0.04, -0.990, -0.14).normalize(),
    // Placa de coice a 39.7 cm e boca a 1.279 m: razao 3.22:1.
    hipPos: new THREE.Vector3(0.116, -0.150, -0.625),
    hipRot: new THREE.Euler(0.010, 0.008, 0.172, 'YXZ'),
    sprintPos: new THREE.Vector3(0.156, -0.208, -0.568),
    sprintRot: new THREE.Euler(-0.335, 0.540, 0.634, 'YXZ'),
    recoilScale: 1.60
  };
}
// ---------------------------------------------------------------------------
// Leitura defensiva da definicao da arma
// ---------------------------------------------------------------------------

/** Primeiro campo numerico valido da lista, senao o padrao. */
function num(def, names, fallback) {
  if (def) {
    for (let i = 0; i < names.length; i++) {
      const v = def[names[i]];
      if (typeof v === 'number' && isFinite(v) && v > 0) return v;
    }
  }
  return fallback;
}

const RELOAD_KEYS = ['reloadTime', 'reload', 'reloadDuration', 'reloadSec', 'tempoRecarga'];
const RELOAD_EMPTY_KEYS = ['reloadEmptyTime', 'reloadEmpty', 'reloadTimeEmpty', 'reloadDurationEmpty', 'tempoRecargaVazia'];
const SWITCH_KEYS = ['switchTime', 'swapTime', 'drawTime', 'equipTime', 'deployTime', 'tempoTroca'];
const ADS_KEYS = ['adsTime', 'aimTime', 'timeToAds', 'adsDuration', 'tempoMira'];
const RPM_KEYS = ['rpm', 'fireRate', 'rateOfFire', 'cadence', 'cadencia'];

/**
 * Descobre a qual das SETE armas construidas a definicao corresponde. O contrato
 * nao fixa o nome dos campos, entao classifico por palavra-chave e, se nada bater,
 * distribuo na ordem em que as armas aparecem (deterministico).
 *
 * A ORDEM DOS TESTES IMPORTA e os quatro modelos novos vem PRIMEIRO de proposito:
 * o teste antigo do 'dmr' captura 'shotgun' e 'espingarda', e o do 'smg' captura
 * 'metralhad' (que e sufixo de "subMETRALHADora" - por isso a metralhadora e
 * reconhecida pelo id 'lm8' e por 'lmg', nunca pela palavra inteira, senao a VP-9
 * cairia no modelo errado se algum dia chegar aqui pelo `name` em vez do `id`).
 *
 * Os tres ids originais (kr7, vp9, sr12) continuam SEM padrao explicito: eles nao
 * casam com nenhum dos regex novos e seguem caindo no mapa `seen`, exatamente como
 * antes desta rodada. Nada muda para eles.
 */
function weaponKeyOf(def, seen) {
  const s = String(
    (def && (def.id || def.key || def.name || def.nome || def.type || def.tipo)) || ''
  ).toLowerCase();
  if (/bk12|shotgun|espingarda|pump|buck|calibre/.test(s)) return 'shotgun';
  if (/px5|pistol|handgun|sidearm|glock/.test(s)) return 'pistol';
  if (/lm8|lmg|machinegun|\bmg\d/.test(s)) return 'lmg';
  if (/mk4|battle|batalha|semiauto/.test(s)) return 'battle';
  if (/dmr|sniper|precis|marks|scout|semi/.test(s)) return 'dmr';
  if (/smg|sub|metralhad|mp\d|uzi|vector|pdw|p90/.test(s)) return 'smg';
  if (/rifle|fuzil|assalto|assault|carab|carbin|ar1|ak4|m4/.test(s)) return 'rifle';
  const order = ['rifle', 'smg', 'dmr'];
  if (!seen.has(s)) seen.set(s, order[Math.min(seen.size, 2)]);
  return seen.get(s);
}

/**
 * Fases da recarga, em fracao da duracao total.
 * relA/relB: soltar o pente   inA/inB: enfiar o novo   seat: bater para assentar
 * chA/chB: engatilhar (so na variante com o pente vazio)   back: voltar a pose
 */
const PHASES_TACTICAL = { relA: 0.09, relB: 0.20, inA: 0.31, inB: 0.57, seat: 0.65, chA: -1, chB: -1, back: 0.72 };
const PHASES_EMPTY = { relA: 0.07, relB: 0.16, inA: 0.27, inB: 0.47, seat: 0.53, chA: 0.60, chB: 0.85, back: 0.86 };

/**
 * Onde cada mao vai durante a recarga, por arma (espaco local da arma).
 *
 * `mag` e o ponto onde a mao esquerda agarra o pente; `charge` onde ela engatilha
 * (null quando a arma nao tem alavanca alcancavel pela mao de apoio).
 *
 * A pistola tem `charge` mesmo com `charging: null` no rig, e isso NAO e descuido:
 * na pistola a corredeira e a propria alavanca, e o animador aplica
 * `boltP = max(boltP, chargeP)` - a mao vai atras da corredeira e ela recua, sem
 * existir peca separada para isso.
 */
const HAND_TARGETS = {
  rifle: { mag: [0.014, -0.100, -0.106], charge: [-0.034, 0.026, 0.004] },
  smg: { mag: [0.014, -0.106, -0.126], charge: [-0.038, 0.014, -0.228] },
  dmr: { mag: [0.014, -0.114, -0.166], charge: null },
  shotgun: { mag: [0.014, -0.104, -0.146], charge: [-0.038, 0.006, -0.068] },
  pistol: { mag: [0.016, -0.096, 0.020], charge: [-0.028, 0.014, -0.006] },
  lmg: { mag: [0.014, -0.070, -0.150], charge: [-0.042, 0.010, -0.212] },
  battle: { mag: [0.014, -0.104, -0.092], charge: [-0.056, 0.024, -0.092] }
};

// ---------------------------------------------------------------------------
// Utilidades de animacao (sem alocacao)
// ---------------------------------------------------------------------------

function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
/** Amortecimento independente de framerate. */
function damp(cur, target, lambda, dt) { return target + (cur - target) * Math.exp(-lambda * dt); }
function smoothstep(t) { const x = clamp01(t); return x * x * (3 - 2 * x); }
function easeOut(t) { const x = clamp01(t); return 1 - (1 - x) * (1 - x) * (1 - x); }
function easeIn(t) { const x = clamp01(t); return x * x; }
/** Progresso normalizado de u dentro do trecho [a,b] da linha do tempo. */
function seg(u, a, b) { return clamp01((u - a) / (b - a)); }
/** Pulso 0->1->0, para batidas curtas (bater no pente, engatilhar). */
function pulse(t) { const x = clamp01(t); return Math.sin(x * Math.PI); }
/** Angulo minimo entre dois valores (evita salto de +-PI ao cruzar a volta). */
function angleDelta(a, b) {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
/**
 * Matiz de uma cor, sem o brilho. As cores do ceu sao RADIANCIAS (0.03 a 0.3):
 * usadas cruas como cor de luz dariam luz preta. Escreve em `out` - sem alocar.
 */
function normHue(src, out) {
  out.copy(src);
  const m = Math.max(out.r, out.g, out.b);
  if (m > 1e-5) out.multiplyScalar(1 / m);
  else out.setRGB(1, 1, 1);
  return out;
}

/**
 * O MESMO tratamento que Lighting.js da a cor do hemisferico do mundo, aplicado
 * agora a luz da arma.
 *
 * O zenite do modelo de Preetham, normalizado, vale (0.127, 0.409, 1.000) -
 * B/R 7.8. Isso e azul de TINTA, nao de luz ambiente: usado cru como cor da fill
 * e do hemisferico do viewmodel, era o que deixava a arma na sombra como a UNICA
 * coisa da cena tingida de azul saturado (a cena parou de ficar azul quando o
 * mundo passou a clarear essa mesma cor com HEMI_SKY_WHITE_MIX 0.78, e a arma
 * tinha ficado fora do tratamento).
 *
 * Clarear em direcao ao branco preserva o MATIZ (continua mais frio que o sol,
 * que e (1, 0.56, 0.26)) e mata a saturacao: com 0.78 a cor vira
 * (0.808, 0.870, 1.000), B/R 1.24, luz de ceu de ~9000 K. Como o clareamento sobe
 * a luminancia da cor de 0.392 para 0.866 (x2.21), a intensidade correspondente
 * cai na mesma proporcao (ver fillBase/ambBase) - o nivel da sombra nao muda de
 * graca, so a croma.
 */
function skyTint(src, out, whiteMix) {
  normHue(src, out);
  const w = clamp01(whiteMix);
  out.setRGB(
    out.r + (1 - out.r) * w,
    out.g + (1 - out.g) * w,
    out.b + (1 - out.b) * w
  );
  return out;
}

/** Clareamento do zenite (fill/hemisferico da arma). Igual ao do mundo. */
const SKY_WHITE_MIX = 0.78;
/** O horizonte e quente e serve de contorno: clareia pouco, so tira o laranja. */
const HORIZON_WHITE_MIX = 0.30;

/**
 * Direcao de ESTUDIO da key, no espaco do holder (que E o espaco da camera):
 * de cima, da esquerda, um pouco do lado do olho.
 *
 * POR QUE A KEY NAO PODE SER SO A DIRECAO DO SOL. A arma esta em x=+0.108 com
 * roll, e a camera ve a face -X, o topo e a lateral. Copiando cru a direcao do sol
 * do mundo, em metade dos azimutes o sol ilumina a face +X e a frente - lados que
 * a camera NUNCA ve - e todas as faces visiveis ficam com NdotL ~0, sobrando
 * apenas a fill fria e o environment. E exatamente o sintoma medido nos prints:
 * arma em silhueta com pixels (20,26,32), azul dominante, dentro de um quadro de
 * sol quente.
 *
 * A key entao e a soma do sol com esta direcao fixa PESADA (KEY_BIAS_W > 1): o
 * matiz e a intensidade continuam sendo os do mundo e a direcao ainda balanca com
 * o sol (+-36 graus), mas nunca cai atras da arma. Somar em vez de interpolar
 * tambem nao degenera: com o sol exatamente oposto, sobra 0.30 do vies.
 */
const KEY_BIAS = new THREE.Vector3(-0.52, 0.74, 0.42).normalize();
const KEY_BIAS_W = 1.30;

// ---------------------------------------------------------------------------
// Sistema
// ---------------------------------------------------------------------------

export class ViewModel {
  constructor(ctx) {
    this.ctx = ctx;
    /** Continua animando na pausa: a camera pode ser movida pela API de debug. */
    this.updateWhilePaused = true;

    this.mats = null;
    this.rigs = null;
    this.rig = null;
    this.seenDefs = new Map();
    this.def = null;

    // pilha de grupos, uma camada de animacao cada
    this.holder = null; this.swayG = null; this.bobG = null;
    this.swapG = null; this.poseG = null; this.recoilG = null;

    this.t = 0;
    this.adsT = 0; this.adsE = 0; this.adsEvent = false;
    this.adsSpeed = 9.0;
    this.sprintT = 0;
    this.bobPhase = 0; this.bobAmp = 0; this.moveAmp = 0;
    this.strafe = 0;

    this.swayX = 0; this.swayY = 0; this.swayXV = 0; this.swayYV = 0;
    this.lastYaw = 0; this.lastPitch = 0; this.hasLast = false;

    this.recoilPos = new THREE.Vector3();
    this.recoilVel = new THREE.Vector3();
    this.recoilRot = new THREE.Vector3();
    this.recoilRotVel = new THREE.Vector3();

    this.boltT = -1; this.boltCycle = 0.045;
    this.triggerT = 0;
    this.shellTimer = -1;
    this.flashT = 0;

    this.reloading = false; this.reloadT = 0; this.reloadDur = 2.05;
    this.reloadEmptyDur = 2.75; this.reloadDurActive = 2.05;
    this.reloadEmpty = false; this.dryFired = false; this.boltLocked = false;
    this.fireHold = 0; this._fxBroken = false;
    this.magFalling = false; this.magFallT = 0;
    this.magFallPos = new THREE.Vector3();
    this.magFallVel = new THREE.Vector3();
    this.magFallRot = new THREE.Vector3();

    this.swapping = false; this.swapT = 0; this.swapDur = 0.55;
    this.pendingDef = null; this.pendingKey = null;

    this.landT = 0;
    this.env = null;
    this._warned = false;
    this._offs = [];

    // luz da arma acoplada ao mundo
    this.keyLight = null; this.fillLight = null; this.rimLight = null; this.ambLight = null;
    this.keyBase = 3.4; this.fillBase = 0.62; this.rimBase = 1.60; this.ambBase = 0.34;
    this.shadowK = 1; this._shadowTarget = 1; this._shadowTimer = 0;
    this._rayBroken = false;
    /** Alvo reaproveitado do raycast de sombra: opts novo por frame alocaria. */
    this._rayOpts = {
      target: {
        point: new THREE.Vector3(), normal: new THREE.Vector3(),
        distance: 0, object: null, surface: null
      }
    };
    /** [material, envMapIntensity base, ...] - lista plana, sem objeto por item. */
    this._envBase = [];
    this._envK = -1;
  }

  // -------------------------------------------------------------------------
  async init() {
    const ctx = this.ctx;
    this.mats = buildMaterials(ctx.settings?.anisotropy || 8);
    for (const k of Object.keys(this.mats)) {
      const m = this.mats[k];
      if (m && m.isMaterial) this._envBase.push(m, m.envMapIntensity);
    }

    this.holder = new THREE.Group();
    this.holder.matrixAutoUpdate = true;
    this.swayG = new THREE.Group();
    this.bobG = new THREE.Group();
    this.swapG = new THREE.Group();
    this.poseG = new THREE.Group();
    this.recoilG = new THREE.Group();
    this.holder.add(this.swayG);
    this.swayG.add(this.bobG);
    this.bobG.add(this.swapG);
    this.swapG.add(this.poseG);
    this.poseG.add(this.recoilG);
    // YXZ em todos: giro (yaw) primeiro, depois elevacao, depois inclinacao -
    // e a ordem que faz a pose de corrida ficar natural com angulos grandes
    for (const g of [this.swayG, this.bobG, this.swapG, this.poseG, this.recoilG]) g.rotation.order = 'YXZ';
    ctx.viewScene.add(this.holder);

    // Lente da arma. O Engine cria a viewCamera com 55 e o resize dele so mexe em
    // `aspect`; este e o unico sistema que desenha na viewScene, entao a lente e
    // responsabilidade daqui. `_syncViewFov` reafirma se alguem mudar.
    this._syncViewFov();

    this._buildLights();

    // As sete armas sao construidas de uma vez, na tela de carregamento: o custo
    // e a oclusao de contato assada (bakeVertexAO), que roda uma unica vez por rig
    // e nao aparece em nenhum frame de jogo.
    this.rigs = {
      rifle: buildRifle(this.mats),
      smg: buildSmg(this.mats),
      dmr: buildDmr(this.mats),
      shotgun: buildShotgun(this.mats),
      pistol: buildPistol(this.mats),
      lmg: buildLmg(this.mats),
      battle: buildBattleRifle(this.mats)
    };
    for (const key of Object.keys(this.rigs)) {
      const rig = this.rigs[key];
      this._finalizeRig(rig);
      rig.root.visible = false;
      this.recoilG.add(rig.root);
    }

    this._setRig(this.rigs.rifle);
    this._bindEvents();

    // o environment do mundo da reflexo correto ao metal; se o Sky ainda nao
    // gerou, seguimos so com as luzes locais
    this.env = ctx.scene?.environment || null;
    ctx.viewScene.environment = this.env;
  }

  /** Mantem a lente da arma no valor deste modulo. Uma comparacao por frame. */
  _syncViewFov() {
    const vc = this.ctx.viewCamera;
    if (vc && vc.fov !== VIEW_FOV) {
      vc.fov = VIEW_FOV;
      vc.updateProjectionMatrix();
    }
  }

  _buildLights() {
    // Tres pontos classicos + preenchimento hemisferico, presos ao holder (o
    // espaco da camera). A DIRECAO e a COR passaram a vir do mundo em _syncLights;
    // estes valores sao so o estado inicial e o modo degradado (menu, sem Sky).
    //
    // A GEOMETRIA do rig e o que da volume a arma, e ela e fixa: key em cima e a
    // esquerda (o topo pega luz), fill fria pela lateral -X que a camera ve (a
    // lateral fica em meio-tom), rim ATRAS da arma (contorno separando a silhueta
    // do fundo). Nenhuma das tres vem de baixo: a face de baixo escurece de
    // proposito, e o que da leitura de volume.
    const key = new THREE.DirectionalLight(0xfff1de, 3.4);
    key.position.copy(KEY_BIAS).multiplyScalar(1.5);
    key.castShadow = false;
    // fill: mesma lateral que a camera enxerga, quase na horizontal
    const fill = new THREE.DirectionalLight(0xa9bad4, 0.62);
    fill.position.set(-0.80, -0.15, 0.70);
    /**
     * RIM. Estava em (0.72, 0.62, 0.88), ou seja DO LADO DO OLHO: iluminava a face
     * que a camera ja ve e nao produzia contorno nenhum. Contorno exige luz por
     * TRAS do objeto (z negativo no espaco da camera), rasante ao topo.
     */
    const rim = new THREE.DirectionalLight(0xffd2a4, 1.60);
    rim.position.set(0.45, 0.80, -0.85);
    const amb = new THREE.HemisphereLight(0x5d6a78, 0x0d0e10, 0.34);

    this.keyLight = key;
    this.fillLight = fill;
    this.rimLight = rim;
    this.ambLight = amb;
    this.keyBase = key.intensity;
    this.fillBase = fill.intensity;
    this.rimBase = rim.intensity;
    this.ambBase = amb.intensity;

    this.holder.add(key, key.target, fill, fill.target, rim, rim.target, amb);

    // Luz curta do fogo do cano, que acende a PROPRIA arma ao disparar. Fica no
    // grupo de coice (nao no holder) para ficar de fato na boca do cano e
    // acompanhar a arma no recuo, na corrida e na troca.
    this.muzzleLight = new THREE.PointLight(0xffb257, 0, 1.6, 2.0);
    this.muzzleLight.visible = false;
    this.recoilG.add(this.muzzleLight);
  }

  /**
   * ACOPLA A LUZ DA ARMA AO MUNDO.
   *
   * As tres direcionais do viewmodel tinham cor, direcao e intensidade CONSTANTES,
   * penduradas no holder. Medindo a mesma regiao da arma nos prints, a luminancia
   * dela ficava travada em 48-75 enquanto a da cena ia de 13 a 112: no contraluz a
   * arma aparecia 3.7x mais clara que a rua em que estava, e sempre cinza neutra
   * dentro de um quadro quente. O unico acoplamento era o environment map, que nem
   * sabe se o jogador entrou num galpao.
   *
   * Tres ligacoes, todas baratas:
   *  1) a key aponta para o SOL de verdade (trazido para o espaco do holder e
   *     somado ao vies de estudio de KEY_BIAS, senao ela ilumina o lado cego da
   *     arma) e copia cor e intensidade de ctx.lighting.sun;
   *  2) fill e rim herdam o MATIZ do zenite e do horizonte, normalizado (as cores
   *     do ceu sao radiancias e virariam luz preta se usadas cruas) e CLAREADO em
   *     direcao ao branco por skyTint - matiz cru do zenite e azul de tinta;
   *  3) um unico raio a cada 0.1 s na direcao do sol diz se a arma esta na sombra;
   *     o fator amortecido multiplica as luzes E o envMapIntensity dos materiais -
   *     sem ele o reflexo do ceu continuaria acendendo a arma dentro do galpao.
   */
  _syncLights(dt) {
    const key = this.keyLight;
    if (!key) return;
    const ctx = this.ctx;
    const lighting = ctx.lighting;
    const sun = lighting ? lighting.sun : null;
    let dir = null;
    if (lighting && typeof lighting.getSunDirection === 'function') dir = lighting.getSunDirection();
    else if (ctx.sky && typeof ctx.sky.getSunDirection === 'function') dir = ctx.sky.getSunDirection();

    // menu, ou mundo ainda nao montado: fica no acendimento padrao
    if (!dir || !isFinite(dir.x) || !sun) return;

    // Direcao do sol no espaco do holder (que E o espaco da camera), somada ao
    // vies de estudio: sem isso, em metade dos azimutes o sol ilumina so as faces
    // que a camera nao ve e a arma sai em silhueta (ver KEY_BIAS).
    _ls1.copy(dir);
    _lq1.copy(this.holder.quaternion).invert();
    _ls1.applyQuaternion(_lq1);
    _ls1.addScaledVector(KEY_BIAS, KEY_BIAS_W);
    if (_ls1.lengthSq() > 1e-8) key.position.copy(_ls1).setLength(1.5);

    this._shadowTimer -= dt;
    if (this._shadowTimer <= 0) {
      this._shadowTimer = 0.1;
      this._shadowTarget = this._sampleSunBlock(dir);
    }
    this.shadowK = damp(this.shadowK, this._shadowTarget, 4, dt);
    const k = this.shadowK;

    const sunI = (typeof sun.intensity === 'number' && isFinite(sun.intensity) && sun.intensity > 0)
      ? sun.intensity : 6.5;
    if (sun.color) key.color.copy(sun.color);
    // Mesma magnitude do sol do mundo; sombra e roughness controlam o excesso.
    key.intensity = sunI * k;

    const sky = ctx.sky;
    const zen = sky && typeof sky.getZenithColor === 'function' ? sky.getZenithColor() : null;
    const hor = sky && typeof sky.getHorizonColor === 'function' ? sky.getHorizonColor() : null;
    /**
     * ATENCAO: o zenite NAO pode ser usado com o matiz cru aqui. normHue devolve
     * (0.127, 0.409, 1.000) - B/R 7.8 - e era isso que tingia a arma de azul
     * saturado na sombra enquanto o resto da cena, ja tratado em Lighting.js,
     * ficava apenas mais frio. skyTint faz o mesmo clareamento do mundo.
     */
    if (zen && isFinite(zen.r)) {
      this.fillLight.color.copy(skyTint(zen, _lc1, SKY_WHITE_MIX));
      this.ambLight.color.copy(_lc1);
    }
    if (hor && isFinite(hor.r)) this.rimLight.color.copy(skyTint(hor, _lc1, HORIZON_WHITE_MIX));

    /**
     * Na sombra o preenchimento cai MENOS que a key (o ceu continua aberto): a
     * key vai a 25% e a fill a 77%, entao a proporcao frio/quente se inverte e a
     * arma na sombra fica mais fria que no sol - sem virar azul, porque a cor da
     * fill agora e (0.808, 0.870, 1.000) e nao (0.127, 0.409, 1.000).
     * O piso de 0.55 tambem e o que impede a arma de cair no breu em interior:
     * ela escurece com o mundo, mas nunca deixa de mostrar volume.
     */
    const soft = 0.55 + 0.45 * k;
    this.fillLight.intensity = this.fillBase * soft;
    this.rimLight.intensity = this.rimBase * (0.34 + 0.66 * k);
    this.ambLight.intensity = this.ambBase * soft;

    const envK = 0.34 + 0.66 * k;
    if (Math.abs(envK - this._envK) > 0.004) {
      this._envK = envK;
      const list = this._envBase;
      for (let i = 0; i < list.length; i += 2) list[i].envMapIntensity = list[i + 1] * envK;
    }
  }

  /** 1 = sol batendo, 0.25 = na sombra. Um raio a cada 0.1 s, nao por frame. */
  _sampleSunBlock(dir) {
    if (this._rayBroken) return 1;
    const ctx = this.ctx;
    const col = ctx.collision;
    const cam = ctx.camera;
    if (!col || !cam || typeof col.raycast !== 'function') return 1;
    try {
      return col.raycast(cam.position, dir, 30, this._rayOpts) ? 0.25 : 1;
    } catch (err) {
      this._rayBroken = true;
      console.warn('[ViewModel] raycast de sombra falhou, luz da arma fixa:', err);
      return 1;
    }
  }

  /** Guarda as poses de repouso e calcula a pose EXATA de mira. */
  _finalizeRig(rig) {
    // AO de contato assado na cor de vertice. Tem de rodar com o rig inteiro ja
    // montado (maos e pente inclusos) e nas poses de repouso.
    try {
      bakeVertexAO(rig.root);
    } catch (err) {
      console.warn('[ViewModel] oclusao de contato falhou, seguindo sem ela:', err);
    }

    rig.boltRest = rig.bolt ? rig.bolt.position.z : 0;
    rig.chargingRest = rig.charging ? rig.charging.position.z : 0;
    rig.triggerRest = rig.trigger ? rig.trigger.position.clone() : null;
    rig.handRestL = rig.hands.left.position.clone();
    rig.handRotL = rig.hands.left.rotation.clone();
    rig.handRestR = rig.hands.right.position.clone();
    rig.root.rotation.order = 'YXZ';
    rig.root.add(rig.muzzle, rig.ejectPort, rig.sight);

    const targets = HAND_TARGETS[rig.key] || HAND_TARGETS.rifle;
    rig.magGrab = new THREE.Vector3().fromArray(targets.mag);
    rig.chargeGrab = targets.charge ? new THREE.Vector3().fromArray(targets.charge) : null;

    /**
     * POSE DE MIRA. A mira de ferro (ou o eixo optico da luneta) esta em
     * x=0, y=sightY, z=sight.z no espaco da arma. Com rotacao ZERO, colocar a
     * arma em (0, -sightY, eyeZ - sightZ) leva esse eixo exatamente para o eixo
     * optico da camera - ou seja, para o centro matematico da tela.
     */
    const eyeZ = rig.adsEyeZ === undefined ? ADS_EYE_Z : rig.adsEyeZ;
    rig.adsPos = new THREE.Vector3(0, -rig.sight.position.y, eyeZ - rig.sight.position.z);
    rig.adsRot = new THREE.Euler(0, 0, 0, 'YXZ');
  }

  _bindEvents() {
    const { events, EVENTS } = this.ctx;
    if (!events || !EVENTS) return;
    const on = (type, fn) => { if (type) this._offs.push(events.on(type, fn)); };

    on(EVENTS.WEAPON_FIRE, (p) => this._onFire(p));
    on(EVENTS.WEAPON_DRY, () => {
      this.dryFired = true;
      this.triggerT = 1;
      this.boltLocked = true;     // ferrolho fica preso atras, mostrando o pente vazio
    });
    on(EVENTS.WEAPON_RELOAD_START, (p) => this._onReloadStart(p));
    on(EVENTS.WEAPON_RELOAD_END, () => this._onReloadEnd());
    on(EVENTS.WEAPON_SWITCH, (p) => { if (p && p.weapon) this.setWeapon(p.weapon); });
    on(EVENTS.WEAPON_ADS, (p) => { this.adsEvent = !!(p && p.active); });
    on(EVENTS.PLAYER_LAND, (p) => {
      const f = clamp((p && p.impact ? p.impact : 4) / 9, 0.15, 1.4);
      this.recoilVel.y -= 0.85 * f;
      this.recoilRotVel.x -= 4.2 * f;
      this.recoilRotVel.z += 2.0 * f;
    });
    on(EVENTS.PLAYER_DAMAGED, () => {
      this.recoilRotVel.z += (Math.random() - 0.5) * 4.5;
      this.recoilRotVel.x += (Math.random() - 0.5) * 3.0;
    });
  }
  // -------------------------------------------------------------------------
  // Troca de arma
  // -------------------------------------------------------------------------

  /** Chamado pelo WeaponSystem. Anima a troca em vez de teleportar a arma. */
  setWeapon(def) {
    if (!this.rigs) { this.def = def; return; }
    const key = weaponKeyOf(def, this.seenDefs);
    const rig = this.rigs[key] || this.rigs.rifle;

    if (!this.rig) { this._setRig(rig, def); return; }
    if (rig === this.rig && !this.swapping) { this.def = def; this._applyTimings(def); return; }
    if (this.swapping && this.pendingKey === key) return;

    this.pendingDef = def;
    this.pendingKey = key;
    this.swapDur = clamp(num(def, SWITCH_KEYS, 0.62), 0.28, 1.4);
    this.swapT = 0;
    this.swapping = true;
    // uma troca cancela a recarga em curso
    this.reloading = false;
    this.magFalling = false;
  }

  _setRig(rig, def) {
    if (this.rig && this.rig !== rig) this.rig.root.visible = false;
    this.rig = rig;
    rig.root.visible = true;
    rig.root.position.set(0, 0, 0);
    rig.root.rotation.set(0, 0, 0);
    if (this.muzzleLight) this.muzzleLight.position.copy(rig.muzzle.position).setZ(rig.muzzle.position.z + 0.02);
    this.boltT = -1;
    this.triggerT = 0;
    this.reloading = false;
    this.magFalling = false;
    if (rig.mag) rig.mag.visible = true;
    if (rig.magDrop) rig.magDrop.visible = false;
    if (def) { this.def = def; this._applyTimings(def); }
  }

  /** Le tempos da definicao da arma, com padroes sensatos quando faltarem. */
  _applyTimings(def) {
    this.reloadDur = clamp(num(def, RELOAD_KEYS, 2.05), 0.5, 5);
    this.reloadEmptyDur = clamp(num(def, RELOAD_EMPTY_KEYS, this.reloadDur * 1.35), 0.6, 6);
    const adsTime = clamp(num(def, ADS_KEYS, 0.16), 0.05, 0.6);
    this.adsSpeed = 1 / (adsTime * 0.42);   // constante da exponencial ~ tempo de mira
    const rpm = num(def, RPM_KEYS, 700);
    this.boltCycle = clamp(28 / rpm, 0.030, 0.095);
  }

  // -------------------------------------------------------------------------
  // Disparo
  // -------------------------------------------------------------------------

  _onFire() {
    const rig = this.rig;
    if (!rig) return;
    // na mira o coice e mais contido: a arma esta encostada no ombro
    const s = (rig.recoilScale || 1) * (1 - 0.34 * this.adsE);
    const r = Math.random();

    this.recoilVel.z += (0.30 + r * 0.06) * s;          // recuo para tras
    this.recoilVel.y += (0.055 + r * 0.03) * s;         // sobe um pouco
    this.recoilVel.x += (r - 0.5) * 0.05 * s;
    this.recoilRotVel.x += (0.80 + r * 0.22) * s;       // cabeceia para cima
    this.recoilRotVel.y += (r - 0.5) * 0.55 * s;
    this.recoilRotVel.z += (Math.random() - 0.5) * 0.85 * s;

    this.boltT = 0;
    this.triggerT = 1;
    this.shellTimer = this.boltCycle * 0.34;
    this.flashT = 1;
    this.fireHold = 0.28;
    this.dryFired = false;
    this.boltLocked = false;
  }

  _ejectShell() {
    const rig = this.rig;
    const fx = this.ctx.fx;
    if (!rig || !fx || this._fxBroken || typeof fx.shell !== 'function') return;
    rig.ejectPort.getWorldPosition(_shellPayload.position);
    const m = this.ctx.camera.matrixWorld;
    _v1.setFromMatrixColumn(m, 0);   // direita da camera
    _v2.setFromMatrixColumn(m, 1);   // cima
    _v3.setFromMatrixColumn(m, 2);   // para tras
    _shellPayload.velocity.set(0, 0, 0)
      .addScaledVector(_v1, 2.0 + Math.random() * 0.8)
      .addScaledVector(_v2, 1.4 + Math.random() * 0.6)
      .addScaledVector(_v3, 0.35 + Math.random() * 0.5);
    try {
      fx.shell(_shellPayload);
    } catch (err) {
      this._fxBroken = true;
      console.warn('[ViewModel] ctx.fx.shell falhou, ejecao desativada:', err);
    }
  }

  // -------------------------------------------------------------------------
  // Recarga
  // -------------------------------------------------------------------------

  _onReloadStart(p) {
    if (!this.rig) return;
    // pente vazio: precisa engatilhar no fim. O WeaponSystem ainda nao zerou o
    // contador quando emite, entao 0 municao OU um gatilho seco recente indicam vazio.
    const ammo = this.ctx.weapons ? this.ctx.weapons.ammo : undefined;
    this.reloadEmpty = this.dryFired || ammo === 0;
    const dur = p && typeof p.duration === 'number' && p.duration > 0
      ? p.duration
      : (this.reloadEmpty ? this.reloadEmptyDur : this.reloadDur);
    this.reloadDurActive = clamp(dur, 0.4, 6);
    this.reloadT = 0;
    this.reloading = true;
    this.dryFired = false;
  }

  _onReloadEnd() {
    // deixo a animacao terminar sozinha se estiver quase la; senao corto
    if (!this.reloading) return;
    if (this.reloadT / this.reloadDurActive < 0.75) this.reloading = false;
  }

  /**
   * Marcos da linha do tempo da recarga. Tabelas constantes de modulo: isto e
   * lido a cada frame durante a recarga e nao pode alocar objeto novo por frame.
   */
  _phases() {
    return this.reloadEmpty ? PHASES_EMPTY : PHASES_TACTICAL;
  }

  // -------------------------------------------------------------------------
  // Laco de frame
  // -------------------------------------------------------------------------

  update(dt) {
    // Blindagem: nenhuma falha daqui pode derrubar o frame do jogo.
    try {
      this._update(dt);
    } catch (err) {
      if (!this._warned) {
        this._warned = true;
        console.warn('[ViewModel] animacao falhou, seguindo em modo degradado:', err);
      }
    }
  }

  /** A camera so fecha a pose depois do CameraRig; ressincronizo aqui tambem. */
  lateUpdate() {
    if (this.holder) this._syncHolder();
  }

  _syncHolder() {
    const cam = this.ctx.camera;
    if (!cam || !this.holder) return;
    this.holder.position.copy(cam.position);
    this.holder.quaternion.copy(cam.quaternion);
  }

  _update(dt) {
    dt = Math.min(dt, 0.05);
    this.t += dt;
    const ctx = this.ctx;

    // reflexo correto no metal assim que o Sky publicar o environment
    if (ctx.scene && ctx.scene.environment !== this.env) {
      this.env = ctx.scene.environment;
      ctx.viewScene.environment = this.env;
    }

    this._syncViewFov();
    // usa a matriz do holder fechada no lateUpdate do frame anterior, que ja e a
    // pose final da camera - nao ha atraso perceptivel numa direcao de luz
    this._syncLights(dt);

    // rede de seguranca: se o WeaponSystem trocar de arma sem emitir evento
    const w = ctx.weapons;
    if (w && w.current && w.current !== this.def && w.current !== this.pendingDef) this.setWeapon(w.current);

    const rig = this.rig;
    if (!rig) { this._syncHolder(); return; }

    // --- estado do jogador ---------------------------------------------------
    const ctrl = ctx.controller;
    let speed = 0, grounded = true, sprinting = false;
    if (ctrl) {
      if (typeof ctrl.speed === 'number') speed = ctrl.speed;
      else if (ctrl.velocity) speed = Math.sqrt(ctrl.velocity.x * ctrl.velocity.x + ctrl.velocity.z * ctrl.velocity.z);
      grounded = ctrl.grounded !== false;
      sprinting = !!ctrl.sprinting;
      if (ctrl.velocity && ctx.camera) {
        _v1.copy(ctrl.velocity);
        _q1.copy(ctx.camera.quaternion).invert();
        _v1.applyQuaternion(_q1);
        this.strafe = damp(this.strafe, clamp(_v1.x / 5, -1, 1), 9, dt);
      }
    }
    if (this.fireHold > 0) this.fireHold -= dt;

    // --- mira ---------------------------------------------------------------
    let adsOn;
    if (w && typeof w.isADS === 'boolean') adsOn = w.isADS;
    else adsOn = this.adsEvent;
    if (this.reloading || this.swapping) adsOn = false;
    this.adsT = damp(this.adsT, adsOn ? 1 : 0, this.adsSpeed, dt);
    // travo nos extremos: na mira cheia a pose tem de bater EXATAMENTE no eixo
    if (this.adsT > 0.9994) this.adsT = 1;
    else if (this.adsT < 0.0006) this.adsT = 0;
    this.adsE = smoothstep(this.adsT);

    // --- corrida ------------------------------------------------------------
    const sprintOn = sprinting && grounded && speed > 2.6 && !this.reloading &&
      !this.swapping && this.adsT < 0.12 && this.fireHold <= 0;
    this.sprintT = damp(this.sprintT, sprintOn ? 1 : 0, sprintOn ? 7.0 : 9.5, dt);
    const sprintE = smoothstep(this.sprintT) * (1 - this.adsE);

    // --- balanco de passo ---------------------------------------------------
    const norm = clamp01(speed / 6.6);
    this.bobPhase += dt * (speed * 3.35 + 0.35);
    if (this.bobPhase > 1e5) this.bobPhase -= 1e5;
    this.bobAmp = damp(this.bobAmp, grounded ? norm : norm * 0.18, 6.5, dt);
    this.moveAmp = this.bobAmp;

    // --- atraso do giro do mouse (o efeito mais importante) -----------------
    if (ctx.camera) {
      const yaw = ctx.camera.rotation.y, pitch = ctx.camera.rotation.x;
      if (!this.hasLast) { this.lastYaw = yaw; this.lastPitch = pitch; this.hasLast = true; }
      const dy = angleDelta(yaw, this.lastYaw);
      const dp = angleDelta(pitch, this.lastPitch);
      this.lastYaw = yaw; this.lastPitch = pitch;
      // impulso proporcional ao giro; a mola devolve a arma ao lugar com overshoot
      this.swayX += clamp(-dy, -0.13, 0.13) * 1.30;
      this.swayY += clamp(-dp, -0.13, 0.13) * 1.10;
    }
    const sk = 76, sc = 12.2;
    this.swayXV += (-sk * this.swayX - sc * this.swayXV) * dt;
    this.swayYV += (-sk * this.swayY - sc * this.swayYV) * dt;
    this.swayX = clamp(this.swayX + this.swayXV * dt, -0.17, 0.17);
    this.swayY = clamp(this.swayY + this.swayYV * dt, -0.14, 0.14);

    // --- molas de coice -----------------------------------------------------
    this._spring(this.recoilPos, this.recoilVel, 205, 20.5, dt);
    this._spring(this.recoilRot, this.recoilRotVel, 262, 22.5, dt);

    // --- temporizadores de ciclo -------------------------------------------
    if (this.boltT >= 0) {
      this.boltT += dt / this.boltCycle;
      if (this.boltT >= 1) this.boltT = -1;
    }
    this.triggerT = damp(this.triggerT, 0, 15, dt);
    if (this.shellTimer >= 0) {
      this.shellTimer -= dt;
      if (this.shellTimer <= 0) { this.shellTimer = -1; this._ejectShell(); }
    }
    if (this.flashT > 0) {
      this.flashT = Math.max(0, this.flashT - dt / 0.05);
      this.muzzleLight.visible = this.flashT > 0;
      // ~5x a luz principal a 30 cm, por 2 ou 3 frames: forte sem estourar
      this.muzzleLight.intensity = this.flashT * this.flashT * 1.7;
    } else if (this.muzzleLight.visible) {
      this.muzzleLight.visible = false;
    }

    // --- troca de arma ------------------------------------------------------
    let swapDown = 0;
    if (this.swapping) {
      this.swapT += dt;
      const u = clamp01(this.swapT / this.swapDur);
      if (u < 0.5) {
        swapDown = easeIn(u / 0.5);
      } else {
        if (this.pendingDef) {                       // meio do caminho: troca a malha
          this._setRig(this.rigs[this.pendingKey] || this.rigs.rifle, this.pendingDef);
          this.pendingDef = null; this.pendingKey = null;
        }
        swapDown = 1 - easeOut((u - 0.5) / 0.5);
      }
      if (u >= 1) { this.swapping = false; swapDown = 0; }
    }
    this.swapG.position.set(0, -0.20 * swapDown, 0.055 * swapDown);
    this.swapG.rotation.set(-0.80 * swapDown, 0.22 * swapDown, -0.30 * swapDown);

    // --- pose base: quadril -> mira -> corrida ------------------------------
    const cur = this.rig;   // pode ter mudado no meio da troca
    _v1.copy(cur.hipPos).lerp(cur.adsPos, this.adsE);
    _v2.set(cur.hipRot.x, cur.hipRot.y, cur.hipRot.z).lerp(_v3.set(0, 0, 0), this.adsE);
    _v1.lerp(cur.sprintPos, sprintE);
    _v2.lerp(_v3.set(cur.sprintRot.x, cur.sprintRot.y, cur.sprintRot.z), sprintE);
    this.poseG.position.copy(_v1);
    this.poseG.rotation.set(_v2.x, _v2.y, _v2.z);

    // --- camada de atraso do mouse -----------------------------------------
    // Na mira o residuo cai para 3%: a arma continua "viva" durante o giro, mas
    // parada ela volta ao eixo optico com erro abaixo de um decimo de pixel.
    const swayScale = 1 - 0.97 * this.adsE;
    this.swayG.rotation.set(
      this.swayY * 0.60 * swayScale,
      this.swayX * 0.76 * swayScale,
      (-this.swayX * 0.52 + this.strafe * 0.085) * swayScale
    );
    this.swayG.position.set(
      (this.swayX * 0.070 + this.strafe * 0.020) * swayScale,
      this.swayY * 0.052 * swayScale,
      0
    );

    // --- camada de passo + respiracao --------------------------------------
    const bobScale = (ctx.settings && typeof ctx.settings.cameraBob === 'number' ? ctx.settings.cameraBob : 1);
    const amp = this.bobAmp * bobScale * (1 - 0.88 * this.adsE) * (1 + 0.45 * sprintE);
    const p = this.bobPhase;
    let bx = Math.sin(p) * 0.0165 * amp;
    let by = -Math.abs(Math.cos(p)) * 0.0115 * amp + 0.0055 * amp;
    let brz = Math.sin(p) * 0.028 * amp;
    let brx = Math.sin(p * 2 + 0.4) * 0.014 * amp;
    // Respiracao em oito + micro-tremor, so quando o jogador esta quase parado.
    // Some quase por completo na mira: e a unica camada aditiva que sobra parado,
    // e mira desalinhada por respiracao seria erro de alinhamento visivel.
    const idle = (1 - clamp01(this.moveAmp * 2.4)) * (1 - 0.97 * this.adsE) * bobScale;
    const b = this.t * 1.05;
    bx += (Math.sin(b) * 0.0030 + Math.sin(this.t * 13.7) * 0.00030) * idle;
    by += (Math.sin(b * 2) * 0.0020 + Math.sin(this.t * 9.1 + 1.3) * 0.00026) * idle;
    brz += Math.sin(b * 0.5) * 0.0095 * idle;
    brx += Math.sin(b * 2 + 0.7) * 0.0060 * idle;
    this.bobG.position.set(bx, by, 0);
    this.bobG.rotation.set(brx, 0, brz);

    // --- coice --------------------------------------------------------------
    this.recoilG.position.copy(this.recoilPos);
    this.recoilG.rotation.set(this.recoilRot.x, this.recoilRot.y, this.recoilRot.z);

    // --- pecas moveis e recarga --------------------------------------------
    this._animateParts(cur, dt);

    this._syncHolder();
  }

  _spring(v, vel, k, c, dt) {
    vel.x += (-k * v.x - c * vel.x) * dt;
    vel.y += (-k * v.y - c * vel.y) * dt;
    vel.z += (-k * v.z - c * vel.z) * dt;
    v.addScaledVector(vel, dt);
  }
  /**
   * Ferrolho, gatilho, alavanca, carregador e mao esquerda.
   * Tudo e reescrito a cada frame (inclusive em repouso), entao nunca sobra estado
   * de uma animacao interrompida.
   */
  _animateParts(rig, dt) {
    _v5.set(0, 0, 0);              // deslocamento do corpo da arma
    _v6.set(0, 0, 0);              // rotacao do corpo da arma
    _v7.copy(rig.handRestL);       // alvo da mao esquerda
    let boltP = 0, chargeP = 0, magOut = 0, magSide = 0, magVisible = true;

    // --- ciclo do ferrolho no disparo ---------------------------------------
    if (this.boltT >= 0) {
      const t = this.boltT;
      // vai rapido (quarto de seno) e volta amortecido: e o que da peso ao tiro
      boltP = t < 0.40 ? Math.sin((t / 0.40) * Math.PI * 0.5) : 1 - smoothstep((t - 0.40) / 0.60);
    } else if (this.boltLocked && !this.reloading) {
      boltP = 1;                   // pente vazio: ferrolho preso atras
    }

    // --- recarga -------------------------------------------------------------
    if (this.reloading) {
      this.reloadT += dt;
      const u = this.reloadT / this.reloadDurActive;
      if (u >= 1) {
        this.reloading = false;
        this.boltLocked = false;
      } else {
        const ph = this._phases();
        // quanto a arma esta "envolvida" na recarga: entra rapido, sai no fim
        const inv = smoothstep(seg(u, 0, 0.14)) * (1 - smoothstep(seg(u, ph.back, 1)));
        _v5.set(-0.020 * inv, -0.038 * inv, 0.026 * inv);
        _v6.set(0.13 * inv, -0.24 * inv, 0.42 * inv);

        // pente: solta, cai, entra outro
        if (u < ph.relA) {
          magOut = 0;
        } else if (u < ph.relB) {
          magOut = smoothstep(seg(u, ph.relA, ph.relB)) * 0.055;
          if (u > ph.relB - 0.012 && !this.magFalling) this._startMagDrop(rig);
        } else if (u < ph.inA) {
          magVisible = false;
        } else {
          const ins = 1 - easeOut(seg(u, ph.inA, ph.inB));
          magOut = ins * 0.30;
          magSide = ins * 0.028;
        }

        // batida para assentar o pente
        const slap = pulse(seg(u, ph.inB - 0.02, ph.seat + 0.05));
        _v5.y -= 0.013 * slap;
        _v6.x += 0.10 * slap;

        // mao esquerda: vai ao pente, desce para buscar outro, sobe e volta
        const toMag = smoothstep(seg(u, 0, ph.relB)) * (1 - smoothstep(seg(u, ph.back, 1)));
        _v7.lerp(rig.magGrab, toMag);
        const fetch = pulse(seg(u, ph.relB, ph.inB));
        _v7.y -= 0.165 * fetch;
        _v7.z += 0.045 * fetch;

        // engatilhar (so quando o pente estava vazio)
        if (ph.chA > 0) {
          const cu = seg(u, ph.chA, ph.chB);
          chargeP = cu < 0.62 ? easeOut(cu / 0.62) : 1 - easeIn((cu - 0.62) / 0.38);
          if (boltP < chargeP) boltP = chargeP;
          if (rig.chargeGrab) _v7.lerp(rig.chargeGrab, pulse(seg(u, ph.chA - 0.05, ph.chB + 0.04)));
          const clack = pulse(seg(u, ph.chB - 0.06, ph.chB + 0.03));
          _v6.x += 0.075 * clack;
          _v5.z += 0.010 * clack;
          if (cu >= 1) this.boltLocked = false;
        }
      }
    }

    // --- pente caindo (segue mesmo depois da recarga terminar) --------------
    if (this.magFalling) {
      this.magFallT += dt;
      this.magFallVel.y -= 9.8 * dt;
      this.magFallPos.addScaledVector(this.magFallVel, dt);
      this.magFallRot.x += 3.2 * dt;
      this.magFallRot.z += 1.9 * dt;
      rig.magDrop.position.copy(this.magFallPos);
      rig.magDrop.rotation.set(this.magFallRot.x, 0, this.magFallRot.z);
      if (this.magFallT >= 1.2) { this.magFalling = false; rig.magDrop.visible = false; }
    } else if (rig.magDrop.visible) {
      rig.magDrop.visible = false;
    }

    // --- escrita -------------------------------------------------------------
    rig.root.position.copy(_v5);
    rig.root.rotation.set(_v6.x, _v6.y, _v6.z);

    if (rig.bolt) rig.bolt.position.z = rig.boltRest + boltP * rig.boltTravel;
    if (rig.charging) {
      const cp = rig.chargingWithBolt ? Math.max(boltP, chargeP) : chargeP;
      rig.charging.position.z = rig.chargingRest + cp * rig.chargingTravel;
    }
    if (rig.trigger && rig.triggerRest) {
      rig.trigger.position.z = rig.triggerRest.z + this.triggerT * rig.triggerTravel;
    }
    if (rig.mag) {
      rig.mag.visible = magVisible;
      if (magVisible) {
        rig.mag.position.copy(rig.magAxis).multiplyScalar(magOut);
        rig.mag.position.x += magSide;
        rig.mag.rotation.z = -magSide * 5;
      }
    }
    rig.hands.left.position.copy(_v7);
  }

  _startMagDrop(rig) {
    this.magFalling = true;
    this.magFallT = 0;
    this.magFallPos.copy(rig.magAxis).multiplyScalar(0.055);
    this.magFallVel.copy(rig.magAxis).multiplyScalar(0.60);
    this.magFallVel.x += 0.18;
    this.magFallVel.z -= 0.12;
    this.magFallRot.set(0, 0, 0);
    rig.magDrop.visible = true;
    rig.magDrop.position.copy(this.magFallPos);
  }

  // -------------------------------------------------------------------------
  // API consumida por FX e balistica
  // -------------------------------------------------------------------------

  /**
   * Posicao MUNDIAL da boca do cano, ja reprojetada.
   *
   * A arma e desenhada com a viewCamera (FOV fixo 55) e o mundo com a camera do
   * jogador (FOV variavel, que ainda encolhe na mira). Devolver a posicao crua
   * faria o clarao e o tracer nascerem visivelmente fora do cano. Reprojeto o
   * ponto para o FOV do mundo mantendo a MESMA coordenada de tela.
   */
  getMuzzleWorldPosition(target) {
    const out = target || _v4;
    const cam = this.ctx.camera;
    const rig = this.rig;
    if (!rig) {
      if (cam) out.copy(cam.position); else out.set(0, 0, 0);
      return out;
    }
    rig.muzzle.getWorldPosition(out);
    if (!cam) return out;

    out.sub(cam.position);
    _q1.copy(cam.quaternion).invert();
    out.applyQuaternion(_q1);
    const viewFov = this.ctx.viewCamera ? this.ctx.viewCamera.fov : 55;
    const kv = Math.tan(viewFov * 0.5 * DEG);
    const kw = Math.tan((cam.fov || viewFov) * 0.5 * DEG);
    const s = kv > 1e-5 ? kw / kv : 1;
    out.x *= s;
    out.y *= s;
    out.applyQuaternion(cam.quaternion);
    out.add(cam.position);
    return out;
  }

  /** Direcao mundial do cano (util para o FX orientar o clarao). */
  getMuzzleWorldDirection(target) {
    const out = target || _v4;
    if (!this.rig) { out.set(0, 0, -1); return out; }
    this.rig.root.getWorldDirection(out);   // eixo -Z do objeto
    return out;
  }

  onResize() { /* nada depende do tamanho da tela */ }

  dispose() {
    for (const off of this._offs) { try { off(); } catch { /* ignorado */ } }
    this._offs.length = 0;
    if (this.holder && this.holder.parent) this.holder.parent.remove(this.holder);
    if (this.holder) {
      this.holder.traverse((o) => {
        if (o.isMesh) {
          if (o.geometry) o.geometry.dispose();
        }
      });
    }
    if (this.mats) {
      for (const k of Object.keys(this.mats)) {
        const m = this.mats[k];
        if (m && m.isMaterial) {
          for (const slot of ['map', 'normalMap', 'roughnessMap', 'aoMap']) {
            if (m[slot]) m[slot].dispose();
          }
          m.dispose();
        }
      }
      if (this.mats._textures) for (const t of this.mats._textures) t.dispose();
    }
    this.rigs = null;
    this.rig = null;
  }
}

